import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faChevronDown, faChevronUp, faEye, faFilter, faImage, faLocationDot, faMagnifyingGlass, faWarehouse, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  AHRI_COMPONENT_SLOTS,
  parseComponentOptions,
  hasComponentChoices,
  resolveConfiguration,
  computeOptionDifferentiators,
  prettifyAttributeKey,
} from "./ahriSystemSelection.js";
import {
  decodeHashRoute,
  encodeHashRoute,
  commitHashRoute,
  subscribeHashRoute,
} from "../../scripts/widgetHashRouter.js";

import { DEFAULT_SYSTEM_SHOP_FILTERS } from "./constants/defaultFilters.js";
import {
  PRODUCT_IMAGE_PLACEHOLDER,
  MAX_COMPARE_SELECTIONS,
  MOBILE_FILTER_BREAKPOINT_PX,
  WAREHOUSE_CODE_STORAGE_KEY,
  SHIP_TO_ZIP_STORAGE_KEY,
  scopeStorageKey,
} from "./constants/widget.js";
import {
  buildFacetUiCacheStorageKey,
  normalizeCachedFacetData,
  applyCachedAdvancedCounts,
  buildAdvancedCountsSnapshot,
  normalizeSystemShopFilters,
  normalizeSystemShopFilterOrder,
  normalizeSystemShopAdvancedFilters,
  buildOrderedSystemShopFilters,
  buildEmptyFilterState,
  hasAnySelectedFilterValue,
  processFacets,
  buildFieldClause,
  quoteValue,
  buildFilterPanelKey,
  resolveMobileActiveFilterKey,
  normalizeProductCodeFilters,
  areStringListsEqual,
  normalizeSystemShopMatchTypes,
  // Pre-existing missing imports inherited from ahri_v5 (see note above the
  // availability.js import block) -- these were used but never imported.
  parseOptionalCount,
  writeFacetUiCache,
  readFacetUiCache,
  buildFilterConfigFingerprint,
  sortFacetItems,
  expandGroupedFilterValues,
} from "./domain/filters.js";
import {
  parseSystemDoc,
  parsePayloadJsonDoc,
  getValueFromDoc,
  getAhriDirectoryUrlFromSystem,
  getValuesFromDoc,
  getNumericFromDoc,
  collectProductCodesFromDocs,
  isDirectLookupQuery,
  getOtherWarehouseCodesFromDoc,
  getVariantCombinationAvailability,
  getEmbeddedSlotProductInfo,
} from "./domain/solrDocs.js";
import { compareSystemsForDisplay, isFeaturedForWarehouse, getFeaturedGroupNamesForWarehouse, normalizeFeaturedGroupTag } from "./domain/featured.js";
import {
  extractProductPricing,
  lookupHostPriceRow,
  formatUsd,
  getPricePendingLookupKey,
  buildFakePricingForCode,
  pricingRowIsDisplayable,
  shouldShowListPrice,
  priceValueIsDisplayable,
  isAhriSystemShapeForHostAddToCart,
  partPriceLookupKeys,
  pickPartNumberFromGetPriceRow,
  coalesceFinitePriceForLookup,
  extractCatalogIdsFromPriceRow,
  mustLoginBeforeGetprice,
  resolvePricingForCodeLayers,
  isProductAddToCartBlockedByPrice,
  getSystemPriceGateForAddToCart,
  productAddToCartBlockedTitle,
  systemAddToCartBlockedTitle,
  invokeWindowAddToCartForAhriSystem,
  invokeWindowAddToCartForSingleProduct,
} from "./domain/pricing.js";
import {
  normalizeWarehouseCode,
  resolveWarehouseDisplayLabel,
  loadInitialWarehouseCode,
  loadInitialShipToZip,
  resolveWarehouseCodeProp,
  resolveShipToZipProp,
  getProductAvailability,
  lookupProductDetail,
  isProductInStockAtWarehouse,
  getAvailabilityDotClass,
  resolveDisplayedSlotCode,
  resolveVisibleSlotCodes,
  getSlotAvailabilityStatus,
  // Fixes pre-existing bugs inherited from ahri_v5: normalizeWarehouseDetails
  // and normalizeShipToZip are exported from this module and used
  // extensively below but were never imported, crashing the widget on mount
  // in any configuration (confirmed against a fresh build of ahri_v5's own
  // unmodified source -- not something this fork introduced). Flagged
  // separately for ahri_v5 itself; fixed here since ahri_v6 must actually
  // work.
  normalizeWarehouseDetails,
  normalizeShipToZip,
  findWarehouseDetailByCode,
  formatWarehouseOptionLabel,
} from "./domain/availability.js";
import {
  getEcoRebatesContainerId,
  hasEcoRebateContentText,
  isEcoRebateNativeClickTarget,
  findEcoRebateNativeClickTarget,
  triggerEcoRebateNativeClick,
  formatEcoRebateDate,
  pickRebateProgramDisplayAmount,
  pickRebateProgramAction,
  summarizeRebateOfferType,
} from "./domain/ecoRebates.js";
import {
  buildAhriRecordDetailRows,
  getAhriAttributeIconType,
  formatDetailValue,
} from "./domain/ahriRecords.js";
import {
  hasAuthHeaders,
  decodeJwtClaims,
  resolveAssetUrl,
  formatScore,
  normalizeInteractionModes,
  normalizeInteractionMode,
  toTitleCase,
  normalizeCartQty,
  normalizeCartUom,
  resolveProductUom,
  humanizeFieldName,
  decodeHtmlEntities,
} from "./domain/formatting.js";
import {
  parseAhriRoute,
  buildAhriHash,
  ahriResultSignature,
  ahriNavigationKey,
  encodeFilterMap,
  decodeAhriFilters,
} from "./domain/routing.js";
export { encodeFilterMap, decodeAhriFilters, buildAhriHash };

function renderAhriAttributeIcon(iconType) {
  switch (iconType) {
    case "energy":
      return <span className="impqx-ahri-shop-attr-icon energy">ES</span>;
    case "energy-off":
      return <span className="impqx-ahri-shop-attr-icon energy-off">NE</span>;
    case "seer":
      return <span className="impqx-ahri-shop-attr-icon metric">SE</span>;
    case "hspf":
      return <span className="impqx-ahri-shop-attr-icon metric">HS</span>;
    case "eer":
      return <span className="impqx-ahri-shop-attr-icon metric">ER</span>;
    case "id":
      return <span className="impqx-ahri-shop-attr-icon neutral">ID</span>;
    case "brand":
      return <span className="impqx-ahri-shop-attr-icon neutral">BR</span>;
    case "model":
      return <span className="impqx-ahri-shop-attr-icon neutral">MD</span>;
    case "match":
      return <span className="impqx-ahri-shop-attr-icon match">MT</span>;
    case "capacity":
      return <span className="impqx-ahri-shop-attr-icon neutral">CP</span>;
    default:
      return <span className="impqx-ahri-shop-attr-icon neutral">AT</span>;
  }
}

export default function AhriV6Widget({
  apiEndpoint = "https://qaenv-ai-tech-assistant-api.extremeb2b.com",
  apiKey = "",
  accessToken = "",
  // Host-app account id. Namespaces this widget's browser-local caches
  // (facet UI cache, last-selected warehouse/ship-to zip) by account so two
  // accounts authenticated in the same browser never read/write each
  // other's cached data.
  accountId = "",
  productCode = "",
  branchCode = "",
  defaultBranchCode = "",
  warehouseCode = "",
  branch_code = "",
  default_branch_code = "",
  warehouse_code = "",
  warehouseDetails = [],
  branches = [],
  shipToZip = "",
  ship_to_zip = "",
  // Story 2.2 (AD-6): optional shopper identity. When set, forwarded as
  // customer_id on every /api/v1/ahri/solr/search call so the backend can
  // apply that customer's configured eligibility rules. Left unset, this
  // widget's search behavior is identical to ahri_v5's (anonymous/public).
  customerId = "",
  customer_id = "",
  // Explicit eligibility rule set override. When set, forwarded as
  // eligibility_ruleset_names on every search/facet call and takes priority
  // over customer_id resolution on the backend entirely -- lets an embedder
  // force specific named rule sets regardless of who's logged in. Accepts a
  // single name (string) or multiple (array); falls back to customer_id
  // resolution when omitted/empty, same as ahri_v5/pre-Story-2.2 behavior.
  eligibilityRulesetNames = [],
  eligibility_ruleset_names = [],
  autoLoad = true,
  includeUnmatchedDefault = false,
  pageSize: pageSizeProp = 12,
  title = "AHRI System Shop",
  // Readable URL context that the widget keeps in the page path before the
  // hash, e.g. `/ahri#shop?...`. Set to "" to disable path-writing (embedded
  // hosts that manage their own path).
  // Empty by default: on a host site the page's own route (e.g. `/ahri`) is
  // kept as-is. Appending a segment would create a path the host doesn't
  // serve, so a reload 404s. Pass a value only where that path exists.
  routeContext = "",
  debug = false,
  enableAdvancedIntegratedSearch = true,
  enableImageSearch = true,
}) {
  const endpoint = String(apiEndpoint || "").replace(/\/+$/, "");
  const effectiveCustomerId = String(customerId || customer_id || "").trim();
  const effectiveEligibilityRulesetNames = useMemo(() => {
    const raw = (Array.isArray(eligibilityRulesetNames) && eligibilityRulesetNames.length)
      ? eligibilityRulesetNames
      : eligibility_ruleset_names;
    const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    return list.map((v) => String(v || "").trim()).filter(Boolean);
  }, [eligibilityRulesetNames, eligibility_ruleset_names]);
  const warehouseOptions = useMemo(
    () => normalizeWarehouseDetails(Array.isArray(warehouseDetails) && warehouseDetails.length ? warehouseDetails : branches),
    [warehouseDetails, branches]
  );
  const configuredWarehouseCodeProp = resolveWarehouseCodeProp(
    branchCode,
    defaultBranchCode,
    warehouseCode,
    branch_code,
    default_branch_code,
    warehouse_code
  );
  const warehouseCodeFallback = loadInitialWarehouseCode("", warehouseOptions, accountId);
  const configuredInitialShipToZip = resolveShipToZipProp(shipToZip, ship_to_zip);
  const appliedWarehouseCodePropRef = useRef("");
  const appliedShipToZipPropRef = useRef("");
  // One-time deep-link raw-fq override (see domain/routing.js parseAhriRoute).
  // Read once on mount and ANDed into every search for the life of this tab
  // -- deliberately not part of the hash-diffing state machine below, since
  // it's meant to persist as a standing scope (e.g. "preview this rule set"),
  // not a refinable filter a user clears via the UI.
  const rawFqOverrideRef = useRef(parseAhriRoute()?.rawFq || []);
  // Display-only labels for the rule set(s) behind that rawFq override (see
  // domain/routing.js parseAhriRoute) -- rendered as a banner below so an
  // admin previewing a rule set can see which one is scoping these results.
  const ruleSetPreviewNamesRef = useRef(parseAhriRoute()?.ruleSetPreviewNames || []);
  const [filterConfig, setFilterConfig] = useState(DEFAULT_SYSTEM_SHOP_FILTERS);
  const [advancedFilterConfig, setAdvancedFilterConfig] = useState([]);
  const [filterOrder, setFilterOrder] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(buildEmptyFilterState(DEFAULT_SYSTEM_SHOP_FILTERS));
  const [selectedAdvancedFilters, setSelectedAdvancedFilters] = useState({});
  const [facetData, setFacetData] = useState(buildEmptyFilterState(DEFAULT_SYSTEM_SHOP_FILTERS));
  const [, setFacetUniverseData] = useState(buildEmptyFilterState(DEFAULT_SYSTEM_SHOP_FILTERS));
  const [searchConfig, setSearchConfig] = useState({
    placeholder: "Search by model number, brand, or AHRI ID",
    interaction_mode: "filters",
    interaction_modes: ["filters", "qa"],
    allow_mode_switch: true,
  });
  const [searchInput, setSearchInput] = useState("");
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchError, setImageSearchError] = useState("");
  const [imageConfigInfo, setImageConfigInfo] = useState(null);
  const [imageCameraMenuOpen, setImageCameraMenuOpen] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const imageFileInputRef = useRef(null);
  const cameraFallbackInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraCanvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraBtnRef = useRef(null);
  const [warehouseCodeInput, setWarehouseCodeInput] = useState(() =>
    loadInitialWarehouseCode(configuredWarehouseCodeProp, warehouseOptions, accountId)
  );
  const [selectedWarehouseCode, setSelectedWarehouseCode] = useState(() =>
    loadInitialWarehouseCode(configuredWarehouseCodeProp, warehouseOptions, accountId)
  );
  const [warehouseDraftCode, setWarehouseDraftCode] = useState(() =>
    loadInitialWarehouseCode(configuredWarehouseCodeProp, warehouseOptions, accountId)
  );
  const [warehouseSearchInput, setWarehouseSearchInput] = useState("");
  const [warehouseEditorOpen, setWarehouseEditorOpen] = useState(false);
  const [shipToZipInput, setShipToZipInput] = useState(() => loadInitialShipToZip(configuredInitialShipToZip, accountId));
  const [selectedShipToZip, setSelectedShipToZip] = useState(() => loadInitialShipToZip(configuredInitialShipToZip, accountId));
  const [shipToZipDraft, setShipToZipDraft] = useState(() => loadInitialShipToZip(configuredInitialShipToZip, accountId));
  const [shipToZipEditorOpen, setShipToZipEditorOpen] = useState(false);
  // Configured-default warehouse/ZIP. The URL omits these when they're unchanged
  // so shared links stay short — a recipient gets the same defaults anyway.
  const defaultWarehouseCode = useMemo(
    () => normalizeWarehouseCode(loadInitialWarehouseCode(configuredWarehouseCodeProp, warehouseOptions, accountId)),
    [configuredWarehouseCodeProp, warehouseOptions, accountId],
  );
  const defaultShipToZip = useMemo(
    () => normalizeShipToZip(loadInitialShipToZip(configuredInitialShipToZip, accountId)),
    [configuredInitialShipToZip, accountId],
  );
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(() => {
    const parsed = Number(pageSizeProp);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 12;
  });
  const [totalFound, setTotalFound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [pageLoadComplete, setPageLoadComplete] = useState(() => (
    typeof document !== "undefined" ? document.readyState === "complete" : true
  ));
  const [error, setError] = useState("");
  const [systemShopMatchTypes, setSystemShopMatchTypes] = useState(() =>
    normalizeSystemShopMatchTypes(null, { includeUnmatched: !!includeUnmatchedDefault })
  );
  const [excludeZeroInRequestedWarehouse, setExcludeZeroInRequestedWarehouse] = useState(true);
  const [otherWarehouseSearchFallback, setOtherWarehouseSearchFallback] = useState(false);
  // When a search returns nothing AND removable filters are applied, we offer to
  // clear the filters and retry. "No" dismisses the prompt until the next search.
  const [clearFiltersPromptDismissed, setClearFiltersPromptDismissed] = useState(false);
  // Mirror system-shop filter settings in refs so fetchResults/fetchFacets always read
  // the freshest values. React state setters are async and the closures captured by the
  // fetch callbacks would otherwise lag behind during bootstrap (loadConfig calls
  // setState then immediately awaits a fetch) and runtime toggles.
  const systemShopMatchTypesRef = useRef(
    normalizeSystemShopMatchTypes(null, { includeUnmatched: !!includeUnmatchedDefault })
  );
  const excludeZeroInRequestedWarehouseRef = useRef(true);
  const [showScore, setShowScore] = useState(false);
  const [showScoreDebug, setShowScoreDebug] = useState(false);
  const [showBrandRow, setShowBrandRow] = useState(true);
  const [scoreDebugSystemId, setScoreDebugSystemId] = useState(null);
  const [productCodeFilters, setProductCodeFilters] = useState([]);
  const configLoadKeyRef = useRef("");
  const [productDetails, setProductDetails] = useState({});
  const [priceLookupByCode, setPriceLookupByCode] = useState({});
  const priceLookupByCodeRef = useRef(priceLookupByCode);
  priceLookupByCodeRef.current = priceLookupByCode;
  const [getPricePendingLower, setGetPricePendingLower] = useState({});
  const priceFetchRequestedRef = useRef(new Set());
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);
  // Per-slot component selection for the detail drawer, keyed by
  // `${systemId}:${slotType}` -> product code. Defaults are resolved
  // inventory-first (first in-stock option at the selected warehouse).
  const [componentSelections, setComponentSelections] = useState({});
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productOpen, setProductOpen] = useState(false);
  const [productImageLoadFailed, setProductImageLoadFailed] = useState(false);
  const [cartInputByKey, setCartInputByKey] = useState({});
  const [embeddedHeaderOffset, setEmbeddedHeaderOffset] = useState(0);
  const [selectedSystemKeys, setSelectedSystemKeys] = useState([]);
  const [selectedSystemsByKey, setSelectedSystemsByKey] = useState({});
  const [ecoRebateVisibleByContainerId, setEcoRebateVisibleByContainerId] = useState({});
  const [expandedAltWarehouseKeys, setExpandedAltWarehouseKeys] = useState({});
  const [ecoRebatesConfig, setEcoRebatesConfig] = useState({ enabled: true, base_url: "" });
  const [rebateDetailsState, setRebateDetailsState] = useState({
    open: false,
    loading: false,
    error: "",
    data: null,
    requestId: 0,
    context: { idType: "", id: "", label: "", source: "" },
  });
  const rebateDetailsRequestIdRef = useRef(0);
  const [compareHighlightMode, setCompareHighlightMode] = useState("none"); // none | diff | similar | both
  const [compareColumnOrder, setCompareColumnOrder] = useState([]);
  const [compareDragIndex, setCompareDragIndex] = useState(null);
  const [compareImageLoadFailedByCode, setCompareImageLoadFailedByCode] = useState({});
  const filterWrapRef = useRef(null);
  const widgetRootRef = useRef(null);
  const warehouseEditorRef = useRef(null);
  const qaValueDropdownRef = useRef(null);
  const [openFilterByKey, setOpenFilterByKey] = useState({});
  const [interactionMode, setInteractionMode] = useState("filters");
  const [qaFocusFilterKey, setQaFocusFilterKey] = useState("");
  const [mobileActiveFilterKey, setMobileActiveFilterKey] = useState("");
  const [isMobileFilterLayout, setIsMobileFilterLayout] = useState(
    () => (typeof window !== "undefined" ? (window.innerWidth || 0) <= MOBILE_FILTER_BREAKPOINT_PX : false)
  );
  const [qaValueDropdownOpen, setQaValueDropdownOpen] = useState(false);
  const qaAttributeRowRef = useRef(null);
  const [qaAttributeScrollState, setQaAttributeScrollState] = useState({ canLeft: false, canRight: false });
  const [advancedSearchEnabled, setAdvancedSearchEnabled] = useState(!!enableAdvancedIntegratedSearch);
  const [imageSearchEnabled, setImageSearchEnabled] = useState(!!enableImageSearch);
  const [imageExtractionCard, setImageExtractionCard] = useState(null);
  const [extractionCardExpanded, setExtractionCardExpanded] = useState(false);
  const [nameplateLightboxOpen, setNameplateLightboxOpen] = useState(false);
  const [activeResultsTab, setActiveResultsTab] = useState("ahri");
  const [productResults, setProductResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productSearchTotal, setProductSearchTotal] = useState(0);
  const [productPage, setProductPage] = useState(1);
  const [productHasNext, setProductHasNext] = useState(false);
  const PRODUCT_PAGE_SIZE = 20;
  const selectedSystemRebateContainerId = getEcoRebatesContainerId(selectedSystem?.id, "detail");
  const selectedProductRebateCode = String(selectedProduct?.code || "").trim();
  const selectedProductRebateContainerId = getEcoRebatesContainerId(selectedProductRebateCode, "product");
  const productCodePropFilters = useMemo(() => normalizeProductCodeFilters(productCode), [productCode]);
  const lockedProductCodeSet = useMemo(
    () => new Set(productCodePropFilters.map((entry) => String(entry || "").trim()).filter(Boolean)),
    [productCodePropFilters]
  );
  const previousProductCodePropSetRef = useRef(
    new Set(productCodePropFilters.map((entry) => String(entry || "").trim()).filter(Boolean))
  );
  const appliedProductCodePropKeyRef = useRef("");
  const latestResultsRequestIdRef = useRef(0);
  // Warehouse|zip of the most recent product-details request. A response is
  // dropped only when this has moved on (stale availability); overlapping
  // requests for the same context all merge, so none of their codes are lost.
  const latestProductDetailsContextRef = useRef("");
  // Uppercased codes with a product-details lookup in flight (count per code,
  // since overlapping requests can include the same code).
  const [productDetailsPending, setProductDetailsPending] = useState({});
  // Synchronous mirror keyed `${context}::${CODE}` so back-to-back calls in the
  // same render don't re-request codes already in flight for that context.
  const productDetailsInFlightRef = useRef(new Set());
  const productDetailsRef = useRef(productDetails);
  productDetailsRef.current = productDetails;
  const ecoRebatesContainerIdsRef = useRef([]);
  const ecoRebatesObserversRef = useRef([]);

  const getCartInputValues = useCallback((type, id, defaults = {}) => {
    const key = `${String(type || "product")}:${String(id || "").trim()}`;
    const existing = cartInputByKey[key] || {};
    return {
      qty: normalizeCartQty(existing.qty, normalizeCartQty(defaults.qty, 1)),
      uom: normalizeCartUom(existing.uom, normalizeCartUom(defaults.uom, "EA")),
    };
  }, [cartInputByKey]);

  const setCartInputField = useCallback((type, id, field, value) => {
    const key = `${String(type || "product")}:${String(id || "").trim()}`;
    setCartInputByKey((prev) => {
      const existing = prev[key] || {};
      const nextValue = field === "qty"
        ? normalizeCartQty(value, normalizeCartQty(existing.qty, 1))
        : normalizeCartUom(value, normalizeCartUom(existing.uom, "EA"));
      return {
        ...prev,
        [key]: {
          ...existing,
          [field]: nextValue,
        },
      };
    });
  }, []);

  const detectEmbeddedHeaderOffset = useCallback(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      setEmbeddedHeaderOffset(0);
      return;
    }
    // Use the widget's own top edge in the viewport as the overlay start point.
    // This naturally accounts for any sticky header, page chrome, or padding
    // above the widget — regardless of what CSS class the host app uses.
    const rootEl = widgetRootRef.current;
    if (rootEl) {
      const rect = rootEl.getBoundingClientRect();
      // Clamp: if widget has scrolled above the viewport top, start at 0;
      // if it's very far down (user somehow opened popup while barely visible), cap at 300.
      const top = Math.max(0, Math.min(300, Math.round(rect.top)));
      setEmbeddedHeaderOffset(top);
      return;
    }
    setEmbeddedHeaderOffset(0);
  }, []);

  const headers = useMemo(() => {
    const queryAuth = (() => {
      if (typeof window === "undefined") return { apiKey: "", accessToken: "" };
      try {
        const params = new URLSearchParams(window.location.search || "");
        const fromQueryApiKey = String(params.get("apiKey") || params.get("xApiKey") || "").trim();
        const fromQueryToken = String(params.get("accessToken") || params.get("token") || "").trim();
        if (fromQueryApiKey) {
          window.localStorage.setItem("ai_tech_api_key", fromQueryApiKey);
        }
        if (fromQueryToken) {
          window.localStorage.setItem("ai_tech_access_token", fromQueryToken);
        }
        return { apiKey: fromQueryApiKey, accessToken: fromQueryToken };
      } catch {
        return { apiKey: "", accessToken: "" };
      }
    })();
    const localStoredKey = (() => {
      if (typeof window === "undefined") return "";
      try {
        return String(window.localStorage.getItem("ai_tech_api_key") || "").trim();
      } catch {
        return "";
      }
    })();
    const localStoredToken = (() => {
      if (typeof window === "undefined") return "";
      try {
        return String(window.localStorage.getItem("ai_tech_access_token") || "").trim();
      } catch {
        return "";
      }
    })();
    const resolvedApiKey = String(apiKey || queryAuth.apiKey || localStoredKey).trim();
    const resolvedToken = String(accessToken || queryAuth.accessToken || localStoredToken).trim();
    const next = {
      "Content-Type": "application/json",
    };
    if (resolvedApiKey) {
      next["X-API-Key"] = resolvedApiKey;
    }
    if (resolvedToken) {
      next.Authorization = resolvedToken.startsWith("Bearer ") ? resolvedToken : `Bearer ${resolvedToken}`;
      // The backend's Authorization-token auth path (core/auth.py get_context,
      // used whenever a bearer token is sent instead of/alongside an API key)
      // separately requires accountId/appInstanceId as plain HTTP headers, even
      // though both are already claims inside this same token -- it never reads
      // them out of the token itself. Decode them here so the host page doesn't
      // need to supply yet another pair of props.
      const rawToken = resolvedToken.startsWith("Bearer ") ? resolvedToken.slice(7) : resolvedToken;
      const claims = decodeJwtClaims(rawToken);
      const claimAccountId = claims?.accountId || claims?.account_id;
      const claimAppInstanceId = claims?.appInstanceId || claims?.app_instance_id;
      if (claimAccountId) next.accountId = String(claimAccountId);
      if (claimAppInstanceId) next.appInstanceId = String(claimAppInstanceId);
    }
    return next;
  }, [apiKey, accessToken]);
  const facetUiCacheKey = useMemo(
    () => buildFacetUiCacheStorageKey(endpoint, headers["X-API-Key"] || headers.Authorization || "", accountId || headers.accountId || ""),
    [endpoint, headers, accountId]
  );
  const hasApiAuth = hasAuthHeaders(headers);
  // The missing-auth notice is a developer/configuration hint. Surfacing it to
  // shoppers (and re-toggling it as loading flags flip during a search) only
  // causes confusion, so it is limited to debug builds.
  const showMissingAuthBanner = debug && pageLoadComplete && bootstrapped && !hasApiAuth && !loading && !facetsLoading;
  // Hide auth/API-key plumbing errors from shoppers (they keep reappearing on
  // every failed request and only confuse customers). They are still logged to
  // the console for debugging.
  const customerVisibleError = useMemo(() => {
    const message = String(error || "").trim();
    if (!message) return "";
    if (debug) return message;
    if (/api[\s_-]*key|x-api-key|unauthorized|\b401\b|\b403\b|\b5\d{2}\b|access\s*token|bearer\s*token|openai|failed to load|request failed|search failed/i.test(message)) {
      return "Something went wrong. Please try again.";
    }
    return message;
  }, [error, debug]);
  const selectedWarehouseDetail = useMemo(
    () => findWarehouseDetailByCode(warehouseOptions, selectedWarehouseCode),
    [warehouseOptions, selectedWarehouseCode]
  );
  const selectedWarehouseLabel = selectedWarehouseDetail
    ? selectedWarehouseDetail.branchName || selectedWarehouseDetail.branchCode
    : normalizeWarehouseCode(warehouseCodeInput);
  const filteredWarehouseOptions = useMemo(() => {
    const searchText = String(warehouseSearchInput || "").trim().toLowerCase();
    if (!searchText) return warehouseOptions;
    return warehouseOptions.filter((warehouse) => {
      const searchableText = [
        warehouse.branchCode,
        warehouse.branchName,
        warehouse.locality,
        warehouse.street,
        warehouse.city,
        warehouse.state,
        warehouse.zip,
        warehouse.phone,
        warehouse.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(searchText);
    });
  }, [warehouseOptions, warehouseSearchInput]);

  useEffect(() => {
    if (!configuredWarehouseCodeProp) return;
    if (appliedWarehouseCodePropRef.current === configuredWarehouseCodeProp) return;
    appliedWarehouseCodePropRef.current = configuredWarehouseCodeProp;
    setSelectedWarehouseCode(configuredWarehouseCodeProp);
    setWarehouseCodeInput(configuredWarehouseCodeProp);
    setWarehouseDraftCode(configuredWarehouseCodeProp);
  }, [configuredWarehouseCodeProp]);

  useEffect(() => {
    if (configuredWarehouseCodeProp || !warehouseCodeFallback) return;
    setSelectedWarehouseCode((current) => normalizeWarehouseCode(current) || warehouseCodeFallback);
    setWarehouseCodeInput((current) => normalizeWarehouseCode(current) || warehouseCodeFallback);
    setWarehouseDraftCode((current) => normalizeWarehouseCode(current) || warehouseCodeFallback);
  }, [configuredWarehouseCodeProp, warehouseCodeFallback]);

  useEffect(() => {
    if (!configuredInitialShipToZip) return;
    if (appliedShipToZipPropRef.current === configuredInitialShipToZip) return;
    appliedShipToZipPropRef.current = configuredInitialShipToZip;
    setSelectedShipToZip(configuredInitialShipToZip);
    setShipToZipDraft(configuredInitialShipToZip);
  }, [configuredInitialShipToZip]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const normalized = normalizeWarehouseCode(selectedWarehouseCode);
      const storageKey = scopeStorageKey(WAREHOUSE_CODE_STORAGE_KEY, accountId);
      if (normalized) {
        window.localStorage.setItem(storageKey, normalized);
      } else {
        window.localStorage.removeItem(storageKey);
      }
      setWarehouseCodeInput(normalized);
    } catch (_) {
      // ignore storage access failures
    }
  }, [selectedWarehouseCode, accountId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const normalized = normalizeShipToZip(selectedShipToZip);
      const storageKey = scopeStorageKey(SHIP_TO_ZIP_STORAGE_KEY, accountId);
      if (normalized) {
        window.localStorage.setItem(storageKey, normalized);
      } else {
        window.localStorage.removeItem(storageKey);
      }
      setShipToZipInput(normalized);
    } catch (_) {
      // ignore storage access failures
    }
  }, [selectedShipToZip, accountId]);

  const buildSearchFilters = useCallback(
    (currentSelection, productCodeFiltersOverride = null, advancedSelectionOverride = null) => {
      const filters = { _raw_fq: [] };
      filterConfig.forEach((filter) => {
        const values = currentSelection?.[filter.key] || [];
        if (!Array.isArray(values) || values.length === 0) return;
        const expandedValues = expandGroupedFilterValues(values, filter.value_groups);
        const clause = buildFieldClause(filter.fields || [], expandedValues, filter.value_type === "number");
        if (clause) filters._raw_fq.push(clause);
      });
      const advancedSelections = advancedSelectionOverride && typeof advancedSelectionOverride === "object"
        ? advancedSelectionOverride
        : selectedAdvancedFilters;
      advancedFilterConfig.forEach((filter) => {
        const selected = advancedSelections?.[filter.key] || [];
        if (!Array.isArray(selected) || selected.length === 0) return;
        const queries = selected
          .map((value) => (filter.values || []).find((entry) => String(entry.value) === String(value))?.query)
          .filter(Boolean);
        if (!queries.length) return;
        const clause = queries.length === 1 ? queries[0] : `(${queries.join(" OR ")})`;
        filters._raw_fq.push(clause);
      });
      const resolvedProductCodes = Array.isArray(productCodeFiltersOverride)
        ? productCodeFiltersOverride
        : productCodeFilters;
      resolvedProductCodes
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
        .forEach((code) => {
          const normalizedVariants = [...new Set([
            code,
            String(code).toUpperCase(),
            String(code).toLowerCase(),
          ])];
          const exactExpr = normalizedVariants.map((value) => quoteValue(value, false)).join(" OR ");
          const productCodeFields = [
            "prodmap_outdoormodelnumber",
            "prodmap_indoormodelnumber",
            "prodmap_furnacemodelnumber",
            "rows_outdoormodelnumber",
            "rows_indoormodelnumber",
            "rows_furnacemodelnumber",
            // ahri_system_variant docs (Story 1.4/3.1) carry no prodmap_*/rows_*
            // fields at all -- their product codes live here instead (see
            // _build_variant_solr_doc in backend/api/ahri.py). OR'ing both
            // field sets is safe for either doc type since they never
            // overlap on the same document.
            "outdoor_product_code",
            "indoor_product_code",
            "furnace_product_code",
          ];
          const fieldClauses = productCodeFields.map(
            (field) => `${field}:(${exactExpr})`
          );
          // Each _raw_fq entry is ANDed by backend; this yields "contains product A AND product B".
          filters._raw_fq.push(`(${fieldClauses.join(" OR ")})`);
        });
      if (rawFqOverrideRef.current.length) filters._raw_fq.push(...rawFqOverrideRef.current);
      if (!filters._raw_fq.length) delete filters._raw_fq;
      return filters;
    },
    [filterConfig, productCodeFilters, advancedFilterConfig, selectedAdvancedFilters]
  );

  const fetchFacets = useCallback(
    async (
      currentSelection,
      filterConfigOverride = null,
      productCodeFiltersOverride = null,
      advancedSelectionOverride = null,
      advancedConfigOverride = null,
      warehouseOverride = null,
      shipToZipOverride = null
    ) => {
      if (!hasAuthHeaders(headers)) return;
      const effectiveWarehouse = normalizeWarehouseCode(
        warehouseOverride === null ? selectedWarehouseCode : warehouseOverride
      );
      const effectiveShipToZip = normalizeShipToZip(
        shipToZipOverride === null ? selectedShipToZip : shipToZipOverride
      );
      setFacetsLoading(true);
      try {
        const configToUse =
          Array.isArray(filterConfigOverride) && filterConfigOverride.length ? filterConfigOverride : filterConfig;
        const resolvedProductCodes = Array.isArray(productCodeFiltersOverride)
          ? productCodeFiltersOverride
          : productCodeFilters;
        const activeAdvancedSelections =
          advancedSelectionOverride && typeof advancedSelectionOverride === "object"
            ? advancedSelectionOverride
            : selectedAdvancedFilters;
        const advancedConfigToUse =
          Array.isArray(advancedConfigOverride) && advancedConfigOverride.length
            ? advancedConfigOverride
            : advancedFilterConfig;
        const advancedCountsPromise = (async () => {
          if (!Array.isArray(advancedConfigToUse) || !advancedConfigToUse.length) return null;
          const advancedCounts = await Promise.all(
            advancedConfigToUse.map(async (advancedFilter) => {
              const optionCounts = new Map();
              const options = Array.isArray(advancedFilter?.values) ? advancedFilter.values : [];
              const baseAdvanced = {
                ...(activeAdvancedSelections || {}),
                [advancedFilter.key]: [],
              };
              const baseFilters = buildSearchFilters(
                currentSelection,
                resolvedProductCodes,
                baseAdvanced
              );
              await Promise.all(
                options.map(async (entry) => {
                  const optionValue = String(entry?.value || entry?.label || "").trim();
                  const optionQuery = String(entry?.query || "").trim();
                  if (!optionValue) return;
                  if (!optionQuery) {
                    optionCounts.set(optionValue, null);
                    return;
                  }
                  const payloadForCount = {
                    query: "*:*",
                    page: 1,
                    page_size: 1,
                    filters: {
                      ...(baseFilters || {}),
                      _raw_fq: [...((baseFilters || {})._raw_fq || []), optionQuery],
                    },
                    include_product_attr_facets: false,
                    context: "system_shop",
                    selected_warehouse: effectiveWarehouse || undefined,
                    ship_to_zip: effectiveShipToZip || undefined,
                    exclude_zero_in_requested_warehouse: !!effectiveWarehouse && excludeZeroInRequestedWarehouseRef.current,
                    customer_id: effectiveCustomerId || undefined,
                    eligibility_ruleset_names: effectiveEligibilityRulesetNames.length ? effectiveEligibilityRulesetNames : undefined,
                  };
                  payloadForCount.match_types = systemShopMatchTypesRef.current;
                  try {
                    const countResp = await fetch(`${endpoint}/api/v1/ahri/solr/search`, {
                      method: "POST",
                      headers,
                      body: JSON.stringify(payloadForCount),
                    });
                    const countData = await countResp.json().catch(() => ({}));
                    if (!countResp.ok) {
                      optionCounts.set(optionValue, null);
                      return;
                    }
                    optionCounts.set(optionValue, Number(countData?.numFound || 0));
                  } catch {
                    optionCounts.set(optionValue, null);
                  }
                })
              );
              return { key: advancedFilter.key, optionCounts };
            })
          );
          return new Map(advancedCounts.map((entry) => [String(entry.key || ""), entry.optionCounts]));
        })();
        const payload = {
          query: "*:*",
          page: 1,
          page_size: 1,
          filters: buildSearchFilters(currentSelection, productCodeFiltersOverride, advancedSelectionOverride),
          include_product_attr_facets: true,
          context: "system_shop",
          selected_warehouse: effectiveWarehouse || undefined,
          ship_to_zip: effectiveShipToZip || undefined,
          exclude_zero_in_requested_warehouse: !!effectiveWarehouse && excludeZeroInRequestedWarehouseRef.current,
          customer_id: effectiveCustomerId || undefined,
          eligibility_ruleset_names: effectiveEligibilityRulesetNames.length ? effectiveEligibilityRulesetNames : undefined,
        };
        payload.match_types = systemShopMatchTypesRef.current;
        const resp = await fetch(`${endpoint}/api/v1/ahri/solr/search`, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(String(data?.detail || `Failed to load facets (${resp.status})`));
        }
        const next = processFacets(data?.facets || {}, data?.product_attribute_facets || {}, configToUse);
        configToUse.forEach((filter) => {
          const selected = currentSelection?.[filter.key] || [];
          const existing = next[filter.key] || [];
          const existingMap = new Set(existing.map((entry) => String(entry.value)));
          const merged = [...existing];
          selected.forEach((value) => {
            const asText = String(value || "");
            if (!asText || existingMap.has(asText)) return;
            merged.push({ value: asText, count: null });
          });
          next[filter.key] = sortFacetItems(merged, filter.sort_order || "asc", filter.value_type || "string", filter.custom_value_order);
        });
        const hasScopedFilters = hasAnySelectedFilterValue(currentSelection) || resolvedProductCodes.length > 0;
        if (!hasScopedFilters) {
          // Keep a baseline snapshot, but do not merge it into scoped filters.
          setFacetUniverseData(next);
          writeFacetUiCache(facetUiCacheKey, {
            facetData: next,
            advancedCounts: buildAdvancedCountsSnapshot(advancedConfigToUse),
            filterConfigFingerprint: buildFilterConfigFingerprint(configToUse),
          });
        }
        setFacetData(next);
        void advancedCountsPromise
          .then((advancedCountMap) => {
            if (!advancedCountMap) return;
            setAdvancedFilterConfig((prev) =>
              (Array.isArray(prev) ? prev : []).map((filter) => {
                const countsForFilter = advancedCountMap.get(String(filter?.key || ""));
                if (!countsForFilter) return filter;
                const nextValues = (Array.isArray(filter?.values) ? filter.values : []).map((entry) => {
                  const optionValue = String(entry?.value || entry?.label || "").trim();
                  const nextCount = countsForFilter.has(optionValue) ? countsForFilter.get(optionValue) : null;
                  return {
                    ...entry,
                    count: typeof nextCount === "number" ? nextCount : null,
                  };
                });
                return {
                  ...filter,
                  values: nextValues,
                };
              })
            );
            if (!hasScopedFilters) {
              const advancedCountsPayload = {};
              advancedCountMap.forEach((countsForFilter, filterKey) => {
                const perFilter = {};
                countsForFilter.forEach((countValue, optionValue) => {
                  perFilter[String(optionValue || "")] = parseOptionalCount(countValue);
                });
                advancedCountsPayload[String(filterKey || "")] = perFilter;
              });
              writeFacetUiCache(facetUiCacheKey, {
                facetData: next,
                advancedCounts: advancedCountsPayload,
                filterConfigFingerprint: buildFilterConfigFingerprint(configToUse),
              });
            }
          })
          .catch(() => {});
      } catch (err) {
        const messageText = String(err?.message || err);
        setError(messageText);
        if (debug) {
          // eslint-disable-next-line no-console
          console.warn("AHRI System Shop widget facets error:", err);
        }
      } finally {
        setFacetsLoading(false);
      }
    },
    [
      headers,
      buildSearchFilters,
      endpoint,
      filterConfig,
      advancedFilterConfig,
      selectedAdvancedFilters,
      debug,
      productCodeFilters,
      selectedWarehouseCode,
      selectedShipToZip,
      facetUiCacheKey,
    ]
  );

  const fetchProductDetails = useCallback(
    async (codes, warehouseOverride = null, forceRefresh = false, shipToZipOverride = null) => {
      const effectiveWarehouse = normalizeWarehouseCode(
        warehouseOverride === null ? selectedWarehouseCode : warehouseOverride
      );
      const effectiveShipToZip = normalizeShipToZip(
        shipToZipOverride === null ? selectedShipToZip : shipToZipOverride
      );
      const uniqueCodes = [...new Set((codes || []).map((code) => String(code || "").trim()).filter(Boolean))];
      const requestContext = `${effectiveWarehouse}|${effectiveShipToZip}`;
      const inFlightKey = (code) => `${requestContext}::${code.toUpperCase()}`;
      const missing = uniqueCodes.filter((code) => {
        if (forceRefresh) return true;
        if (productDetailsInFlightRef.current.has(inFlightKey(code))) return false;
        const detail = lookupProductDetail(code, productDetailsRef.current);
        if (!detail || !Object.keys(detail).length) return true;
        const detailWarehouse = normalizeWarehouseCode(
          detail?.availability?.selected_warehouse || detail?.__selected_warehouse
        );
        const detailShipToZip = normalizeShipToZip(detail?.__ship_to_zip);
        if (effectiveWarehouse && detailWarehouse !== effectiveWarehouse) return true;
        if (effectiveShipToZip && detailShipToZip !== effectiveShipToZip) return true;
        if (!effectiveShipToZip && detailShipToZip) return true;
        if (
          effectiveWarehouse
          && !getProductAvailability(detail, effectiveWarehouse).hasWarehouseData
        ) {
          return true;
        }
        return false;
      });
      if (!missing.length || !hasAuthHeaders(headers)) return;
      latestProductDetailsContextRef.current = requestContext;
      const inFlightKeys = missing.map(inFlightKey);
      inFlightKeys.forEach((key) => productDetailsInFlightRef.current.add(key));
      const pendingKeys = missing.map((code) => code.toUpperCase());
      const adjustPending = (delta) =>
        setProductDetailsPending((prev) => {
          const next = { ...prev };
          pendingKeys.forEach((key) => {
            const count = (next[key] || 0) + delta;
            if (count > 0) next[key] = count;
            else delete next[key];
          });
          return next;
        });
      adjustPending(1);
      try {
        const resp = await fetch(`${endpoint}/api/v1/ahri/products/lookup`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            product_codes: missing,
            selected_warehouse: effectiveWarehouse || undefined,
            ship_to_zip: effectiveShipToZip || undefined,
          }),
        });
        if (!resp.ok) return;
        const data = await resp.json().catch(() => ({}));
        if (requestContext !== latestProductDetailsContextRef.current) return;
        if (data?.products && typeof data.products === "object") {
          const stamped = {};
          Object.entries(data.products).forEach(([code, detail]) => {
            if (!code || !detail || typeof detail !== "object") return;
            const normalizedKey = String(code || "").trim().toUpperCase();
            if (!normalizedKey) return;
            const detailEntry = {
              ...detail,
              product_code: detail.product_code || String(code || "").trim(),
              __selected_warehouse: effectiveWarehouse || "",
              __ship_to_zip: effectiveShipToZip || "",
            };
            stamped[normalizedKey] = detailEntry;
          });
          setProductDetails((prev) => ({ ...prev, ...stamped }));
        }
      } catch (err) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.warn("AHRI System Shop widget product detail fetch error:", err);
        }
      } finally {
        inFlightKeys.forEach((key) => productDetailsInFlightRef.current.delete(key));
        adjustPending(-1);
      }
    },
    [headers, endpoint, debug, selectedWarehouseCode, selectedShipToZip]
  );

  const isProductDetailPending = useCallback(
    (code) => !!productDetailsPending[String(code || "").trim().toUpperCase()],
    [productDetailsPending]
  );

  // Self-heal availability for the cards on screen: when results land or the
  // warehouse/zip settles after them (prop/fallback effects, route restore),
  // look up any code still missing data for the current warehouse.
  // fetchProductDetails skips codes that are already up to date.
  useEffect(() => {
    if (!results.length) return;
    void fetchProductDetails(collectProductCodesFromDocs(results));
  }, [results, fetchProductDetails]);

  const isGetPricePendingForCode = useCallback(
    (code) => {
      if (mustLoginBeforeGetprice()) return false;
      if (typeof window === "undefined" || typeof window.getprice !== "function") return false;
      const k = getPricePendingLookupKey(code);
      return k ? !!getPricePendingLower[k] : false;
    },
    [getPricePendingLower]
  );

  const fetchPricesForProductCodes = useCallback(
    async (codes) => {
      const uniqueCodes = [...new Set((codes || []).map((c) => String(c || "").trim()).filter(Boolean))];
      if (!uniqueCodes.length) return;
      if (typeof window === "undefined") {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log("[AHRI System Shop] fetchPricesForProductCodes: no window (SSR)");
        }
        return;
      }
      if (typeof window.getprice !== "function") {
        if (debug) {
          // eslint-disable-next-line no-console
          console.warn(
            "[AHRI System Shop] fetchPricesForProductCodes: window.getprice is not a function. typeof:",
            typeof window.getprice
          );
        }
        return;
      }
      if (mustLoginBeforeGetprice()) {
        if (debug) {
          // eslint-disable-next-line no-console
          console.log("[AHRI System Shop] fetchPricesForProductCodes: skipped — set window.userlogin when logged in");
        }
        return;
      }
      const toRequest = uniqueCodes.filter((code) => !priceFetchRequestedRef.current.has(code));
      if (!toRequest.length) return;
      toRequest.forEach((code) => priceFetchRequestedRef.current.add(code));
      const pendingKeys = toRequest.map((c) => getPricePendingLookupKey(c)).filter(Boolean);
      setGetPricePendingLower((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((k) => {
          next[k] = true;
        });
        return next;
      });
      try {
        const payload = { productCodes: toRequest };
        if (debug) {
          // eslint-disable-next-line no-console
          console.log("[AHRI System Shop] fetchPricesForProductCodes: calling window.getprice", payload);
        }
        const rows = await Promise.resolve(window.getprice(payload));
        if (debug) {
          // eslint-disable-next-line no-console
          console.log(
            "[AHRI System Shop] fetchPricesForProductCodes: returned",
            Array.isArray(rows) ? `array length ${rows.length}` : typeof rows,
            rows
          );
        }
        if (!Array.isArray(rows)) {
          if (debug) {
            // eslint-disable-next-line no-console
            console.warn(
              "AHRI System Shop: window.getprice must return an array (see finalizeGetPriceRowsForWidget)."
            );
          }
          toRequest.forEach((code) => priceFetchRequestedRef.current.delete(code));
          return;
        }
        const next = {};
        rows.forEach((row) => {
          const partNumber = pickPartNumberFromGetPriceRow(row);
          if (!partNumber) return;
          let listPrice = coalesceFinitePriceForLookup(row.listPrice);
          let yourPrice = coalesceFinitePriceForLookup(row.yourPrice);
          if (listPrice == null && yourPrice == null && row.price != null && typeof row.price === "object") {
            const nested = extractProductPricing({ price_info: row.price, pricing: row.price });
            listPrice = nested.listPrice;
            yourPrice = nested.yourPrice;
          }
          const { itemId: rowItemId, itemPriceId: rowItemPriceId } = extractCatalogIdsFromPriceRow(row);
          const hasPrice = Number.isFinite(listPrice) || Number.isFinite(yourPrice);
          const hasMeta = rowItemId != null || rowItemPriceId != null;
          if (!hasPrice && !hasMeta) return;
          const entry = {
            listPrice: Number.isFinite(listPrice) ? listPrice : null,
            yourPrice: Number.isFinite(yourPrice) ? yourPrice : null,
          };
          if (rowItemId != null) entry.itemId = rowItemId;
          if (rowItemPriceId != null) entry.itemPriceId = rowItemPriceId;
          partPriceLookupKeys(partNumber).forEach((key) => {
            next[key] = entry;
          });
        });
        if (Object.keys(next).length) {
          setPriceLookupByCode((prev) => ({ ...prev, ...next }));
        } else {
          toRequest.forEach((code) => priceFetchRequestedRef.current.delete(code));
        }
      } catch (err) {
        toRequest.forEach((code) => priceFetchRequestedRef.current.delete(code));
        if (debug) {
          // eslint-disable-next-line no-console
          console.warn("AHRI System Shop widget getprice error:", err);
        }
      } finally {
        setGetPricePendingLower((prev) => {
          const next = { ...prev };
          pendingKeys.forEach((k) => {
            delete next[k];
          });
          return next;
        });
      }
    },
    [debug]
  );

  const fetchResults = useCallback(
    async (
      pageNum,
      queryValue,
      currentSelection,
      productCodeFiltersOverride = null,
      advancedSelectionOverride = null,
      warehouseOverride = null,
      shipToZipOverride = null,
      filterConfigOverride = null,
      advancedConfigOverride = null
    ) => {
      if (!hasAuthHeaders(headers)) return;
      const requestId = latestResultsRequestIdRef.current + 1;
      latestResultsRequestIdRef.current = requestId;
      const effectiveWarehouse = normalizeWarehouseCode(
        warehouseOverride === null ? selectedWarehouseCode : warehouseOverride
      );
      const effectiveShipToZip = normalizeShipToZip(
        shipToZipOverride === null ? selectedShipToZip : shipToZipOverride
      );
      setLoading(true);
      setError("");
      try {
        const resolvedProductCodes = Array.isArray(productCodeFiltersOverride)
          ? productCodeFiltersOverride
          : productCodeFilters;
        const buildResultsPayload = (excludeZeroRequested) => ({
          query: String(queryValue || "").trim() || "*:*",
          page: pageNum,
          page_size: pageSize,
          filters: buildSearchFilters(currentSelection, productCodeFiltersOverride, advancedSelectionOverride),
          include_product_attr_facets: true,
          context: "system_shop",
          search_config: searchConfig,
          selected_warehouse: effectiveWarehouse || undefined,
          ship_to_zip: effectiveShipToZip || undefined,
          sort_by_inventory: !!effectiveWarehouse,
          exclude_zero_in_requested_warehouse: !!effectiveWarehouse && excludeZeroRequested,
          exclude_unavailable_non_featured: false,
          match_types: systemShopMatchTypesRef.current,
          // Story 2.2 (AD-6): omitted entirely (not an empty string) when no
          // customerId is configured, so the backend's "no customer_id ->
          // completely unaffected" fast path applies -- see api/ahri.py's
          // solr_search.
          customer_id: effectiveCustomerId || undefined,
          eligibility_ruleset_names: effectiveEligibilityRulesetNames.length ? effectiveEligibilityRulesetNames : undefined,
        });
        const requestResults = async (excludeZeroRequested) => {
          const resp = await fetch(`${endpoint}/api/v1/ahri/solr/search`, {
            method: "POST",
            headers,
            body: JSON.stringify(buildResultsPayload(excludeZeroRequested)),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            throw new Error(String(data?.detail || `Search failed (${resp.status})`));
          }
          return data;
        };
        const shouldTryOtherWarehouseFallback = (searchData) =>
          !!effectiveWarehouse
          && excludeZeroInRequestedWarehouseRef.current
          && isDirectLookupQuery(queryValue)
          && Number(searchData?.numFound || 0) === 0;
        let data = await requestResults(excludeZeroInRequestedWarehouseRef.current);
        let usedOtherWarehouseFallback = false;
        if (shouldTryOtherWarehouseFallback(data)) {
          const fallbackData = await requestResults(false);
          if (Number(fallbackData?.numFound || 0) > 0) {
            data = fallbackData;
            usedOtherWarehouseFallback = true;
          }
        }
        if (requestId !== latestResultsRequestIdRef.current) return;
        setOtherWarehouseSearchFallback(usedOtherWarehouseFallback);
        const docs = Array.isArray(data?.docs) ? data.docs : [];
        setResults(docs);
        setTotalFound(Number(data?.numFound || 0));
        setPage(pageNum);
        const facetConfigToUse =
          Array.isArray(filterConfigOverride) && filterConfigOverride.length ? filterConfigOverride : filterConfig;
        const nextFacets = processFacets(data?.facets || {}, data?.product_attribute_facets || {}, facetConfigToUse);
        facetConfigToUse.forEach((filter) => {
          const selected = currentSelection?.[filter.key] || [];
          const existing = nextFacets[filter.key] || [];
          const existingMap = new Set(existing.map((entry) => String(entry.value)));
          const merged = [...existing];
          selected.forEach((value) => {
            const asText = String(value || "");
            if (!asText || existingMap.has(asText)) return;
            merged.push({ value: asText, count: null });
          });
          nextFacets[filter.key] = sortFacetItems(merged, filter.sort_order || "asc", filter.value_type || "string", filter.custom_value_order);
        });
        const hasScopedFilters = hasAnySelectedFilterValue(currentSelection) || resolvedProductCodes.length > 0;
        if (!hasScopedFilters) {
          setFacetUniverseData(nextFacets);
          writeFacetUiCache(facetUiCacheKey, {
            facetData: nextFacets,
            advancedCounts: buildAdvancedCountsSnapshot(advancedFilterConfig),
            filterConfigFingerprint: buildFilterConfigFingerprint(facetConfigToUse),
          });
        }
        setFacetData(nextFacets);
        const advancedConfigToUse =
          Array.isArray(advancedConfigOverride) && advancedConfigOverride.length
            ? advancedConfigOverride
            : advancedFilterConfig;
        const activeAdvancedSelections =
          advancedSelectionOverride && typeof advancedSelectionOverride === "object"
            ? advancedSelectionOverride
            : selectedAdvancedFilters;
        if (Array.isArray(advancedConfigToUse) && advancedConfigToUse.length) {
          void (async () => {
            try {
              const advancedCounts = await Promise.all(
                advancedConfigToUse.map(async (advancedFilter) => {
                  const optionCounts = new Map();
                  const options = Array.isArray(advancedFilter?.values) ? advancedFilter.values : [];
                  const baseAdvanced = {
                    ...(activeAdvancedSelections || {}),
                    [advancedFilter.key]: [],
                  };
                  const baseFilters = buildSearchFilters(
                    currentSelection,
                    resolvedProductCodes,
                    baseAdvanced
                  );
                  await Promise.all(
                    options.map(async (entry) => {
                      const optionValue = String(entry?.value || entry?.label || "").trim();
                      const optionQuery = String(entry?.query || "").trim();
                      if (!optionValue) return;
                      if (!optionQuery) {
                        optionCounts.set(optionValue, null);
                        return;
                      }
                      const payloadForCount = {
                        query: "*:*",
                        page: 1,
                        page_size: 1,
                        filters: {
                          ...(baseFilters || {}),
                          _raw_fq: [...((baseFilters || {})._raw_fq || []), optionQuery],
                        },
                        include_product_attr_facets: false,
                        context: "system_shop",
                        selected_warehouse: effectiveWarehouse || undefined,
                        ship_to_zip: effectiveShipToZip || undefined,
                        exclude_zero_in_requested_warehouse: !!effectiveWarehouse && excludeZeroInRequestedWarehouseRef.current,
                        customer_id: effectiveCustomerId || undefined,
                        eligibility_ruleset_names: effectiveEligibilityRulesetNames.length ? effectiveEligibilityRulesetNames : undefined,
                      };
                      payloadForCount.match_types = systemShopMatchTypesRef.current;
                      try {
                        const countResp = await fetch(`${endpoint}/api/v1/ahri/solr/search`, {
                          method: "POST",
                          headers,
                          body: JSON.stringify(payloadForCount),
                        });
                        const countData = await countResp.json().catch(() => ({}));
                        if (!countResp.ok) {
                          optionCounts.set(optionValue, null);
                          return;
                        }
                        optionCounts.set(optionValue, Number(countData?.numFound || 0));
                      } catch {
                        optionCounts.set(optionValue, null);
                      }
                    })
                  );
                  return { key: advancedFilter.key, optionCounts };
                })
              );
              const advancedCountMap = new Map(advancedCounts.map((entry) => [String(entry.key || ""), entry.optionCounts]));
              setAdvancedFilterConfig((prev) =>
                (Array.isArray(prev) ? prev : []).map((filter) => {
                  const countsForFilter = advancedCountMap.get(String(filter?.key || ""));
                  if (!countsForFilter) return filter;
                  const nextValues = (Array.isArray(filter?.values) ? filter.values : []).map((entry) => {
                    const optionValue = String(entry?.value || entry?.label || "").trim();
                    const nextCount = countsForFilter.has(optionValue) ? countsForFilter.get(optionValue) : null;
                    return {
                      ...entry,
                      count: typeof nextCount === "number" ? nextCount : null,
                    };
                  });
                  return { ...filter, values: nextValues };
                })
              );
              if (!hasScopedFilters) {
                const advancedCountsPayload = {};
                advancedCountMap.forEach((countsForFilter, filterKey) => {
                  const perFilter = {};
                  countsForFilter.forEach((countValue, optionValue) => {
                    perFilter[String(optionValue || "")] = parseOptionalCount(countValue);
                  });
                  advancedCountsPayload[String(filterKey || "")] = perFilter;
                });
                writeFacetUiCache(facetUiCacheKey, {
                  facetData: nextFacets,
                  advancedCounts: advancedCountsPayload,
                  filterConfigFingerprint: buildFilterConfigFingerprint(facetConfigToUse),
                });
              }
            } catch {
              // advanced count errors are non-fatal
            }
          })();
        }
        const productCodes = collectProductCodesFromDocs(docs);
        void fetchProductDetails(productCodes, effectiveWarehouse, false, effectiveShipToZip);
        void fetchPricesForProductCodes(productCodes);
      } catch (err) {
        if (requestId !== latestResultsRequestIdRef.current) return;
        setOtherWarehouseSearchFallback(false);
        const messageText = String(err?.message || err);
        setError(messageText);
        setResults([]);
        setTotalFound(0);
        if (debug) {
          // eslint-disable-next-line no-console
          console.warn("AHRI System Shop widget results error:", err);
        }
      } finally {
        if (requestId !== latestResultsRequestIdRef.current) return;
        setLoading(false);
      }
    },
    [
      headers,
      pageSize,
      buildSearchFilters,
      filterConfig,
      productCodeFilters,
      selectedAdvancedFilters,
      facetUiCacheKey,
      advancedFilterConfig,
      endpoint,
      searchConfig,
      debug,
      fetchProductDetails,
      fetchPricesForProductCodes,
      selectedWarehouseCode,
      selectedShipToZip,
    ]
  );

  const loadConfig = useCallback(async (force = false) => {
    if (!hasAuthHeaders(headers)) {
      setBootstrapped(true);
      return;
    }
    const configLoadKey = `${endpoint}|${String(headers["X-API-Key"] || "")}|${String(headers.Authorization || "")}`;
    if (!force && configLoadKeyRef.current === configLoadKey) {
      return;
    }
    configLoadKeyRef.current = configLoadKey;
    try {
      const resp = await fetch(`${endpoint}/api/v1/ahri/config`, { headers });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        if (resp.status === 401) {
          throw new Error("401 Unauthorized: invalid/missing X-API-Key for /api/v1/ahri/config");
        }
        throw new Error(String(errData?.detail || `Failed to load config (${resp.status})`));
      }
      const data = await resp.json().catch(() => ({}));
      const normalizedFilters = normalizeSystemShopFilters(data?.config?.system_shop_filters);
      const normalizedAdvancedFilters = normalizeSystemShopAdvancedFilters(data?.config?.system_shop_advanced_filters);
      const normalizedFilterOrder = normalizeSystemShopFilterOrder(data?.config?.system_shop_filters_settings?.order);
      const nextSelected = buildEmptyFilterState(normalizedFilters);
      const nextSelectedAdvanced = normalizedAdvancedFilters.reduce((acc, filter) => {
        acc[filter.key] = [];
        return acc;
      }, {});
      const cachedFacetUi = readFacetUiCache(facetUiCacheKey);
      const cachedFacetData = normalizeCachedFacetData(
        normalizedFilters,
        cachedFacetUi?.facetData,
        cachedFacetUi?.filterConfigFingerprint
      );
      const hydratedAdvancedFilters = applyCachedAdvancedCounts(
        normalizedAdvancedFilters,
        cachedFacetUi?.advancedCounts
      );
      setFilterConfig(normalizedFilters);
      setAdvancedFilterConfig(hydratedAdvancedFilters);
      setFilterOrder(normalizedFilterOrder);
      setSelectedFilters(nextSelected);
      setSelectedAdvancedFilters(nextSelectedAdvanced);
      setFacetData(cachedFacetData);
      setFacetUniverseData(buildEmptyFilterState(normalizedFilters));
      const placeholder = String(data?.config?.system_shop_search?.placeholder || "").trim();
      const allowedInteractionModes = normalizeInteractionModes(data?.config?.system_shop_search?.interaction_modes);
      const initialInteractionMode = normalizeInteractionMode(
        data?.config?.system_shop_search?.interaction_mode,
        allowedInteractionModes
      );
      const allowModeSwitch = typeof data?.config?.system_shop_search?.allow_mode_switch === "boolean"
        ? data.config.system_shop_search.allow_mode_switch
        : true;
      setSearchConfig({
        ...(data?.config?.system_shop_search || {}),
        placeholder: placeholder || "Search by model number, brand, or AHRI ID",
        interaction_modes: allowedInteractionModes,
        interaction_mode: initialInteractionMode,
        allow_mode_switch: allowModeSwitch,
      });
      setInteractionMode(initialInteractionMode);
      // Refs are written synchronously *before* the initial fetchResults/fetchFacets
      // call below so the very first request reflects the admin defaults
      // (setState alone would be too late — the closure on fetchResults reads the
      // value at call time, not after React commits).
      const nextMatchTypes = normalizeSystemShopMatchTypes(
        data?.config?.system_shop_filters_settings?.match_types,
        { includeUnmatched: !!data?.config?.system_shop_filters_settings?.include_unmatched }
      );
      systemShopMatchTypesRef.current = nextMatchTypes;
      setSystemShopMatchTypes(nextMatchTypes);
      let nextExcludeZero = true;
      if (typeof data?.config?.system_shop_filters_settings?.exclude_zero_in_requested_warehouse === "boolean") {
        nextExcludeZero = !!data.config.system_shop_filters_settings.exclude_zero_in_requested_warehouse;
      }
      excludeZeroInRequestedWarehouseRef.current = nextExcludeZero;
      setExcludeZeroInRequestedWarehouse(nextExcludeZero);
      setShowScore(!!data?.config?.system_shop_filters_settings?.show_score);
      setShowScoreDebug(!!data?.config?.system_shop_filters_settings?.show_score_debug);
      setShowBrandRow(data?.config?.system_shop_filters_settings?.show_brand_row !== false);
      const cfgIntegratedSearch = data?.config?.system_shop_filters_settings?.enable_integrated_search;
      setAdvancedSearchEnabled(typeof cfgIntegratedSearch === "boolean" ? cfgIntegratedSearch : true);
      const cfgImageSearch = data?.config?.system_shop_filters_settings?.enable_image_search;
      const nextImageEnabled = typeof cfgImageSearch === "boolean" ? cfgImageSearch : true;
      setImageSearchEnabled(nextImageEnabled);
      if (!nextImageEnabled) {
        setImageCameraMenuOpen(false);
        setImageSearchError("");
        setImageConfigInfo(null);
      }
      const rawEcoRebates = data?.config?.eco_rebates;
      setEcoRebatesConfig({
        enabled: rawEcoRebates != null ? !!rawEcoRebates.enabled : true,
        base_url: String(rawEcoRebates?.base_url || "").trim(),
      });
      if (autoLoad) {
        await fetchResults(1, "", nextSelected, productCodePropFilters, nextSelectedAdvanced, null, null, normalizedFilters, hydratedAdvancedFilters);
      } else {
        await fetchFacets(
          nextSelected,
          normalizedFilters,
          productCodePropFilters,
          nextSelectedAdvanced,
          hydratedAdvancedFilters
        );
      }
      appliedProductCodePropKeyRef.current = productCodePropFilters.join("|");
    } catch (err) {
      setError(String(err?.message || err));
      if (debug) {
        // eslint-disable-next-line no-console
        console.warn("AHRI System Shop widget config error:", err);
      }
    } finally {
      setBootstrapped(true);
    }
  }, [headers, endpoint, fetchFacets, fetchResults, autoLoad, debug, productCodePropFilters, facetUiCacheKey]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // ─── Hash routing: sync widget view ⇄ URL hash ───────────────────────────
  const ahriNavKeyRef = useRef(null);
  const ahriRouteSyncingRef = useRef(false);
  const ahriInitialAppliedRef = useRef(false);
  // "What the URL asks to be open" (system / product detail), updated on load
  // and on back/forward. A separate effect acts on it once the matching record
  // is present in the loaded results.
  const [routeOverlay, setRouteOverlay] = useState(() => {
    const route = parseAhriRoute();
    return { system: route?.system || "", product: route?.product || "" };
  });

  // Keep the readable `ahri` context in the page path (e.g. `/ahri#shop?...`).
  // The widget parses navigation from the hash, so this is purely cosmetic and
  // never affects routing; runs once via replaceState (no history entry).
  useEffect(() => {
    if (typeof window === "undefined" || !routeContext) return;
    const ctx = String(routeContext).trim().replace(/^\/+|\/+$/g, "");
    if (!ctx) return;
    const { pathname, search, hash } = window.location;
    const normalized = pathname.replace(/\/+$/, "");
    if (new RegExp(`(^|/)${ctx}$`).test(normalized)) return;
    const nextPath = `${normalized}/${ctx}`.replace(/\/{2,}/g, "/");
    window.history.replaceState(window.history.state, "", `${nextPath}${search}${hash}`);
  }, [routeContext]);

  // Reconstruct the full filter/advanced maps a route asks for: every key the UI
  // currently knows about is reset to the URL's value (cleared when the URL omits
  // it), so Back/Forward fully reflects the link rather than leaving stale facets.
  const mergeFiltersFromRoute = useCallback(
    (route) => {
      const mergedFilters = {};
      Object.keys(selectedFilters || {}).forEach((k) => { mergedFilters[k] = route.filters[k] || []; });
      Object.keys(route.filters || {}).forEach((k) => { if (!(k in mergedFilters)) mergedFilters[k] = route.filters[k]; });
      const mergedAdvanced = {};
      Object.keys(selectedAdvancedFilters || {}).forEach((k) => { mergedAdvanced[k] = route.advanced[k] || []; });
      Object.keys(route.advanced || {}).forEach((k) => { if (!(k in mergedAdvanced)) mergedAdvanced[k] = route.advanced[k]; });
      return { mergedFilters, mergedAdvanced };
    },
    [selectedFilters, selectedAdvancedFilters],
  );

  // Back/forward: close overlays that are no longer in the URL, and re-run the
  // search when any result-affecting input (query, page, filters, warehouse/zip)
  // actually changed. Overlay-only changes never trigger a refetch.
  useEffect(() => {
    const applyAhriRoute = () => {
      const route = parseAhriRoute();
      if (!route) {
        // Hash is empty or belongs to the host page (e.g. Back went past all
        // widget entries, or host had its own hash that the widget replaced on
        // first load). Close any open overlays so widget state stays consistent
        // with the URL, but leave search results in place.
        if (ahriNavKeyRef.current !== null) {
          setCartOpen(false);
          setDetailOpen(false);
          setProductOpen(false);
          setRouteOverlay({ system: "", product: "" });
        }
        return;
      }
      ahriRouteSyncingRef.current = true;
      setCartOpen(route.cart);
      if (!route.system) setDetailOpen(false);
      if (!route.product) setProductOpen(false);
      setRouteOverlay({ system: route.system || "", product: route.product || "" });

      const { mergedFilters, mergedAdvanced } = mergeFiltersFromRoute(route);
      // Warehouse/ZIP are sticky context: apply when the URL carries them, but
      // never clear the current selection when the URL omits them.
      const nextWarehouse = route.warehouse || normalizeWarehouseCode(selectedWarehouseCode);
      const nextZip = route.zip || normalizeShipToZip(selectedShipToZip);

      const routeSig = ahriResultSignature({
        q: route.q, pageNum: route.pageNum, filters: mergedFilters,
        advanced: mergedAdvanced, productCodes: route.productCodes,
        warehouse: nextWarehouse, zip: nextZip,
      });
      const currentSig = ahriResultSignature({
        q: query === "*:*" ? "" : query, pageNum: page, filters: selectedFilters,
        advanced: selectedAdvancedFilters, productCodes: productCodeFilters,
        warehouse: normalizeWarehouseCode(selectedWarehouseCode),
        zip: normalizeShipToZip(selectedShipToZip),
      });

      if (routeSig !== currentSig) {
        const nextQuery = route.q || "*:*";
        setSearchInput(route.q || "");
        setQuery(nextQuery);
        setSelectedFilters(mergedFilters);
        setSelectedAdvancedFilters(mergedAdvanced);
        setProductCodeFilters(route.productCodes);
        if (route.warehouse) { setSelectedWarehouseCode(nextWarehouse); setWarehouseCodeInput(nextWarehouse); }
        if (route.zip) { setSelectedShipToZip(nextZip); setShipToZipInput(nextZip); }
        void fetchResults(route.pageNum, nextQuery, mergedFilters, route.productCodes, mergedAdvanced, nextWarehouse, nextZip);
      }
      window.setTimeout(() => { ahriRouteSyncingRef.current = false; }, 0);
    };
    return subscribeHashRoute(applyAhriRoute);
  }, [
    fetchResults, mergeFiltersFromRoute, query, page,
    selectedFilters, productCodeFilters, selectedAdvancedFilters,
    selectedWarehouseCode, selectedShipToZip,
  ]);

  // Cold load: restore the full view from a shared link once the widget has
  // bootstrapped — search query, page, cart, AND the filter set (facets,
  // advanced filters, product-code chips, warehouse/ZIP). Detail/product overlays
  // are handled separately (the underlying record isn't in memory yet).
  useEffect(() => {
    if (!bootstrapped || ahriInitialAppliedRef.current) return;
    ahriInitialAppliedRef.current = true;
    const route = parseAhriRoute();
    if (!route) return;
    if (route.cart) setCartOpen(true);

    const { mergedFilters, mergedAdvanced } = mergeFiltersFromRoute(route);
    const hasFilterState =
      Object.keys(route.filters).length > 0 ||
      Object.keys(route.advanced).length > 0 ||
      route.productCodes.length > 0 ||
      !!route.warehouse ||
      !!route.zip;

    if (route.q || route.pageNum > 1 || hasFilterState) {
      ahriRouteSyncingRef.current = true;
      const nextQuery = route.q || "*:*";
      setSearchInput(route.q || "");
      setQuery(nextQuery);
      if (Object.keys(route.filters).length) setSelectedFilters(mergedFilters);
      if (Object.keys(route.advanced).length) setSelectedAdvancedFilters(mergedAdvanced);
      if (route.productCodes.length) setProductCodeFilters(route.productCodes);
      const nextWarehouse = route.warehouse || null;
      const nextZip = route.zip || null;
      if (route.warehouse) { setSelectedWarehouseCode(route.warehouse); setWarehouseCodeInput(route.warehouse); }
      if (route.zip) { setSelectedShipToZip(route.zip); setShipToZipInput(route.zip); }
      void fetchResults(
        route.pageNum,
        nextQuery,
        mergedFilters,
        route.productCodes.length ? route.productCodes : productCodeFilters,
        mergedAdvanced,
        nextWarehouse,
        nextZip,
      );
      window.setTimeout(() => { ahriRouteSyncingRef.current = false; }, 0);
    }
  }, [bootstrapped, fetchResults, mergeFiltersFromRoute, selectedFilters, productCodeFilters, selectedAdvancedFilters]);

  // Write current view to the URL hash (push for overlays, replace for search
  // refinements). Suppressed while applying an inbound route and until bootstrap.
  useEffect(() => {
    if (typeof window === "undefined" || ahriRouteSyncingRef.current || !bootstrapped) return;
    const parts = {
      q: query === "*:*" ? "" : query,
      pageNum: page,
      cart: cartOpen,
      system: detailOpen && selectedSystem?.id ? String(selectedSystem.id) : "",
      product: productOpen && selectedProduct?.code ? String(selectedProduct.code) : "",
      filters: selectedFilters,
      advanced: selectedAdvancedFilters,
      productCodes: productCodeFilters,
      // Only encode warehouse/ZIP when they differ from the configured default.
      warehouse: ((wh) => (wh && wh !== defaultWarehouseCode ? wh : ""))(normalizeWarehouseCode(selectedWarehouseCode)),
      zip: ((z) => (z && z !== defaultShipToZip ? z : ""))(normalizeShipToZip(selectedShipToZip)),
      // Preserve the admin eligibility rule-set "preview selected" deep-link
      // scope (see rawFqOverrideRef/parseAhriRoute) across every hash
      // rewrite -- otherwise the first search/filter/page change drops it
      // from the URL and a refresh loses the preview.
      rawFq: rawFqOverrideRef.current,
      ruleSetPreviewNames: ruleSetPreviewNamesRef.current,
    };
    commitHashRoute(buildAhriHash(parts), ahriNavigationKey(parts), ahriNavKeyRef);
  }, [
    bootstrapped, query, page, cartOpen, detailOpen, selectedSystem?.id,
    productOpen, selectedProduct?.code, selectedFilters, selectedAdvancedFilters,
    productCodeFilters, selectedWarehouseCode, selectedShipToZip,
    defaultWarehouseCode, defaultShipToZip,
  ]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      setPageLoadComplete(true);
      return undefined;
    }
    if (document.readyState === "complete") {
      setPageLoadComplete(true);
      return undefined;
    }
    const handlePageLoad = () => setPageLoadComplete(true);
    window.addEventListener("load", handlePageLoad, { once: true });
    return () => window.removeEventListener("load", handlePageLoad);
  }, []);

  const onFilterChange = (filterKey, values) => {
    const next = {
      ...selectedFilters,
      [filterKey]: values,
    };
    setSelectedFilters(next);
    if (bootstrapped) {
      if (autoLoad) {
        void fetchResults(1, query, next, productCodeFilters, selectedAdvancedFilters);
      } else {
        void fetchFacets(next, null, productCodeFilters, selectedAdvancedFilters);
      }
    }
  };

  const onAdvancedFilterChange = (filterKey, values) => {
    const nextAdvanced = {
      ...selectedAdvancedFilters,
      [filterKey]: values,
    };
    setSelectedAdvancedFilters(nextAdvanced);
    if (bootstrapped) {
      if (autoLoad) {
        void fetchResults(1, query, selectedFilters, productCodeFilters, nextAdvanced);
      } else {
        void fetchFacets(selectedFilters, null, productCodeFilters, nextAdvanced);
      }
    }
  };

  const fetchProductResults = useCallback(async (searchQuery, page = 1) => {
    if (!advancedSearchEnabled) return;
    setProductSearchLoading(true);
    try {
      const q = searchQuery === "*:*" ? "" : String(searchQuery || "").trim();
      const offset = (page - 1) * PRODUCT_PAGE_SIZE;
      const url = `${endpoint}/api/v1/products/?search=${encodeURIComponent(q)}&limit=${PRODUCT_PAGE_SIZE}&offset=${offset}&include_facets=false`;
      const resp = await fetch(url, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setProductResults(Array.isArray(data?.products) ? data.products : []);
        setProductSearchTotal(Number(data?.pagination?.total_count || data?.total_count || data?.total || 0));
        setProductHasNext(!!data?.pagination?.has_next);
        setProductPage(page);
      }
    } catch (_) {
      // ignore product search errors silently
    } finally {
      setProductSearchLoading(false);
    }
  }, [endpoint, headers, advancedSearchEnabled]);

  const onSearch = () => {
    const rawQuery = String(searchInput || "").trim();
    const nextQuery = rawQuery || "*:*";
    const nextWarehouse = normalizeWarehouseCode(selectedWarehouseCode);
    const nextShipToZip = normalizeShipToZip(selectedShipToZip);
    setQuery(nextQuery);
    setSelectedWarehouseCode(nextWarehouse);
    setSelectedShipToZip(nextShipToZip);
    void fetchResults(
      1,
      nextQuery,
      selectedFilters,
      productCodeFilters,
      selectedAdvancedFilters,
      nextWarehouse,
      nextShipToZip
    );
    if (advancedSearchEnabled) {
      void fetchProductResults(nextQuery);
    }
  };

  const clearSearch = () => {
    // Reset the query in-place and refetch. A full page reload restored the
    // previous search because the query is persisted in the URL hash (?q=...);
    // clearing state lets the URL-sync effect drop the q param instead.
    setSearchInput("");
    setQuery("*:*");
    void fetchResults(
      1,
      "*:*",
      selectedFilters,
      productCodeFilters,
      selectedAdvancedFilters,
      normalizeWarehouseCode(selectedWarehouseCode),
      normalizeShipToZip(selectedShipToZip)
    );
    setProductResults([]);
    setProductSearchTotal(0);
    setProductPage(1);
    setProductHasNext(false);
    setActiveResultsTab("ahri");
    setImageExtractionCard(null);
    setExtractionCardExpanded(false);
  };

  const checkImageConfig = async () => {
    try {
      const resp = await fetch(`${endpoint}/api/v1/ahri/openai-config-status`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setImageConfigInfo(data);
      }
    } catch (_) {}
  };

  const handleImageSearch = async (file) => {
    if (!file) return;
    setImageSearchError("");
    setImageConfigInfo(null);
    setImageExtractionCard(null);
    setImageSearchLoading(true);
    try {
      const imageDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = imageDataUrl.split(",")[1];
      const mediaType = file.type || "image/jpeg";
      const resp = await fetch(`${endpoint}/api/v1/ahri/extract-model-from-image`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ image: base64, media_type: mediaType }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        if (resp.status === 500 || resp.status === 400) void checkImageConfig();
        throw new Error(String(err?.detail || `Request failed (${resp.status})`));
      }
      const data = await resp.json();
      if (data?.blocked) {
        setImageSearchError(data.blocked_reason || "Image is blocked and will be reported.");
        return;
      }
      // Guard: if model_number is a JSON string (backend fallback path), extract the field
      let rawModel = (data?.model_number || "").trim();
      let extractedInfo = data?.extracted_info && typeof data.extracted_info === "object" ? data.extracted_info : {};
      if (rawModel.startsWith("{")) {
        try {
          const parsed = JSON.parse(rawModel);
          rawModel = (String(parsed.model_number || "")).trim();
          if (Object.keys(extractedInfo).length === 0) extractedInfo = parsed;
        } catch (_) { /* keep raw */ }
      }
      const modelNumber = rawModel;
      if (!modelNumber) {
        setImageSearchError("No model number found in image. Try a clearer photo of the equipment plate.");
        return;
      }
      const searchQueryUsed = (data?.search_query_used || modelNumber).trim();
      const trimCount = Number(data?.trim_count || 0);
      const productFound = Number(data?.product_found || 0);

      setExtractionCardExpanded(false);
      setImageExtractionCard({
        imageDataUrl,
        modelNumber,
        searchQueryUsed,
        trimCount,
        productFound,
        extractedInfo,
      });
      console.log("[AHRI System Shop] image_search_extraction", {
        extracted_model: modelNumber,
        search_query_used: searchQueryUsed,
        trim_count: trimCount,
        trimmed: trimCount > 0,
        ahri_found: Number(data?.ahri_found || 0),
        product_found: productFound,
        brand: extractedInfo?.brand || null,
      });
      setSearchInput(modelNumber);
      const nextWarehouse = normalizeWarehouseCode(selectedWarehouseCode);
      const nextShipToZip = normalizeShipToZip(selectedShipToZip);
      setQuery(searchQueryUsed);
      void fetchResults(1, searchQueryUsed, selectedFilters, productCodeFilters, selectedAdvancedFilters, nextWarehouse, nextShipToZip);
      if (advancedSearchEnabled) {
        void fetchProductResults(searchQueryUsed);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("AHRI System Shop widget image search error:", err);
      setImageSearchError("Image search failed. Please try again.");
    } finally {
      setImageSearchLoading(false);
      if (imageFileInputRef.current) imageFileInputRef.current.value = "";
    }
  };

  const handleStopCameraStream = useCallback(() => {
    const stream = cameraStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const handleCloseCameraModal = useCallback(() => {
    setCameraModalOpen(false);
    handleStopCameraStream();
  }, [handleStopCameraStream]);

  const handleCameraPick = useCallback(() => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      // No getUserMedia — fall back to file input with capture attribute
      const input = document.createElement("input");
      input.type = "file";
      input.setAttribute("accept", "image/*");
      input.setAttribute("capture", "environment");
      input.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none;";
      input.onchange = (e) => {
        handleImageSearch(e.target.files?.[0]);
        document.body.removeChild(input);
      };
      document.body.appendChild(input);
      input.click();
      return;
    }
    setCameraError("");
    setCameraReady(false);
    setCameraModalOpen(true);
  }, [handleImageSearch]);

  const handleCapturePhoto = useCallback(() => {
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    if (!video || !canvas) return;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `equipment-plate-${Date.now()}.jpg`, { type: "image/jpeg" });
      handleImageSearch(file);
      handleCloseCameraModal();
    }, "image/jpeg");
  }, [handleImageSearch, handleCloseCameraModal]);

  useEffect(() => {
    if (!cameraModalOpen) return undefined;
    let cancelled = false;
    const openCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
        }
        setCameraReady(true);
      } catch (_) {
        if (cancelled) return;
        setCameraError("Unable to access camera. Please allow camera permissions, or use Upload Image instead.");
        setCameraReady(false);
      }
    };
    openCamera();
    return () => {
      cancelled = true;
      handleStopCameraStream();
    };
  }, [cameraModalOpen, handleStopCameraStream]);

  const openWarehouseEditor = () => {
    setWarehouseDraftCode(normalizeWarehouseCode(selectedWarehouseCode));
    setWarehouseSearchInput("");
    setWarehouseEditorOpen(true);
  };

  const cancelWarehouseEditor = () => {
    setWarehouseDraftCode(normalizeWarehouseCode(selectedWarehouseCode));
    setWarehouseSearchInput("");
    setWarehouseEditorOpen(false);
  };

  const saveWarehouseEditor = (warehouseCodeOverride = warehouseDraftCode) => {
    const nextWarehouse = normalizeWarehouseCode(warehouseCodeOverride);
    const previousWarehouse = normalizeWarehouseCode(selectedWarehouseCode);
    setWarehouseEditorOpen(false);
    setWarehouseSearchInput("");
    setWarehouseDraftCode(nextWarehouse);
    setWarehouseCodeInput(nextWarehouse);
    setSelectedWarehouseCode(nextWarehouse);
    if (nextWarehouse === previousWarehouse) {
      return;
    }
    setComponentSelections({});
    const nextQuery = String(query || "*:*").trim() || "*:*";
    if (autoLoad) {
      void fetchResults(
        1,
        nextQuery,
        selectedFilters,
        productCodeFilters,
        selectedAdvancedFilters,
        nextWarehouse,
        selectedShipToZip
      );
    } else {
      void fetchFacets(
        selectedFilters,
        null,
        productCodeFilters,
        selectedAdvancedFilters,
        null,
        nextWarehouse,
        selectedShipToZip
      );
    }
  };

  // Used only by a system card's "available at N other locations" chip: unlike
  // saveWarehouseEditor (the global header warehouse picker, which intentionally
  // keeps the current browse query unscoped), this both switches warehouse AND
  // narrows productCodeFilters to this exact system's own indoor/outdoor/furnace
  // codes (ANDed via buildSearchFilters) -- otherwise switching location from
  // this chip just reloads the full unscoped grid at the new warehouse instead
  // of showing the one system the chip was clicked from.
  const viewSystemAtWarehouse = (system, warehouseCodeOverride) => {
    const nextWarehouse = normalizeWarehouseCode(warehouseCodeOverride);
    setWarehouseEditorOpen(false);
    setWarehouseSearchInput("");
    setWarehouseDraftCode(nextWarehouse);
    setWarehouseCodeInput(nextWarehouse);
    setSelectedWarehouseCode(nextWarehouse);
    setComponentSelections({});
    const systemCodes = [system?.outdoorProductCode, system?.indoorProductCode, system?.furnaceProductCode]
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
    const nextProductCodes = systemCodes.length ? systemCodes : productCodeFilters;
    setProductCodeFilters(nextProductCodes);
    const nextQuery = String(query || "*:*").trim() || "*:*";
    if (autoLoad) {
      void fetchResults(
        1,
        nextQuery,
        selectedFilters,
        nextProductCodes,
        selectedAdvancedFilters,
        nextWarehouse,
        selectedShipToZip
      );
    } else {
      void fetchFacets(
        selectedFilters,
        null,
        nextProductCodes,
        selectedAdvancedFilters,
        null,
        nextWarehouse,
        selectedShipToZip
      );
    }
  };

  const openShipToZipEditor = () => {
    setShipToZipDraft(normalizeShipToZip(selectedShipToZip));
    setShipToZipEditorOpen(true);
  };

  const cancelShipToZipEditor = () => {
    setShipToZipDraft(normalizeShipToZip(selectedShipToZip));
    setShipToZipEditorOpen(false);
  };

  const saveShipToZipEditor = () => {
    const nextZip = normalizeShipToZip(shipToZipDraft);
    const previousZip = normalizeShipToZip(selectedShipToZip);
    setShipToZipEditorOpen(false);
    setShipToZipInput(nextZip);
    setSelectedShipToZip(nextZip);
    if (nextZip === previousZip) {
      return;
    }
    setComponentSelections({});
    const nextQuery = String(query || "*:*").trim() || "*:*";
    if (autoLoad) {
      void fetchResults(
        1,
        nextQuery,
        selectedFilters,
        productCodeFilters,
        selectedAdvancedFilters,
        selectedWarehouseCode,
        nextZip
      );
    } else {
      void fetchFacets(
        selectedFilters,
        null,
        productCodeFilters,
        selectedAdvancedFilters,
        null,
        selectedWarehouseCode,
        nextZip
      );
    }
  };

  const clearAllFilters = () => {
    const cleared = buildEmptyFilterState(filterConfig);
    const clearedAdvanced = advancedFilterConfig.reduce((acc, filter) => {
      acc[filter.key] = [];
      return acc;
    }, {});
    const nextProductCodes = [...productCodePropFilters];
    setSelectedFilters(cleared);
    setSelectedAdvancedFilters(clearedAdvanced);
    setProductCodeFilters(nextProductCodes);
    if (autoLoad) {
      void fetchResults(1, query, cleared, nextProductCodes, clearedAdvanced);
    } else {
      void fetchFacets(cleared, null, nextProductCodes, clearedAdvanced);
    }
  };

  // True when the shopper has applied filters that clearAllFilters would remove
  // (locked product-code filters supplied via props are not removable).
  const hasRemovableFilters = useMemo(() => {
    if (hasAnySelectedFilterValue(selectedFilters)) return true;
    if (hasAnySelectedFilterValue(selectedAdvancedFilters)) return true;
    return productCodeFilters.some(
      (code) =>
        !productCodePropFilters.some(
          (locked) => String(locked).trim() === String(code).trim()
        )
    );
  }, [selectedFilters, selectedAdvancedFilters, productCodeFilters, productCodePropFilters]);

  // Re-show the "clear filters and retry" prompt whenever the search or filters
  // change (a fresh attempt deserves a fresh prompt).
  useEffect(() => {
    setClearFiltersPromptDismissed(false);
  }, [query, selectedFilters, selectedAdvancedFilters, productCodeFilters]);

  const orderedFilterConfig = useMemo(() => {
    return buildOrderedSystemShopFilters(filterConfig, advancedFilterConfig, filterOrder);
  }, [filterConfig, advancedFilterConfig, filterOrder]);

  const systems = useMemo(() => {
    const normalizedWarehouse = normalizeWarehouseCode(selectedWarehouseCode);
    const availabilityField = normalizedWarehouse ? `rowd_available_at_${normalizedWarehouse}` : "";
    const rankForSystem = (system, isFeaturedForWarehouse) => {
      const componentCodes = [
        String(system?.outdoorProductCode || "").trim(),
        String(system?.indoorProductCode || "").trim(),
        String(system?.furnaceProductCode || "").trim(),
      ].filter(Boolean);
      if (!componentCodes.length) return { rank: 99, availableCount: 0, totalCount: 0, hasAny: false, allAvailable: false };
      const statuses = componentCodes.map((code) =>
        getProductAvailability(lookupProductDetail(code, productDetails), normalizedWarehouse)
      );
      const availableCount = statuses.filter((entry) => entry.inSelectedWarehouse).length;
      const totalCount = statuses.length;
      const hasAny = availableCount > 0;
      const allAvailable = totalCount > 0 && availableCount === totalCount;
      let rank = 99;
      if (isFeaturedForWarehouse && allAvailable) rank = 0;
      else if (isFeaturedForWarehouse && hasAny) rank = 1;
      else if (!isFeaturedForWarehouse && allAvailable) rank = 2;
      else if (!isFeaturedForWarehouse && hasAny) rank = 3;
      return { rank, availableCount, totalCount, hasAny, allAvailable };
    };

    return results
      .map((doc, index) => ({ ...parseSystemDoc(doc, filterConfig), _sourceIndex: index }))
      .map((system) => {
        const warehouseAvailability = availabilityField ? getNumericFromDoc(system.rawDoc, availabilityField) : 0;
        const normalizedTags = Array.from(
          new Set(
            (Array.isArray(system.featuredBranchCodes) ? system.featuredBranchCodes : [])
              .map((entry) => normalizeFeaturedGroupTag(entry))
              .filter(Boolean)
          )
        );
        // Branch codes get stored/typed inconsistently (zero-padded "073" vs
        // bare "73") -- check both forms, mirroring isFeaturedForWarehouse.
        const warehouseTagCandidates = normalizedWarehouse
          ? new Set([
              normalizedWarehouse,
              ...(/^\d+$/.test(normalizedWarehouse)
                ? [normalizedWarehouse.padStart(3, "0"), normalizedWarehouse.replace(/^0+/, "") || "0"]
                : []),
            ])
          : new Set();
        const hasWarehouseTagMatch = normalizedWarehouse
          ? normalizedTags.some((tag) => warehouseTagCandidates.has(tag))
          : false;
        const featuredForWarehouse = normalizedWarehouse
          ? isFeaturedForWarehouse(system, normalizedWarehouse)
          : !!system.validFeatured;
        // All group names that actually cover the branch being browsed --
        // an AHRI can be featured in more than one group for the same branch.
        const featuredGroupNamesForWarehouse = getFeaturedGroupNamesForWarehouse(system, normalizedWarehouse);
        const bucket = rankForSystem(system, featuredForWarehouse);
        const featuredBonus = featuredForWarehouse ? 100 : 0;
        const warehouseTagBonus = featuredForWarehouse && hasWarehouseTagMatch ? 200 : 0;
        const completeMatchBonus = system.completeMatch ? 100 : 0;
        const showFeaturedTag = !!featuredForWarehouse;
        return {
          ...system,
          featuredForWarehouse,
          featuredGroupNamesForWarehouse,
          availabilityBucketRank: bucket.rank,
          availableComponentCount: bucket.availableCount,
          totalComponentCount: bucket.totalCount,
          hasAnyComponentAvailable: bucket.hasAny,
          allComponentsAvailable: bucket.allAvailable,
          warehouseAvailabilityScoreValue: warehouseAvailability,
          featuredScoreBonus: featuredBonus,
          completeMatchScoreBonus: completeMatchBonus,
          warehouseTagMatched: hasWarehouseTagMatch,
          warehouseTagScoreBonus: warehouseTagBonus,
          showFeaturedTag,
          sortScore: featuredBonus + completeMatchBonus + warehouseTagBonus + warehouseAvailability,
        };
      })
      .map(({ _sourceIndex, ...system }) => system);
  }, [results, productDetails, selectedWarehouseCode]);

  const totalPages = Math.max(1, Math.ceil(totalFound / pageSize));
  const hasRemovableProductCodeFilters = useMemo(
    () => productCodeFilters.some((entry) => !lockedProductCodeSet.has(String(entry || "").trim())),
    [productCodeFilters, lockedProductCodeSet]
  );
  const hasAnyFilter = useMemo(
    () =>
      Object.values(selectedFilters).some((values) => Array.isArray(values) && values.length > 0)
      || Object.values(selectedAdvancedFilters).some((values) => Array.isArray(values) && values.length > 0)
      || hasRemovableProductCodeFilters,
    [selectedFilters, selectedAdvancedFilters, hasRemovableProductCodeFilters]
  );
  const enabledInteractionModes = useMemo(
    () => normalizeInteractionModes(searchConfig?.interaction_modes),
    [searchConfig?.interaction_modes]
  );
  const allowCustomerModeSwitch = useMemo(
    () => searchConfig?.allow_mode_switch !== false,
    [searchConfig?.allow_mode_switch]
  );
  const effectiveInteractionMode = useMemo(
    () => normalizeInteractionMode(interactionMode, enabledInteractionModes),
    [interactionMode, enabledInteractionModes]
  );
  const buildSummary = useMemo(() => {
    const sections = [];
    orderedFilterConfig.forEach((filterItem) => {
      const filter = filterItem.entry || {};
      const source = filterItem.type === "advanced" ? selectedAdvancedFilters : selectedFilters;
      const values = Array.isArray(source?.[filter.key]) ? source[filter.key] : [];
      const cleaned = values
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      if (!cleaned.length) return;
      sections.push({
        key: filter.key,
        type: filterItem.type,
        label: filter.label || filter.key,
        values: cleaned,
      });
    });
    const productValues = productCodeFilters
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
    return {
      sections,
      productValues,
      hasSelections: sections.length > 0 || productValues.length > 0,
    };
  }, [orderedFilterConfig, selectedFilters, selectedAdvancedFilters, productCodeFilters]);
  const conversationalSelectionText = useMemo(() => {
    const latestSelection = buildSummary.sections.length
      ? buildSummary.sections[buildSummary.sections.length - 1]
      : null;
    if (!latestSelection) {
      if (buildSummary.productValues.length > 0) {
        const plural = buildSummary.productValues.length > 1 ? "values" : "value";
        return `You have selected product filter ${plural}: ${buildSummary.productValues.join(", ")}. How would you like to narrow further?`;
      }
      return "How would you like to begin building your system? Choose an attribute and then select values to narrow results.";
    }
    const valuesText = latestSelection.values.join(", ");
    const valueWord = latestSelection.values.length > 1 ? "values" : "value";
    const selectedCount = buildSummary.sections.reduce((sum, section) => sum + section.values.length, 0);
    const selectedSuffix = selectedCount > latestSelection.values.length
      ? ` You currently have ${selectedCount} total selections.`
      : "";
    return `You have selected ${latestSelection.label} with ${valueWord}: ${valuesText}. How would you like to narrow further?${selectedSuffix}`;
  }, [buildSummary]);
  const filterPanelEntries = useMemo(
    () => orderedFilterConfig
      .map((filterItem) => {
        const key = String(filterItem?.key || "").trim();
        const panelKey = buildFilterPanelKey(filterItem?.type, key);
        const label = String(filterItem?.entry?.label || key).trim() || key;
        return {
          ...filterItem,
          panelKey,
          label,
        };
      })
      .filter((entry) => entry.panelKey),
    [orderedFilterConfig]
  );
  const qaAvailableFilters = useMemo(() => filterPanelEntries, [filterPanelEntries]);
  const qaFocusFilter = useMemo(() => {
    if (!qaAvailableFilters.length) return null;
    const byKey = new Map(qaAvailableFilters.map((filter) => [String(filter.key), filter]));
    if (qaFocusFilterKey && byKey.has(qaFocusFilterKey)) return byKey.get(qaFocusFilterKey);
    return qaAvailableFilters[0];
  }, [qaAvailableFilters, qaFocusFilterKey]);
  const qaFocusOptions = useMemo(() => {
    if (!qaFocusFilter) return [];
    if (qaFocusFilter.type === "advanced") {
      return (qaFocusFilter.entry?.values || []).map((entry) => ({
        value: entry?.value || entry?.label,
        count: parseOptionalCount(entry?.count),
      }));
    }
    return facetData?.[qaFocusFilter.key] || [];
  }, [qaFocusFilter, facetData]);
  const qaSelectedValues = useMemo(() => {
    if (!qaFocusFilter) return [];
    const source = qaFocusFilter.type === "advanced" ? selectedAdvancedFilters : selectedFilters;
    return source?.[qaFocusFilter.key] || [];
  }, [qaFocusFilter, selectedFilters, selectedAdvancedFilters]);
  const qaFocusPanelKey = useMemo(
    () => (qaFocusFilter ? buildFilterPanelKey(qaFocusFilter.type, qaFocusFilter.key) : ""),
    [qaFocusFilter]
  );
  const mobileActiveFilterKeyResolved = useMemo(
    () => resolveMobileActiveFilterKey(filterPanelEntries.map((entry) => entry.panelKey), mobileActiveFilterKey),
    [filterPanelEntries, mobileActiveFilterKey]
  );
  const mobileActiveFilterEntry = useMemo(
    () => filterPanelEntries.find((entry) => entry.panelKey === mobileActiveFilterKeyResolved) || null,
    [filterPanelEntries, mobileActiveFilterKeyResolved]
  );
  const mobileActiveFilterOptions = useMemo(() => {
    if (!mobileActiveFilterEntry) return [];
    if (mobileActiveFilterEntry.type === "advanced") {
      return (mobileActiveFilterEntry.entry?.values || []).map((entry) => ({
        value: entry?.value || entry?.label,
        count: parseOptionalCount(entry?.count),
      }));
    }
    return facetData?.[mobileActiveFilterEntry.key] || [];
  }, [mobileActiveFilterEntry, facetData]);
  const mobileActiveSelectedValues = useMemo(() => {
    if (!mobileActiveFilterEntry) return [];
    const source = mobileActiveFilterEntry.type === "advanced" ? selectedAdvancedFilters : selectedFilters;
    return source?.[mobileActiveFilterEntry.key] || [];
  }, [mobileActiveFilterEntry, selectedFilters, selectedAdvancedFilters]);
  const updateQaScrollState = useCallback((rowRef, setter) => {
    const el = rowRef?.current;
    if (!el) {
      setter({ canLeft: false, canRight: false });
      return;
    }
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const scrollLeft = el.scrollLeft || 0;
    setter({
      canLeft: scrollLeft > 2,
      canRight: maxScrollLeft - scrollLeft > 2,
    });
  }, []);
  const scrollQaRowBy = useCallback((rowRef, delta) => {
    const el = rowRef?.current;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: "smooth" });
  }, []);
  const matchSummary = useMemo(() => {
    let complete = 0;
    let partial = 0;
    let unmatched = 0;
    systems.forEach((entry) => {
      if (entry.completeMatch) complete += 1;
      else if (entry.partialMatch) partial += 1;
      else unmatched += 1;
    });
    return { complete, partial, unmatched };
  }, [systems]);

  const clearSummaryFilter = useCallback((filterKey, filterType = "standard") => {
    const key = String(filterKey || "").trim();
    if (!key) return;
    const normalizedType = String(filterType || "").trim().toLowerCase();
    const next = {
      ...selectedFilters,
    };
    const nextAdvanced = {
      ...selectedAdvancedFilters,
    };
    if (normalizedType === "advanced") {
      nextAdvanced[key] = [];
    } else {
      next[key] = [];
    }
    setSelectedFilters(next);
    setSelectedAdvancedFilters(nextAdvanced);
    if (autoLoad) {
      void fetchResults(1, query, next, productCodeFilters, nextAdvanced);
    } else {
      void fetchFacets(next, null, productCodeFilters, nextAdvanced);
    }
  }, [selectedFilters, selectedAdvancedFilters, productCodeFilters, fetchFacets, autoLoad, fetchResults, query]);

  const clearSummaryProductFilter = useCallback(() => {
    const nextProductCodes = productCodeFilters.filter((entry) => lockedProductCodeSet.has(String(entry || "").trim()));
    setProductCodeFilters(nextProductCodes);
    if (autoLoad) {
      void fetchResults(1, query, selectedFilters, nextProductCodes, selectedAdvancedFilters);
    } else {
      void fetchFacets(selectedFilters, null, nextProductCodes, selectedAdvancedFilters);
    }
  }, [selectedFilters, selectedAdvancedFilters, fetchFacets, autoLoad, fetchResults, query, productCodeFilters, lockedProductCodeSet]);

  const applyProductCodeFilter = useCallback((productCode) => {
    const clickedCode = String(productCode || "").trim();
    if (!clickedCode) return;
    if (lockedProductCodeSet.has(clickedCode)) return;
    const exists = productCodeFilters.some((entry) => String(entry) === clickedCode);
    const nextProductCodes = exists
      ? productCodeFilters.filter((entry) => String(entry) !== clickedCode)
      : [...productCodeFilters, clickedCode];
    setProductCodeFilters(nextProductCodes);
    if (autoLoad) {
      void fetchResults(1, query, selectedFilters, nextProductCodes, selectedAdvancedFilters);
    } else {
      void fetchFacets(selectedFilters, null, nextProductCodes, selectedAdvancedFilters);
    }
  }, [productCodeFilters, selectedFilters, selectedAdvancedFilters, fetchFacets, autoLoad, fetchResults, query, lockedProductCodeSet]);

  const resolvePricingForProductCode = useCallback(
    (productCode) => {
      const code = String(productCode || "").trim();
      if (!code) return { listPrice: null, yourPrice: null };
      const detail = productDetails[code] || {};
      return resolvePricingForCodeLayers(code, detail, priceLookupByCode);
    },
    [productDetails, priceLookupByCode]
  );

  const systemPriceSummaryByKey = useMemo(() => {
    const summary = {};
    systems.forEach((system, index) => {
      const systemSelectionKey = String(system?.id || "").trim() || `system-${index}`;
      const pricingRows = [
        resolvePricingForProductCode(system.outdoorProductCode),
        resolvePricingForProductCode(system.indoorProductCode),
        resolvePricingForProductCode(system.furnaceProductCode),
      ].filter((entry) => pricingRowIsDisplayable(entry));
      summary[systemSelectionKey] = {
        totalList: pricingRows.reduce((sum, entry) => sum + (Number.isFinite(entry.listPrice) ? entry.listPrice : 0), 0),
        totalYour: pricingRows.reduce((sum, entry) => sum + (Number.isFinite(entry.yourPrice) ? entry.yourPrice : 0), 0),
      };
    });
    return summary;
  }, [systems, resolvePricingForProductCode]);

  const maxSeerOnPage = useMemo(() => {
    const values = systems
      .map((system) => Number(system?.seer2))
      .filter((value) => Number.isFinite(value));
    return values.length ? Math.max(...values) : null;
  }, [systems]);

  const minYourTotalOnPage = useMemo(() => {
    const values = Object.values(systemPriceSummaryByKey)
      .map((entry) => Number(entry?.totalYour))
      .filter((value) => Number.isFinite(value) && value > 0);
    return values.length ? Math.min(...values) : null;
  }, [systemPriceSummaryByKey]);

  const toggleFilterValue = (filterKey, value) => {
    const current = selectedFilters[filterKey] || [];
    const asText = String(value || "");
    const exists = current.some((entry) => String(entry) === asText);
    const filterDef = filterConfig.find((filter) => filter.key === filterKey);
    if (!filterDef?.multi_select) {
      onFilterChange(filterKey, exists ? [] : [asText]);
      return;
    }
    const nextValues = exists ? current.filter((entry) => String(entry) !== asText) : [...current, asText];
    onFilterChange(filterKey, nextValues);
  };

  const cartItemCount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item?.quantity || 0), 0),
    [cart]
  );

  const resolveSystemPricing = useCallback((system) => {
    if (!system || typeof system !== "object") {
      return { listPrice: null, yourPrice: null, hasPrice: false };
    }
    const pricingRows = [system.outdoorProductCode, system.indoorProductCode, system.furnaceProductCode]
      .map((code) => resolvePricingForProductCode(code))
      .filter((entry) => pricingRowIsDisplayable(entry));
    if (!pricingRows.length) {
      return { listPrice: null, yourPrice: null, hasPrice: false };
    }
    return {
      listPrice: pricingRows.reduce((sum, entry) => sum + (Number.isFinite(entry.listPrice) ? entry.listPrice : 0), 0),
      yourPrice: pricingRows.reduce((sum, entry) => sum + (Number.isFinite(entry.yourPrice) ? entry.yourPrice : 0), 0),
      hasPrice: true,
    };
  }, [resolvePricingForProductCode]);

  const addToCart = useCallback(
    (system, type = "system", options = {}) => {
      const id = String(options?.itemId || system?.id || "").trim();
      if (!id) return;

      if (type === "system") {
        const gate = getSystemPriceGateForAddToCart(
          system,
          productDetails,
          priceLookupByCode,
          isGetPricePendingForCode
        );
        if (gate.pending || !gate.hasDisplayablePrice) return;
      }
      if (type === "product") {
        const code = String(options?.itemId || id || "").trim();
        const detail = productDetails[code] || {};
        const pricing = resolvePricingForCodeLayers(code, detail, priceLookupByCode);
        if (isProductAddToCartBlockedByPrice(code, pricing, isGetPricePendingForCode)) return;
      }

      const fallbackSystemPricing =
        type === "system" ? resolveSystemPricing(system) : { listPrice: null, yourPrice: null, hasPrice: false };
      const unitListPrice = Number.isFinite(options?.unitListPrice)
        ? Number(options.unitListPrice)
        : fallbackSystemPricing.listPrice;
      const unitYourPrice = Number.isFinite(options?.unitYourPrice)
        ? Number(options.unitYourPrice)
        : fallbackSystemPricing.yourPrice;
      const imageUrl = String(options?.imageUrl || "").trim();
      const title =
        String(options?.title || "").trim() || (type === "system" ? `AHRI #${system?.id || id}` : id);
      const subtitle =
        String(options?.subtitle || "").trim() || (type === "system" ? "Complete system" : "Product item");
      const lineComment = String(
        options?.lineComment || (type === "product" && system?.id ? `AHRI# ${system.id}` : "")
      ).trim();
      const quantityToAdd = normalizeCartQty(options?.quantity, 1);
      const uom = normalizeCartUom(options?.uom, "EA");

      setCart((prev) => {
        const existingIndex = prev.findIndex((item) => item.id === id && item.type === type);
        if (existingIndex >= 0) {
          const next = [...prev];
          next[existingIndex] = {
            ...next[existingIndex],
            quantity: Number(next[existingIndex].quantity || 0) + quantityToAdd,
            system: system || next[existingIndex].system,
            title,
            subtitle,
            imageUrl: imageUrl || next[existingIndex].imageUrl || "",
            unitListPrice: Number.isFinite(unitListPrice) ? unitListPrice : next[existingIndex].unitListPrice,
            unitYourPrice: Number.isFinite(unitYourPrice) ? unitYourPrice : next[existingIndex].unitYourPrice,
            lineComment: lineComment || next[existingIndex].lineComment || "",
            uom: uom || next[existingIndex].uom || "EA",
          };
          return next;
        }
        return [
          ...prev,
          {
            id,
            type,
            quantity: quantityToAdd,
            system,
            title,
            subtitle,
            imageUrl,
            unitListPrice: Number.isFinite(unitListPrice) ? unitListPrice : null,
            unitYourPrice: Number.isFinite(unitYourPrice) ? unitYourPrice : null,
            lineComment,
            uom,
          },
        ];
      });

      const lookupMap = priceLookupByCodeRef.current;
      if (type === "system") {
        invokeWindowAddToCartForAhriSystem(system, quantityToAdd, uom, lookupMap, debug);
      } else {
        const partCode = String(options?.itemId || id || "").trim();
        invokeWindowAddToCartForSingleProduct(partCode, quantityToAdd, uom, lookupMap, debug, {
          role: options?.componentRole || "product",
          ahriNumber: system?.id ? String(system.id).trim() : null,
        });
      }
    },
    [
      resolveSystemPricing,
      productDetails,
      priceLookupByCode,
      isGetPricePendingForCode,
      debug,
    ]
  );

  const addSystemProductsToCart = useCallback(
    (system, options = {}) => {
      const qty = normalizeCartQty(options?.quantity, 1);
      const uom = normalizeCartUom(options?.uom, "EA");
      addToCart(system, "system", { quantity: qty, uom });
    },
    [addToCart]
  );

  const updateCartQty = (id, type, nextQty) => {
    if (nextQty <= 0) {
      setCart((prev) => prev.filter((item) => !(item.id === id && item.type === type)));
      return;
    }
    setCart((prev) =>
      prev.map((item) => (item.id === id && item.type === type ? { ...item, quantity: nextQty } : item))
    );
  };

  const cartTotals = useMemo(() => {
    return cart.reduce(
      (totals, item) => {
        const qty = Math.max(0, Number(item?.quantity || 0));
        const lineList = Number.isFinite(item?.unitListPrice) ? Number(item.unitListPrice) * qty : 0;
        const lineYour = Number.isFinite(item?.unitYourPrice) ? Number(item.unitYourPrice) * qty : 0;
        return {
          listTotal: totals.listTotal + lineList,
          yourTotal: totals.yourTotal + lineYour,
        };
      },
      { listTotal: 0, yourTotal: 0 }
    );
  }, [cart]);

  const openSystemDetail = (system) => {
    setSelectedSystem({
      ...(system || {}),
    });
    setDetailOpen(true);
    // Fetch every approved alternative across all slots (not just the primary)
    // so the picker can show stock/pricing and hide out-of-stock options.
    const options = system?.componentOptions || {};
    const codes = Array.from(
      new Set(
        AHRI_COMPONENT_SLOTS.flatMap((slot) => options[slot.type]?.codes || [])
          .concat([system?.outdoorProductCode, system?.indoorProductCode, system?.furnaceProductCode])
          .map((c) => String(c || "").trim())
          .filter(Boolean)
      )
    );
    void fetchProductDetails(codes);
    void fetchPricesForProductCodes(codes);
  };

  const openProductDetail = (productCode, productType, systemContext = null) => {
    const code = String(productCode || "").trim();
    if (!code) return;
    setSelectedProduct({
      code,
      type: productType,
      system: systemContext,
      data: productDetails[code] || {},
    });
    setProductOpen(true);
    void fetchProductDetails([code]);
    void fetchPricesForProductCodes([code]);
  };

  // Opens the AHRI reference id on ahridirectory.org's public "details" page.
  // Known rows_programname values build the URL directly; otherwise our backend
  // proxy (GET .../systems/{id}/directory-url) resolves AHRI's ProgramId. The
  // tab is opened synchronously before the fetch, since browsers block a
  // window.open() made after an await inside a click handler.
  const openAhriDirectoryLink = useCallback(
    (ahriId, event, system) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      const normalizedId = String(ahriId || "").trim();
      if (!normalizedId) return;
      const directUrl = getAhriDirectoryUrlFromSystem(normalizedId, system);
      if (directUrl) {
        if (typeof window !== "undefined") window.open(directUrl, "_blank", "noopener,noreferrer");
        return;
      }
      if (!hasAuthHeaders(headers)) return;
      const newTab = typeof window !== "undefined" ? window.open("", "_blank", "noopener,noreferrer") : null;
      (async () => {
        try {
          const resp = await fetch(
            `${endpoint}/api/v1/ahri/systems/${encodeURIComponent(normalizedId)}/directory-url`,
            { method: "GET", headers }
          );
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || !data?.url) {
            if (newTab && !newTab.closed) newTab.close();
            return;
          }
          if (newTab && !newTab.closed) {
            newTab.location.href = data.url;
          } else if (typeof window !== "undefined") {
            window.open(data.url, "_blank", "noopener,noreferrer");
          }
        } catch (err) {
          if (newTab && !newTab.closed) newTab.close();
          if (debug) {
            // eslint-disable-next-line no-console
            console.warn("AHRI V6 widget directory link error:", err);
          }
        }
      })();
    },
    [headers, endpoint, debug]
  );

  // Renders the AHRI id as a clickable link to its AHRI Directory page; plain
  // "-" when there's no id.
  const renderAhriIdText = useCallback(
    (ahriId, system) => {
      if (!ahriId) return "-";
      return (
        <span
          role="button"
          tabIndex={0}
          className="impqx-ahri-shop-id-link"
          onClick={(event) => openAhriDirectoryLink(ahriId, event, system)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              openAhriDirectoryLink(ahriId, event, system);
            }
          }}
          title="Open on AHRI Directory"
        >
          {ahriId}
        </span>
      );
    },
    [openAhriDirectoryLink]
  );

  // URL-driven overlay restore: when the hash references a system/product and
  // the matching record is present in the loaded results, open the detail view.
  // Lets shared links and Back/Forward reopen a detail, not just the grid.
  //
  // Each distinct URL overlay state is auto-opened at most ONCE (tracked by
  // consumedOverlayKeyRef). This is what stops a closed popup from re-opening
  // when the user later applies a filter (which recomputes `systems` and re-runs
  // this effect) — closing via the drawer's own button doesn't change the URL,
  // so the key stays "consumed". A real Back/Forward changes the key and re-arms.
  const consumedOverlayKeyRef = useRef(null);
  useEffect(() => {
    if (!bootstrapped) return;
    const key = `${routeOverlay.system}|${routeOverlay.product}`;
    if (consumedOverlayKeyRef.current === key) return;

    if (!routeOverlay.system && !routeOverlay.product) {
      consumedOverlayKeyRef.current = key;
      return;
    }

    let handledAll = true;
    if (routeOverlay.system) {
      const alreadyOpen = detailOpen && String(selectedSystem?.id || "") === routeOverlay.system;
      if (!alreadyOpen) {
        const found = systems.find((s) => String(s?.id || "") === routeOverlay.system);
        if (found) openSystemDetail(found);
        // Record not in memory yet (cold load mid-fetch) — retry next `systems` change.
        else handledAll = false;
      }
    }
    if (routeOverlay.product) {
      const alreadyOpen = productOpen && String(selectedProduct?.code || "") === routeOverlay.product;
      if (!alreadyOpen) openProductDetail(routeOverlay.product, "product", null);
    }
    if (handledAll) consumedOverlayKeyRef.current = key;
    // openers / selected* deliberately omitted to avoid reopening loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOverlay, systems, bootstrapped]);

  // In-stock predicate for a product code at the currently selected warehouse.
  // When no warehouse is selected we cannot filter, so every code is treated as
  // available (the picker shows all options).
  const isCodeInStock = useCallback(
    (code) => isProductInStockAtWarehouse(lookupProductDetail(code, productDetails), selectedWarehouseCode),
    [productDetails, selectedWarehouseCode]
  );

  // Resolve inventory-first default selections whenever the open system, its
  // fetched product details, or the warehouse change. Preserves any existing
  // (still-visible) user choice for the slot.
  useEffect(() => {
    if (!selectedSystem?.componentOptions) return;
    const systemId = String(selectedSystem.id || "");
    const previousSelections = {};
    for (const slot of AHRI_COMPONENT_SLOTS) {
      const current = componentSelections[`${systemId}:${slot.type}`];
      if (current) previousSelections[slot.type] = current;
    }
    const { selections } = resolveConfiguration(selectedSystem.componentOptions, {
      isInStock: isCodeInStock,
      hideOutOfStock: true,
      previousSelections,
    });
    setComponentSelections((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const slot of AHRI_COMPONENT_SLOTS) {
        const key = `${systemId}:${slot.type}`;
        const value = selections[slot.type] || "";
        if (value && next[key] !== value) {
          next[key] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSystem, isCodeInStock]);

  const getSelectedComponentCode = useCallback(
    (slotType) => {
      if (!selectedSystem) return "";
      const systemId = String(selectedSystem.id || "");
      const chosen = componentSelections[`${systemId}:${slotType}`];
      if (chosen) return chosen;
      const option = selectedSystem.componentOptions?.[slotType];
      return option?.codes?.[0] || "";
    },
    [selectedSystem, componentSelections]
  );

  const selectComponentCode = useCallback(
    (slotType, code) => {
      if (!selectedSystem) return;
      const systemId = String(selectedSystem.id || "");
      const normalized = String(code || "").trim();
      if (!normalized) return;
      setComponentSelections((prev) => ({ ...prev, [`${systemId}:${slotType}`]: normalized }));
      void fetchProductDetails([normalized]);
      void fetchPricesForProductCodes([normalized]);
    },
    [selectedSystem, fetchProductDetails, fetchPricesForProductCodes]
  );

  // Build a system object whose per-slot product codes reflect the current
  // picker selections, so add-to-cart uses the chosen components (not just the
  // primary _product1 default).
  const buildConfiguredSystem = useCallback(
    (system) => {
      if (!system) return system;
      const systemId = String(system.id || "");
      const pick = (slotType, fallback) =>
        componentSelections[`${systemId}:${slotType}`] || fallback || "";
      return {
        ...system,
        outdoorProductCode: pick("outdoor", system.outdoorProductCode),
        indoorProductCode: pick("indoor", system.indoorProductCode),
        furnaceProductCode: pick("furnace", system.furnaceProductCode),
        componentSelections: {
          outdoor: pick("outdoor", system.outdoorProductCode),
          indoor: pick("indoor", system.indoorProductCode),
          furnace: pick("furnace", system.furnaceProductCode),
        },
      };
    },
    [componentSelections]
  );

  const selectedSystemProducts = useMemo(() => {
    if (!selectedSystem) return [];
    const optionsByType = selectedSystem.componentOptions || {};
    const slots = [
      { slot: "Outdoor Unit", type: "outdoor", code: getSelectedComponentCode("outdoor"), model: selectedSystem.outdoorModel },
      { slot: "Indoor Unit", type: "indoor", code: getSelectedComponentCode("indoor"), model: selectedSystem.indoorModel },
      { slot: "Furnace", type: "furnace", code: getSelectedComponentCode("furnace"), model: selectedSystem.furnaceModel },
    ];
    return slots
      .map((slot) => {
        const code = String(slot.code || "").trim();
        if (!code) return null;
        const allCodes = optionsByType[slot.type]?.codes || [];
        // Show every approved option (in-stock first); each option card shows its
        // own availability. Mirrors the card list so a slot with a single
        // in-stock option still exposes its alternatives.
        const inStockCodes = allCodes.filter((candidate) => isCodeInStock(candidate));
        const optionCodes = selectedWarehouseCode && inStockCodes.length
          ? [...inStockCodes, ...allCodes.filter((code) => !inStockCodes.includes(code))]
          : allCodes;
        const optionDiffs = computeOptionDifferentiators(
          optionCodes.map((candidate) => ({
            code: candidate,
            attributes: productDetails[candidate]?.attributes,
            description: decodeHtmlEntities(productDetails[candidate]?.description),
            category: productDetails[candidate]?.category,
          })),
          { maxKeys: 3 }
        );
        const optionDiffKeyLabels = (optionDiffs.labelsByCode[optionCodes[0]] || []).map(
          (entry) => entry.keyLabel
        );
        const slotOptions = optionCodes.map((candidate) => {
          const optDetail = lookupProductDetail(candidate, productDetails);
          const candName = decodeHtmlEntities(optDetail?.name) || candidate;
          const candPricing = resolvePricingForProductCode(candidate);
          const inStock = isCodeInStock(candidate);
          const diffLabels = optionDiffs.labelsByCode[candidate] || [];
          const dotClass = getAvailabilityDotClass(optDetail, selectedWarehouseCode, {
            pending: isProductDetailPending(candidate),
          });
          const stockStatus = getSlotAvailabilityStatus(
            dotClass,
            selectedWarehouseCode,
            selectedWarehouseLabel
          );
          const labelParts = [candName];
          if (candName !== candidate) labelParts.push(`(${candidate})`);
          if (pricingRowIsDisplayable(candPricing) && Number.isFinite(candPricing.yourPrice)) {
            labelParts.push(`· ${formatUsd(candPricing.yourPrice)}`);
          }
          if (selectedWarehouseCode && stockStatus) {
            labelParts.push(`· ${stockStatus.tone === "available" ? "In stock" : "Out of stock"}`);
          }
          for (const diff of diffLabels) {
            labelParts.push(`· ${diff.keyLabel}: ${diff.value}`);
          }
          return {
            code: candidate,
            inStock,
            name: candName,
            label: labelParts.join(" "),
            detail: optDetail,
            pricing: candPricing,
            diffLabels,
            dotClass,
            stockStatus,
            imageUrl: resolveAssetUrl(optDetail?.image_url, endpoint) || PRODUCT_IMAGE_PLACEHOLDER,
          };
        });
        const detail = lookupProductDetail(code, productDetails);
        const pricing = resolvePricingForProductCode(code);
        const attrs = detail?.attributes && typeof detail.attributes === "object"
          ? Object.entries(detail.attributes).filter(
              ([key, value]) => String(key || "").trim() && String(formatDetailValue(value) || "").trim()
            )
          : [];
        const imageUrl = resolveAssetUrl(detail.image_url, endpoint) || PRODUCT_IMAGE_PLACEHOLDER;
        return {
          ...slot,
          code,
          detail,
          pricing,
          attrs,
          imageUrl,
          slotOptions,
          optionDiffKeyLabels,
          hasChoices: slotOptions.length > 1,
          name: decodeHtmlEntities(detail?.name) || code,
          description: decodeHtmlEntities(detail?.description) || "",
        };
      })
      .filter(Boolean);
  }, [selectedSystem, productDetails, resolvePricingForProductCode, endpoint, getSelectedComponentCode, isCodeInStock, selectedWarehouseCode, selectedWarehouseLabel]);

  const selectedSystemChoiceSlotCount = useMemo(
    () => selectedSystemProducts.filter((product) => product.hasChoices).length,
    [selectedSystemProducts]
  );

  const selectedSystemTotals = useMemo(() => {
    const priceLoading = selectedSystemProducts.some((entry) =>
      isGetPricePendingForCode(entry.code)
    );
    const rows = selectedSystemProducts
      .map((entry) => entry.pricing)
      .filter((pricing) => pricingRowIsDisplayable(pricing));
    return {
      hasPrice: rows.length > 0,
      priceLoading,
      totalList: rows.reduce((sum, row) => sum + (Number.isFinite(row.listPrice) ? row.listPrice : 0), 0),
      totalYour: rows.reduce((sum, row) => sum + (Number.isFinite(row.yourPrice) ? row.yourPrice : 0), 0),
    };
  }, [selectedSystemProducts, isGetPricePendingForCode]);

  const selectedSystemAhriRows = useMemo(
    () => buildAhriRecordDetailRows(selectedSystem?.rawDoc),
    [selectedSystem]
  );

  const addProductToCart = useCallback(
    (product, systemContext = null, options = {}) => {
      if (!product?.code) return;
      const detail = productDetails[product.code] || {};
      const pricing = resolvePricingForCodeLayers(product.code, detail, priceLookupByCode);
      if (isProductAddToCartBlockedByPrice(product.code, pricing, isGetPricePendingForCode)) return;
      const qty = normalizeCartQty(options?.quantity, 1);
      const uom = normalizeCartUom(options?.uom, resolveProductUom(product?.detail));
      addToCart(systemContext || { id: product.code }, "product", {
        itemId: product.code,
        title: product.name || product.code,
        subtitle: `${product.slot || "Product"} • ${product.code}`,
        imageUrl: product.imageUrl || "",
        unitListPrice: product?.pricing?.listPrice,
        unitYourPrice: product?.pricing?.yourPrice,
        lineComment: systemContext?.id ? `AHRI# ${systemContext.id}` : "",
        quantity: qty,
        uom,
        componentRole: product.type || "product",
      });
    },
    [addToCart, productDetails, priceLookupByCode, isGetPricePendingForCode]
  );

  const toggleSystemSelection = useCallback((selectionKey, systemData = null) => {
    const key = String(selectionKey || "").trim();
    if (!key) return;
    const exists = selectedSystemKeys.includes(key);
    if (!exists && selectedSystemKeys.length >= MAX_COMPARE_SELECTIONS) {
      setError(`You can compare up to ${MAX_COMPARE_SELECTIONS} systems at a time.`);
      return;
    }
    setError("");
    setSelectedSystemKeys((prev) => {
      return prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key];
    });
    setSelectedSystemsByKey((prev) => {
      const exists = Object.prototype.hasOwnProperty.call(prev, key);
      if (exists) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: systemData || prev[key] || null };
    });
  }, [selectedSystemKeys]);

  const clearSelectedSystems = useCallback(() => {
    setSelectedSystemKeys([]);
    setSelectedSystemsByKey({});
    setError("");
  }, []);

  const selectedSystems = useMemo(
    () => selectedSystemKeys.map((key) => ({ key, system: selectedSystemsByKey[key] })).filter((entry) => entry.system),
    [selectedSystemKeys, selectedSystemsByKey]
  );

  const selectedSystemsAddToCartGate = useMemo(() => {
    if (!selectedSystems.length) {
      return { disabled: true, title: undefined };
    }
    let pending = false;
    let allHavePrice = true;
    selectedSystems.forEach(({ system }) => {
      const g = getSystemPriceGateForAddToCart(
        system,
        productDetails,
        priceLookupByCode,
        isGetPricePendingForCode
      );
      if (g.pending) pending = true;
      if (!g.hasDisplayablePrice) allHavePrice = false;
    });
    const disabled = pending || !allHavePrice;
    let title;
    if (pending) title = "Loading prices…";
    else if (!allHavePrice) {
      title = mustLoginBeforeGetprice()
        ? "Log in to see prices and add to cart"
        : "Pricing unavailable — add when prices are shown";
    }
    return { disabled, title };
  }, [selectedSystems, productDetails, priceLookupByCode, isGetPricePendingForCode]);

  const selectedSystemsOnPage = useMemo(
    () =>
      systems
        .map((system, index) => ({
          key: String(system?.id || "").trim() || `system-${index}`,
          system,
        }))
        .filter((entry) => selectedSystemKeys.includes(entry.key)),
    [systems, selectedSystemKeys]
  );

  const addSelectedSystemsToCart = useCallback(() => {
    selectedSystems.forEach((entry) => addToCart(entry.system, "system"));
  }, [selectedSystems, addToCart]);

  const compareSelectedSystems = useCallback(() => {
    const next = selectedSystems.slice(0, 3).map((entry) => ({ key: entry.key, system: entry.system }));
    if (!next.length) return;
    const comparedSystems = next.map((entry) => ({ ...(entry.system || {}), __selectionKey: entry.key }));
    const comparedProductCodes = [];
    comparedSystems.forEach((system) => {
      [system?.outdoorProductCode, system?.indoorProductCode, system?.furnaceProductCode]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
        .forEach((code) => comparedProductCodes.push(code));
    });
    void fetchProductDetails(comparedProductCodes);
    void fetchPricesForProductCodes(comparedProductCodes);
    setCompareColumnOrder(comparedSystems.map((_, index) => index));
    setCompareHighlightMode("none");
    setSelectedSystem({
      ...(comparedSystems[0] || {}),
      id: next.map((entry) => entry.system?.id || "-").join(" vs "),
      outdoorBrand: "Selection Compare",
      indoorBrand: `${next.length} systems`,
      isComparison: true,
      matchedAttrs: {
        systems: comparedSystems,
      },
    });
    setDetailOpen(true);
  }, [selectedSystems, fetchProductDetails, fetchPricesForProductCodes]);

  const comparisonSystems = useMemo(() => {
    const rows = selectedSystem?.matchedAttrs?.systems;
    if (!Array.isArray(rows)) return [];
    return rows.filter((entry) => entry && typeof entry === "object");
  }, [selectedSystem]);

  const isComparisonView = comparisonSystems.length > 1 || Boolean(selectedSystem?.isComparison);

  const detailDrawerSystemAddGate = useMemo(() => {
    if (!selectedSystem || isComparisonView) {
      return { pending: false, hasDisplayablePrice: true };
    }
    return getSystemPriceGateForAddToCart(
      selectedSystem,
      productDetails,
      priceLookupByCode,
      isGetPricePendingForCode
    );
  }, [selectedSystem, isComparisonView, productDetails, priceLookupByCode, isGetPricePendingForCode]);

  const orderedComparisonSystems = useMemo(() => {
    if (!comparisonSystems.length) return [];
    const nextOrder = Array.isArray(compareColumnOrder) && compareColumnOrder.length === comparisonSystems.length
      ? compareColumnOrder
      : comparisonSystems.map((_, index) => index);
    return nextOrder.map((index) => comparisonSystems[index]).filter(Boolean);
  }, [comparisonSystems, compareColumnOrder]);

  useEffect(() => {
    if (!isComparisonView) return;
    setCompareColumnOrder(comparisonSystems.map((_, index) => index));
  }, [isComparisonView, comparisonSystems]);

  useEffect(() => {
    if (!isComparisonView) {
      setCompareImageLoadFailedByCode({});
      return;
    }
    const visibleCodes = new Set(
      comparisonSystems
        .flatMap((system) => [system?.outdoorProductCode, system?.indoorProductCode, system?.furnaceProductCode])
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
    );
    setCompareImageLoadFailedByCode((prev) => {
      const next = {};
      visibleCodes.forEach((code) => {
        if (prev[code]) next[code] = true;
      });
      return next;
    });
  }, [isComparisonView, comparisonSystems]);

  const parseAttributeString = useCallback((raw) => {
    const out = {};
    let text = String(raw || "").trim();
    if (!text) return out;
    text = text.replace(/^[{\[\s]+|[}\]\s]+$/g, "").trim();
    const quotedPairRegex = /"([^"]+)"\s*:?\s*"([^"]*)"/g;
    let match = quotedPairRegex.exec(text);
    while (match) {
      const key = String(match[1] || "").trim();
      const value = String(match[2] || "").trim();
      if (key) out[key] = value;
      match = quotedPairRegex.exec(text);
    }
    if (Object.keys(out).length > 0) return out;
    text
      .split(/[;,]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        const idx = part.indexOf(":");
        if (idx === -1) return;
        const key = part.slice(0, idx).replace(/^"+|"+$/g, "").trim();
        const value = part.slice(idx + 1).replace(/^"+|"+$/g, "").trim();
        if (!key) return;
        out[key] = value;
      });
    return out;
  }, []);

  const getProductAttributes = useCallback((detail) => {
    if (!detail || typeof detail !== "object") return {};
    const candidates = [
      detail.attributes,
      detail.attributes_json,
      detail.attributesJson,
      detail.product_attributes,
      detail.productAttributes,
      detail.attribute_map,
      detail.attributeMap,
    ];
    for (const entry of candidates) {
      if (!entry) continue;
      if (typeof entry === "string") {
        try {
          const parsed = JSON.parse(entry);
          if (parsed && typeof parsed === "object") return parsed;
        } catch {
          const parsed = parseAttributeString(entry);
          if (Object.keys(parsed).length > 0) return parsed;
        }
        continue;
      }
      if (typeof entry === "object") return entry;
    }
    return {};
  }, [parseAttributeString]);

  const formatAttributeValue = useCallback((value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) {
      return value
        .map((entry) => formatAttributeValue(entry))
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
        .join(", ");
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        try {
          const parsed = JSON.parse(trimmed);
          return formatAttributeValue(parsed);
        } catch {
          return trimmed;
        }
      }
      if (trimmed.includes(":") && trimmed.includes(";")) {
        const parsed = parseAttributeString(trimmed);
        if (Object.keys(parsed).length > 0) {
          return Object.entries(parsed).map(([k, v]) => `${k}: ${formatAttributeValue(v)}`).join("; ");
        }
      }
      return trimmed;
    }
    if (typeof value === "object") {
      return Object.entries(value)
        .map(([k, v]) => `${k}: ${formatAttributeValue(v)}`)
        .filter((entry) => String(entry || "").trim())
        .join("; ");
    }
    return String(value);
  }, [parseAttributeString]);

  const buildAttributeEntries = useCallback((attrs) => {
    const rows = [];
    const source = attrs && typeof attrs === "object" ? attrs : {};
    Object.entries(source).forEach(([key, value]) => {
      if (value === null || value === undefined || String(value).trim() === "") return;
      if (typeof value === "string") {
        const parsed = parseAttributeString(value);
        if (Object.keys(parsed).length > 0) {
          Object.entries(parsed).forEach(([subKey, subVal]) => {
            const formatted = formatAttributeValue(subVal);
            if (String(formatted || "").trim()) rows.push({ key: subKey, value: formatted });
          });
          return;
        }
      }
      if (typeof value === "object" && !Array.isArray(value)) {
        Object.entries(value).forEach(([subKey, subVal]) => {
          const formatted = formatAttributeValue(subVal);
          if (String(formatted || "").trim()) rows.push({ key: subKey, value: formatted });
        });
        return;
      }
      const formatted = formatAttributeValue(value);
      if (!String(formatted || "").trim()) return;
      const parsed = parseAttributeString(formatted);
      if (Object.keys(parsed).length > 0) {
        Object.entries(parsed).forEach(([subKey, subVal]) => {
          const formattedSub = formatAttributeValue(subVal);
          if (String(formattedSub || "").trim()) rows.push({ key: subKey, value: formattedSub });
        });
      } else {
        rows.push({ key, value: formatted });
      }
    });
    return rows;
  }, [parseAttributeString, formatAttributeValue]);

  const compareAttributeRows = useMemo(() => {
    const buildRows = (label, getCode) => {
      const keys = new Set();
      orderedComparisonSystems.forEach((system) => {
        const detail = productDetails[String(getCode(system) || "").trim()] || {};
        const attrs = getProductAttributes(detail);
        buildAttributeEntries(attrs).forEach((entry) => keys.add(entry.key));
      });
      return Array.from(keys).map((key) => ({
        key: `${label}:${key}`,
        label: `${label} - ${key}`,
        value: (system) => {
          const detail = productDetails[String(getCode(system) || "").trim()] || {};
          const attrs = getProductAttributes(detail);
          const entries = buildAttributeEntries(attrs);
          return entries.find((entry) => entry.key === key)?.value || "-";
        },
      }));
    };
    return {
      outdoor: buildRows("Outdoor", (system) => system?.outdoorProductCode),
      indoor: buildRows("Indoor", (system) => system?.indoorProductCode),
      furnace: buildRows("Furnace", (system) => system?.furnaceProductCode),
    };
  }, [orderedComparisonSystems, productDetails, getProductAttributes, buildAttributeEntries]);

  const getSystemRawValue = useCallback((system, fields = []) => {
    const doc = system?.rawDoc;
    if (!doc || typeof doc !== "object") return "-";
    for (let index = 0; index < fields.length; index += 1) {
      const value = getValueFromDoc(doc, fields[index]);
      if (String(value || "").trim()) return value;
    }
    return "-";
  }, []);

  const compareRows = useMemo(() => ([
    { key: "product:outdoor", label: "Outdoor Unit", type: "product", productType: "outdoor" },
    ...(compareAttributeRows.outdoor || []),
    { key: "product:indoor", label: "Indoor Unit", type: "product", productType: "indoor" },
    ...(compareAttributeRows.indoor || []),
    { key: "product:furnace", label: "Furnace", type: "product", productType: "furnace" },
    ...(compareAttributeRows.furnace || []),
    { key: "seer2", label: "SEER2", value: (s) => s?.seer2 || "-" },
    { key: "hspf2", label: "HSPF2", value: (s) => s?.hspf2 || "-" },
    { key: "eer2", label: "EER2", value: (s) => s?.eer2 || "-" },
    { key: "coolingCapacity", label: "Cooling Capacity", value: (s) => getSystemRawValue(s, ["rows_coolingcapacity", "rows_cooling_capacity"]) },
    { key: "program", label: "Program", value: (s) => getSystemRawValue(s, ["rows_programname", "rows_program_name"]) },
    { key: "region", label: "Region", value: (s) => getSystemRawValue(s, ["rows_region"]) },
    { key: "energyStar", label: "Energy Star", value: (s) => (s?.isEnergyStar ? "Yes" : "No") },
    { key: "completeMatch", label: "Complete Match", value: (s) => (s?.completeMatch ? "Yes" : "No") },
    { key: "partialMatch", label: "Partial Match", value: (s) => (s?.partialMatch ? "Yes" : "No") },
    {
      key: "productCodes",
      label: "Product Codes",
      value: (s) => [s?.outdoorProductCode, s?.indoorProductCode, s?.furnaceProductCode].filter(Boolean).join(", ") || "-",
    },
  ]), [compareAttributeRows, getSystemRawValue]);

  const normalizeCompareValue = useCallback((value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) return value.map((entry) => String(entry ?? "")).join(",").toLowerCase();
    return String(value).trim().toLowerCase();
  }, []);

  const getProductHighlightClass = useCallback((productType, system) => {
    if (compareHighlightMode === "none") return "";
    const getCode = (row) => (productType === "outdoor"
      ? row?.outdoorProductCode
      : productType === "indoor"
        ? row?.indoorProductCode
        : row?.furnaceProductCode);
    const values = orderedComparisonSystems.map((entry) => normalizeCompareValue(getCode(entry)));
    const unique = new Set(values.filter((entry) => entry && entry !== "-"));
    if (!unique.size) return "";
    const allSame = unique.size === 1;
    if ((compareHighlightMode === "similar" || compareHighlightMode === "both") && allSame) return "compare-highlight-similar";
    if ((compareHighlightMode === "diff" || compareHighlightMode === "both") && !allSame) return "compare-highlight-diff";
    return "";
  }, [compareHighlightMode, orderedComparisonSystems, normalizeCompareValue]);

  const getRowCellHighlightClass = useCallback((row, system) => {
    if (row.type === "product") return "";
    if (compareHighlightMode === "none") return "";
    if (typeof row.value !== "function") return "";
    const values = orderedComparisonSystems.map((entry) => normalizeCompareValue(row.value(entry)));
    const unique = new Set(values.filter((entry) => entry && entry !== "-"));
    if (!unique.size) return "";
    const allSame = unique.size === 1;
    if (compareHighlightMode === "similar") return allSame ? "compare-highlight-similar" : "";
    if (compareHighlightMode === "diff") return allSame ? "" : "compare-highlight-diff";
    if (allSame) return "compare-highlight-similar";
    const target = normalizeCompareValue(row.value(system));
    const first = values[0];
    return target === first ? "compare-highlight-similar" : "compare-highlight-diff";
  }, [compareHighlightMode, orderedComparisonSystems, normalizeCompareValue]);

  const renderCompareProductCard = useCallback((code, label, system = null, slotType = "product") => {
    const normalizedCode = String(code || "").trim();
    if (!normalizedCode) return "-";
    const detail = productDetails[normalizedCode] || {};
    const imageSrc = resolveAssetUrl(detail.image_url || detail.imageUrl || detail.image, endpoint);
    const imageFailed = Boolean(compareImageLoadFailedByCode[normalizedCode]);
    const showImage = Boolean(imageSrc) && !imageFailed;
    const pricing = resolvePricingForCodeLayers(normalizedCode, detail, priceLookupByCode);
    const uom = resolveProductUom(detail);
    const cartInput = getCartInputValues("product", normalizedCode, { qty: 1, uom });
    return (
      <div key={`${label}-${normalizedCode}`} className="compare-product-card">
        <div className="compare-product-label">{label}</div>
        <div className="compare-product-body">
          {showImage ? (
            <img
              src={imageSrc}
              alt={`${label}: ${decodeHtmlEntities(detail?.name) || normalizedCode}`}
              onError={(event) => {
                event.currentTarget.onerror = null;
                setCompareImageLoadFailedByCode((prev) => (
                  prev[normalizedCode]
                    ? prev
                    : { ...prev, [normalizedCode]: true }
                ));
              }}
            />
          ) : (
            <div className="compare-product-placeholder">No image</div>
          )}
          <div className="compare-product-info">
            <div className="compare-product-name">{decodeHtmlEntities(detail?.name) || normalizedCode}</div>
            <div className="compare-product-meta">
              {detail?.manufacturer_part_number || detail?.manufacturerPartNumber || normalizedCode}
            </div>
            {detail?.category && <div className="compare-product-meta">{detail.category}</div>}
            {detail?.description && (
              <div
                className="compare-product-desc"
                dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(detail.description) }}
              />
            )}
          </div>
        </div>
        <div className="compare-product-cart">
          <div className="compare-product-price">
            {isGetPricePendingForCode(normalizedCode) ? (
              <span className="impqx-ahri-shop-subtitle">Loading…</span>
            ) : mustLoginBeforeGetprice() ? (
              <span className="impqx-ahri-shop-subtitle">Login for Price</span>
            ) : !pricingRowIsDisplayable(pricing) ? (
              <span className="impqx-ahri-shop-subtitle">Call for price</span>
            ) : (
              <span className="impqx-ahri-shop-price-pair">
                {shouldShowListPrice(pricing.listPrice, pricing.yourPrice) && (
                  <span className="impqx-ahri-shop-price-list">{formatUsd(pricing.listPrice)}</span>
                )}
                <span className="impqx-ahri-shop-price-your">{formatUsd(pricing.yourPrice)}</span>
              </span>
            )}
          </div>
          <div className="impqx-ahri-shop-cart-entry-inline">
            <input
              type="number"
              min={1}
              step={1}
              className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
              aria-label={`${label} quantity`}
              value={cartInput.qty}
              onChange={(event) => setCartInputField("product", normalizedCode, "qty", event.target.value)}
            />
            <button
              type="button"
              className="impqx-ahri-shop-button primary impqx-ahri-shop-system-add-btn"
              disabled={isProductAddToCartBlockedByPrice(normalizedCode, pricing, isGetPricePendingForCode)}
              title={productAddToCartBlockedTitle(normalizedCode, pricing, isGetPricePendingForCode)}
              onClick={() => {
                addProductToCart(
                  {
                    code: normalizedCode,
                    name: decodeHtmlEntities(detail?.name) || normalizedCode,
                    slot: label,
                    type: slotType,
                    imageUrl: imageSrc || "",
                    pricing,
                    detail,
                  },
                  system,
                  { quantity: cartInput.qty, uom: cartInput.uom }
                );
              }}
            >
              <span className="impqx-ahri-shop-system-add-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                  <circle cx="9" cy="20" r="1.6" />
                  <circle cx="18" cy="20" r="1.6" />
                  <path d="M3 4h2l2.2 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 .98-.79L20 8H7" />
                </svg>
              </span>
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    );
  }, [
    productDetails,
    endpoint,
    compareImageLoadFailedByCode,
    priceLookupByCode,
    isGetPricePendingForCode,
    getCartInputValues,
    setCartInputField,
    addProductToCart,
  ]);

  // "Add System" control for one compared system (header column on desktop,
  // card footer on mobile) -- mirrors the result-card Add System action.
  const renderCompareSystemAddAction = useCallback((system) => {
    const systemCartId = String(system?.id || system?.__selectionKey || "").trim();
    if (!systemCartId) return null;
    const gate = getSystemPriceGateForAddToCart(system, productDetails, priceLookupByCode, isGetPricePendingForCode);
    const cartInput = getCartInputValues("system", systemCartId, { qty: 1, uom: "EA" });
    return (
      <div className="impqx-ahri-shop-cart-entry-inline compare-system-cart" onClick={(event) => event.stopPropagation()}>
        <input
          type="number"
          min={1}
          step={1}
          className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
          aria-label="System quantity"
          value={cartInput.qty}
          onChange={(event) => setCartInputField("system", systemCartId, "qty", event.target.value)}
        />
        <button
          type="button"
          className="impqx-ahri-shop-button primary impqx-ahri-shop-system-add-btn"
          disabled={gate.pending || !gate.hasDisplayablePrice}
          title={systemAddToCartBlockedTitle(gate)}
          onClick={() => {
            addSystemProductsToCart(buildConfiguredSystem(system), {
              quantity: cartInput.qty,
              uom: cartInput.uom,
            });
          }}
        >
          <span className="impqx-ahri-shop-system-add-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" role="presentation" focusable="false">
              <circle cx="9" cy="20" r="1.6" />
              <circle cx="18" cy="20" r="1.6" />
              <path d="M3 4h2l2.2 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 .98-.79L20 8H7" />
            </svg>
          </span>
          Add System
        </button>
      </div>
    );
  }, [
    productDetails,
    priceLookupByCode,
    isGetPricePendingForCode,
    getCartInputValues,
    setCartInputField,
    addSystemProductsToCart,
    buildConfiguredSystem,
  ]);

  const selectedSystemAnchor = selectedSystems[0]?.system || selectedSystemsOnPage[0]?.system || null;

  const applyQuickNarrow = useCallback((mode) => {
    const anchor = selectedSystemAnchor;
    if (!anchor) return;
    const nextFilters = { ...selectedFilters };
    let nextProductCodes = [...productCodeFilters];

    if (mode === "brand") {
      const brand = String(anchor?.outdoorBrand || anchor?.indoorBrand || "").trim();
      if (brand) {
        const current = Array.isArray(nextFilters.brand) ? nextFilters.brand : [];
        if (!current.some((entry) => String(entry) === brand)) {
          nextFilters.brand = [...current, brand];
        }
      }
    }

    if (mode === "seer2") {
      const seer = String(anchor?.seer2 || "").trim();
      if (seer) {
        const current = Array.isArray(nextFilters.seer2) ? nextFilters.seer2 : [];
        if (!current.some((entry) => String(entry) === seer)) {
          nextFilters.seer2 = [...current, seer];
        }
      }
    }

    if (mode === "bundle") {
      [anchor?.outdoorProductCode, anchor?.indoorProductCode, anchor?.furnaceProductCode]
        .map((entry) => String(entry || "").trim())
        .filter(Boolean)
        .forEach((code) => {
          if (!nextProductCodes.some((entry) => String(entry) === code)) {
            nextProductCodes.push(code);
          }
        });
    }

    setSelectedFilters(nextFilters);
    setProductCodeFilters(nextProductCodes);
    if (autoLoad) {
      void fetchResults(1, query, nextFilters, nextProductCodes, selectedAdvancedFilters);
    } else {
      void fetchFacets(nextFilters, null, nextProductCodes, selectedAdvancedFilters);
    }
  }, [selectedSystemAnchor, selectedFilters, productCodeFilters, selectedAdvancedFilters, fetchFacets, autoLoad, fetchResults, query]);

  useEffect(() => {
    const closeAllOpenFilters = () => setOpenFilterByKey({});

    const handlePointerDown = (event) => {
      const standardFilterContainer = filterWrapRef.current;
      const warehouseEditorContainer = warehouseEditorRef.current;
      const qaDropdownContainer = qaValueDropdownRef.current;

      if (standardFilterContainer && !standardFilterContainer.contains(event.target)) {
        closeAllOpenFilters();
      }
      if (warehouseEditorContainer && !warehouseEditorContainer.contains(event.target)) {
        setWarehouseEditorOpen(false);
        setWarehouseSearchInput("");
      }
      if (qaDropdownContainer && !qaDropdownContainer.contains(event.target)) {
        setQaValueDropdownOpen(false);
      }
      if (cameraBtnRef.current && !cameraBtnRef.current.closest(".impqx-ahri-shop-camera-wrap")?.contains(event.target)) {
        setImageCameraMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAllOpenFilters();
        setQaValueDropdownOpen(false);
        setImageCameraMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!enabledInteractionModes.includes(interactionMode)) {
      setInteractionMode(enabledInteractionModes[0] || "filters");
    }
  }, [enabledInteractionModes, interactionMode]);

  useEffect(() => {
    const propFilterKey = productCodePropFilters.join("|");
    const previousPropSet = previousProductCodePropSetRef.current;
    const mergedProductCodes = (() => {
      const priorCodes = (Array.isArray(productCodeFilters) ? productCodeFilters : [])
        .map((entry) => String(entry || "").trim())
        .filter(Boolean);
      const widgetAddedCodes = priorCodes.filter((code) => !previousPropSet.has(code));
      return [...new Set([...productCodePropFilters, ...widgetAddedCodes])];
    })();
    if (!areStringListsEqual(productCodeFilters, mergedProductCodes)) {
      setProductCodeFilters(mergedProductCodes);
    }
    previousProductCodePropSetRef.current = new Set(productCodePropFilters);
    if (!bootstrapped) return;
    if (appliedProductCodePropKeyRef.current === propFilterKey) return;
    appliedProductCodePropKeyRef.current = propFilterKey;
    if (autoLoad) {
      void fetchResults(1, query, selectedFilters, mergedProductCodes, selectedAdvancedFilters);
    } else {
      void fetchFacets(selectedFilters, null, mergedProductCodes, selectedAdvancedFilters);
    }
  }, [
    productCodeFilters,
    productCodePropFilters,
    bootstrapped,
    selectedFilters,
    selectedAdvancedFilters,
    fetchFacets,
    autoLoad,
    fetchResults,
    query,
    appliedProductCodePropKeyRef,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const rootEl = widgetRootRef.current;
    const syncViewport = () => {
      const elementWidth = rootEl?.getBoundingClientRect?.().width || 0;
      const fallbackWidth = window.innerWidth || 0;
      const effectiveWidth = elementWidth > 0 ? elementWidth : fallbackWidth;
      setIsMobileFilterLayout(effectiveWidth <= MOBILE_FILTER_BREAKPOINT_PX);
    };
    syncViewport();
    let observer = null;
    if (typeof ResizeObserver !== "undefined" && rootEl) {
      observer = new ResizeObserver(() => syncViewport());
      observer.observe(rootEl);
    }
    window.addEventListener("resize", syncViewport);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", syncViewport);
    };
  }, []);

  useEffect(() => {
    const next = resolveMobileActiveFilterKey(
      filterPanelEntries.map((entry) => entry.panelKey),
      mobileActiveFilterKey
    );
    if (next !== mobileActiveFilterKey) {
      setMobileActiveFilterKey(next);
    }
  }, [filterPanelEntries, mobileActiveFilterKey]);

  useEffect(() => {
    if (!isMobileFilterLayout || effectiveInteractionMode !== "qa") return;
    if (qaFocusPanelKey && qaFocusPanelKey !== mobileActiveFilterKey) {
      setMobileActiveFilterKey(qaFocusPanelKey);
    }
  }, [isMobileFilterLayout, effectiveInteractionMode, qaFocusPanelKey, mobileActiveFilterKey]);

  useEffect(() => {
    if (allowCustomerModeSwitch) return;
    const defaultMode = normalizeInteractionMode(searchConfig?.interaction_mode, enabledInteractionModes);
    if (interactionMode !== defaultMode) {
      setInteractionMode(defaultMode);
    }
  }, [allowCustomerModeSwitch, searchConfig?.interaction_mode, enabledInteractionModes, interactionMode]);

  useEffect(() => {
    if (effectiveInteractionMode !== "qa") return;
    if (!qaAvailableFilters.length) {
      setQaFocusFilterKey("");
      return;
    }
    if (!qaFocusFilterKey || !qaAvailableFilters.some((filter) => String(filter.key) === String(qaFocusFilterKey))) {
      setQaFocusFilterKey(String(qaAvailableFilters[0].key || ""));
    }
  }, [effectiveInteractionMode, qaAvailableFilters, qaFocusFilterKey]);

  useEffect(() => {
    if (effectiveInteractionMode !== "qa" || isMobileFilterLayout) return undefined;
    const updateAll = () => {
      updateQaScrollState(qaAttributeRowRef, setQaAttributeScrollState);
    };
    updateAll();
    window.addEventListener("resize", updateAll);
    return () => {
      window.removeEventListener("resize", updateAll);
    };
  }, [effectiveInteractionMode, qaAvailableFilters, updateQaScrollState, isMobileFilterLayout]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    const removeEcoRebatesScriptTags = () => {
      const staleScripts = document.querySelectorAll("script[data-impqx-eco-rebates='true']");
      staleScripts.forEach((node) => {
        if (node && node.parentNode) node.parentNode.removeChild(node);
      });
    };
    const fullEcoRebatesCleanup = () => {
      removeEcoRebatesScriptTags();
      const previousIds = Array.isArray(ecoRebatesContainerIdsRef.current) ? ecoRebatesContainerIdsRef.current : [];
      if (typeof window !== "undefined" && window._ecr) {
        previousIds.forEach((containerId) => {
          if (containerId && window._ecr[containerId]) {
            delete window._ecr[containerId];
          }
        });
      }
      ecoRebatesContainerIdsRef.current = [];
    };
    if (!bootstrapped || !ecoRebatesConfig.enabled) {
      fullEcoRebatesCleanup();
      return fullEcoRebatesCleanup;
    }
    const isProductContext = productOpen && selectedProductRebateCode;
    const rebateMounts = [];
    if (isProductContext) {
      rebateMounts.push({
        keyValue: selectedProductRebateCode,
        containerId: getEcoRebatesContainerId(selectedProductRebateCode, "product"),
      });
    } else {
      systems.forEach((system) => {
        const ahriId = String(system?.id || "").trim();
        if (!ahriId) return;
        rebateMounts.push({ keyValue: ahriId, containerId: getEcoRebatesContainerId(ahriId, "card") });
      });
      if (detailOpen && selectedSystem?.id) {
        const ahriId = String(selectedSystem.id || "").trim();
        if (ahriId) rebateMounts.push({ keyValue: ahriId, containerId: getEcoRebatesContainerId(ahriId, "detail") });
      }
    }
    const uniqMounts = [];
    const seenContainerIds = new Set();
    rebateMounts.forEach((entry) => {
      const containerId = String(entry?.containerId || "").trim();
      const keyValue = String(entry?.keyValue || "").trim();
      if (!containerId || !keyValue || seenContainerIds.has(containerId)) return;
      seenContainerIds.add(containerId);
      uniqMounts.push({ keyValue, containerId });
    });
    if (!uniqMounts.length) return fullEcoRebatesCleanup;

    window._ecr = window._ecr || {};
    const previousIds = Array.isArray(ecoRebatesContainerIdsRef.current) ? ecoRebatesContainerIdsRef.current : [];
    previousIds.forEach((containerId) => {
      if (containerId && window._ecr[containerId]) {
        delete window._ecr[containerId];
      }
    });
    uniqMounts.forEach(({ containerId, keyValue }) => {
      if (!containerId || !keyValue) return;
      window._ecr[containerId] = [keyValue];
    });
    ecoRebatesContainerIdsRef.current = uniqMounts.map((entry) => entry.containerId);

    const zip = normalizeShipToZip(selectedShipToZip);
    const zipAsNumber = Number(zip);
    window._ecr.options = {
      ...(window._ecr.options || {}),
      searchIdType: isProductContext ? "sku" : "ahri",
      zip: Number.isFinite(zipAsNumber) && zipAsNumber > 0 ? zipAsNumber : undefined,
    };

    removeEcoRebatesScriptTags();

    const ecoRebatesBaseUrl = ecoRebatesConfig.base_url || "https://static.ecorebates.com/ui/widgets/demo";
    const scriptUrl = `${ecoRebatesBaseUrl.replace(/\/+$/, "")}/product.js`;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = scriptUrl;
    script.setAttribute("data-impqx-eco-rebates", "true");
    document.body.appendChild(script);
    return fullEcoRebatesCleanup;
  }, [systems, selectedShipToZip, detailOpen, selectedSystem?.id, productOpen, selectedProductRebateCode, ecoRebatesConfig, bootstrapped]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (!bootstrapped || !ecoRebatesConfig.enabled) return undefined;
    const cleanup = () => {
      ecoRebatesObserversRef.current.forEach((observer) => {
        try {
          observer?.disconnect();
        } catch {
          // no-op
        }
      });
      ecoRebatesObserversRef.current = [];
    };
    const hasRebateContent = (container) => {
      if (!container) return false;
      return hasEcoRebateContentText(container.textContent || "");
    };
    const emphasizeDollarAmounts = (container) => {
      if (!container || typeof document === "undefined") return;
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const nodes = [];
      let node = walker.nextNode();
      while (node) {
        const parentElement = node.parentElement;
        if (
          parentElement
          && !parentElement.classList.contains("impqx-ahri-shop-rebate-amount")
          && /\$\s*[\d,]+(?:\.\d+)?(?:\s*\/\s*ton)?/i.test(String(node.nodeValue || ""))
        ) {
          nodes.push(node);
        }
        node = walker.nextNode();
      }
      nodes.forEach((textNode) => {
        const raw = String(textNode.nodeValue || "");
        const matches = [...raw.matchAll(/\$\s*[\d,]+(?:\.\d+)?(?:\s*\/\s*ton)?/gi)];
        if (!matches.length) return;
        const fragment = document.createDocumentFragment();
        let cursor = 0;
        matches.forEach((match) => {
          const value = match[0];
          const index = typeof match.index === "number" ? match.index : -1;
          if (index < 0) return;
          if (index > cursor) {
            fragment.appendChild(document.createTextNode(raw.slice(cursor, index)));
          }
          const amount = document.createElement("span");
          amount.className = "impqx-ahri-shop-rebate-amount";
          amount.textContent = value;
          fragment.appendChild(amount);
          cursor = index + value.length;
        });
        if (cursor < raw.length) {
          fragment.appendChild(document.createTextNode(raw.slice(cursor)));
        }
        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(fragment, textNode);
        }
      });
    };
    const normalizeRebateCopy = (container) => {
      if (!container || typeof document === "undefined") return;
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        const raw = String(node.nodeValue || "");
        const cleaned = raw.replace(/\s+/g, " ").trim();
        if (
          cleaned
          && /rebates?\s+up\s+to/i.test(cleaned)
          && !/energy\s+rebate\s+up\s+to/i.test(cleaned)
        ) {
          const nextValue = raw.replace(/(\b\d+\b\s+)?rebates?\s+up\s+to/gi, (match, countPrefix) => {
            const prefix = String(countPrefix || "");
            return `${prefix}Energy Rebate Up To`;
          });
          if (nextValue !== raw) {
            node.nodeValue = nextValue;
          }
        }
        node = walker.nextNode();
      }
    };
    const parseSystemTonnage = (system) => {
      const doc = system?.rawDoc;
      if (!doc || typeof doc !== "object") return null;
      const tonnageFields = [
        "prodattr_s_tonnage",
        "rows_coolingcapacity",
        "rows_cooling_capacity",
        "rows_coolingcapacity95fm",
        "rows_coolingcapacity95fm1",
        "prodattr_s_cooling_capacity",
        "prodattr_s_prod_attr_cooling_capacity",
        "prodattr_s_btu_rating",
      ];
      const toNumber = (value) => {
        const cleaned = String(value || "").replace(/[^0-9.]+/g, "");
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : null;
      };
      const normalizeTonnage = (valueText) => {
        const text = String(valueText || "").trim().toLowerCase();
        if (!text) return null;
        const tonMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:ton|tons|t)\b/);
        if (tonMatch) {
          const tonValue = Number(tonMatch[1]);
          return Number.isFinite(tonValue) && tonValue > 0 ? tonValue : null;
        }
        const numeric = toNumber(text);
        if (!Number.isFinite(numeric) || numeric <= 0) return null;
        if (numeric >= 10000) {
          const converted = numeric / 12000;
          return converted > 0 ? Math.round(converted * 10) / 10 : null;
        }
        if (numeric <= 10) return numeric;
        return null;
      };
      for (let idx = 0; idx < tonnageFields.length; idx += 1) {
        const rawValue = getValueFromDoc(doc, tonnageFields[idx]);
        const tonnage = normalizeTonnage(rawValue);
        if (Number.isFinite(tonnage) && tonnage > 0) return tonnage;
      }
      return null;
    };
    const applyPerTonConversion = (container, tonnage) => {
      if (!container || !Number.isFinite(tonnage) || tonnage <= 0 || typeof document === "undefined") return;
      const formatUsd = (value) => {
        const rounded = Math.round(value * 100) / 100;
        return rounded % 1 === 0 ? `$${rounded.toFixed(0)}` : `$${rounded.toFixed(2)}`;
      };
      const tonnageLabel = Number.isInteger(tonnage) ? String(tonnage) : tonnage.toFixed(1);
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      const nodes = [];
      let node = walker.nextNode();
      while (node) {
        const raw = String(node.nodeValue || "");
        if (/\$\s*[\d,]+(?:\.\d+)?\s*\/\s*ton\b/i.test(raw) && !/\(\s*\d+(?:\.\d+)?\s*ton\s*\)/i.test(raw)) {
          nodes.push(node);
        }
        node = walker.nextNode();
      }
      nodes.forEach((textNode) => {
        const raw = String(textNode.nodeValue || "");
        const nextValue = raw.replace(/\$\s*([\d,]+(?:\.\d+)?)\s*\/\s*ton\b/gi, (_, rateText) => {
          const rate = Number(String(rateText || "").replace(/,/g, ""));
          if (!Number.isFinite(rate) || rate <= 0) return _;
          const total = rate * tonnage;
          return `${formatUsd(total)} (${tonnageLabel} ton)`;
        });
        if (nextValue !== raw) {
          textNode.nodeValue = nextValue;
        }
      });
    };
    const updateVisibility = (mount) => {
      const normalizedId = String(mount?.keyValue || "").trim();
      const containerId = String(mount?.containerId || "").trim();
      const mountType = String(mount?.type || "ahri").trim().toLowerCase();
      if (!normalizedId || !containerId) return;
      const matchedSystem = [
        ...(Array.isArray(systems) ? systems : []),
        selectedSystem || null,
        selectedProduct?.system || null,
      ].find((entry) => {
        const candidateId = String(entry?.id || "").trim();
        if (candidateId && mountType === "ahri" && candidateId === normalizedId) return true;
        const candidateProductCode = String(selectedProduct?.code || "").trim();
        return mountType === "sku" && candidateProductCode && candidateProductCode === normalizedId;
      });
      const tonnage = parseSystemTonnage(matchedSystem);
      const container = document.getElementById(containerId);
      normalizeRebateCopy(container);
      applyPerTonConversion(container, tonnage);
      emphasizeDollarAmounts(container);
      const hasRebate = hasRebateContent(container);
      setEcoRebateVisibleByContainerId((prev) => {
        if (prev[containerId] === hasRebate) return prev;
        return {
          ...prev,
          [containerId]: hasRebate,
        };
      });
    };

    cleanup();
    const isProductContext = productOpen && selectedProductRebateCode;
    const activeMounts = [];
    if (isProductContext) {
      activeMounts.push({
        keyValue: selectedProductRebateCode,
        containerId: getEcoRebatesContainerId(selectedProductRebateCode, "product"),
        type: "sku",
      });
    } else {
      systems.forEach((system) => {
        const ahriId = String(system?.id || "").trim();
        if (!ahriId) return;
        activeMounts.push({ keyValue: ahriId, containerId: getEcoRebatesContainerId(ahriId, "card"), type: "ahri" });
      });
      if (detailOpen && selectedSystem?.id) {
        const ahriId = String(selectedSystem.id || "").trim();
        if (ahriId) activeMounts.push({ keyValue: ahriId, containerId: getEcoRebatesContainerId(ahriId, "detail"), type: "ahri" });
      }
    }
    const uniqActiveMounts = [];
    const seenMountIds = new Set();
    activeMounts.forEach((entry) => {
      const containerId = String(entry?.containerId || "").trim();
      const keyValue = String(entry?.keyValue || "").trim();
      const type = String(entry?.type || "ahri").trim().toLowerCase();
      if (!containerId || !keyValue || seenMountIds.has(containerId)) return;
      seenMountIds.add(containerId);
      uniqActiveMounts.push({ keyValue, containerId, type });
    });
    setEcoRebateVisibleByContainerId((prev) => {
      const next = {};
      uniqActiveMounts.forEach((entry) => {
        next[entry.containerId] = !!prev[entry.containerId];
      });
      return next;
    });
    uniqActiveMounts.forEach((mount) => {
      const containerId = mount.containerId;
      const container = document.getElementById(containerId);
      if (!container || typeof MutationObserver === "undefined") return;
      const observer = new MutationObserver(() => {
        updateVisibility(mount);
      });
      observer.observe(container, { childList: true, subtree: true, characterData: true });
      ecoRebatesObserversRef.current.push(observer);
      updateVisibility(mount);
      window.setTimeout(() => updateVisibility(mount), 800);
      window.setTimeout(() => updateVisibility(mount), 2000);
    });

    return cleanup;
  }, [systems, selectedShipToZip, detailOpen, selectedSystem, productOpen, selectedProduct?.system, selectedProductRebateCode, selectedProduct?.code, ecoRebatesConfig, bootstrapped]);

  useEffect(() => {
    setProductImageLoadFailed(false);
  }, [selectedProduct?.code, selectedProduct?.data?.image_url, productOpen]);

  useEffect(() => {
    const anyOpen = productOpen || detailOpen || cartOpen || rebateDetailsState?.open;
    if (!anyOpen) return undefined;
    detectEmbeddedHeaderOffset();
    const handleViewportChange = () => detectEmbeddedHeaderOffset();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, { passive: true });
    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange);
    };
  }, [productOpen, detailOpen, cartOpen, rebateDetailsState?.open, detectEmbeddedHeaderOffset]);

  const selectedProductName = useMemo(
    () => decodeHtmlEntities(selectedProduct?.data?.name) || selectedProduct?.code || "-",
    [selectedProduct?.data?.name, selectedProduct?.code]
  );
  const selectedProductPricing = useMemo(
    () => (selectedProduct?.code ? resolvePricingForProductCode(selectedProduct.code) : { listPrice: null, yourPrice: null }),
    [selectedProduct?.code, resolvePricingForProductCode]
  );
  const selectedProductImageUrl = useMemo(() => {
    const code = selectedProduct?.code;
    // productDetails[code] is updated async after the popup opens; prefer it
    // over the snapshot in selectedProduct.data so the image appears once loaded.
    const liveImageUrl = code ? productDetails[code]?.image_url : null;
    return (
      resolveAssetUrl(liveImageUrl || selectedProduct?.data?.image_url, endpoint) ||
      PRODUCT_IMAGE_PLACEHOLDER
    );
  }, [selectedProduct?.code, selectedProduct?.data?.image_url, productDetails, endpoint]);
  const selectedProductCartInput = useMemo(
    () => getCartInputValues("product", selectedProduct?.code || "", {
      qty: 1,
      uom: resolveProductUom(selectedProduct?.data),
    }),
    [getCartInputValues, selectedProduct?.code, selectedProduct?.data]
  );
  const selectedProductPdpPriceUi = useMemo(() => {
    const code = selectedProduct?.code;
    if (!code) {
      return { mode: "empty", addDisabled: true, addTitle: undefined };
    }
    const pricing = resolvePricingForProductCode(code);
    const addDisabled = isProductAddToCartBlockedByPrice(code, pricing, isGetPricePendingForCode);
    const addTitle = productAddToCartBlockedTitle(code, pricing, isGetPricePendingForCode);
    if (isGetPricePendingForCode(code)) return { mode: "pending", addDisabled, addTitle, pricing };
    if (mustLoginBeforeGetprice()) return { mode: "login", addDisabled, addTitle, pricing };
    if (!pricingRowIsDisplayable(pricing)) return { mode: "call", addDisabled, addTitle, pricing };
    return { mode: "price", addDisabled, addTitle, pricing };
  }, [selectedProduct?.code, resolvePricingForProductCode, isGetPricePendingForCode]);
  const closeRebateDetails = useCallback(() => {
    rebateDetailsRequestIdRef.current += 1;
    setRebateDetailsState((prev) => ({
      ...prev,
      open: false,
      loading: false,
      error: "",
      requestId: rebateDetailsRequestIdRef.current,
    }));
  }, []);

  // Icon / "More info" / pill padding should trigger EcoRebates' native click target,
  // not our legacy details drawer (its API route is not deployed).
  const activateRebateDetails = useCallback((containerId, event) => {
    if (isEcoRebateNativeClickTarget(event?.target)) return;
    if (!triggerEcoRebateNativeClick(containerId)) return;
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    if (event && typeof event.stopPropagation === "function") event.stopPropagation();
  }, []);

  // Auto-switch tabs only on fresh searches (page 1), not during pagination.
  useEffect(() => {
    if (loading || productSearchLoading) return;
    if (productPage > 1) return;
    if (totalFound > 0) {
      setActiveResultsTab("ahri");
    } else if (productSearchTotal > 0) {
      setActiveResultsTab("products");
    }
  }, [loading, productSearchLoading, totalFound, productSearchTotal, productPage]);

  return (
    <>
    <div ref={widgetRootRef} className="impqx-ahri-shop-widget container-fluid px-0">
      <div className="impqx-ahri-shop-shell">
        <div className="impqx-ahri-shop-header">
          {ruleSetPreviewNamesRef.current.length > 0 && (
            <div className="impqx-ahri-shop-rule-preview-banner" role="status">
              <FontAwesomeIcon icon={faEye} />
              <span>
                Previewing rule set{ruleSetPreviewNamesRef.current.length > 1 ? "s" : ""}:{" "}
                <strong>{ruleSetPreviewNamesRef.current.join(", ")}</strong>
              </span>
            </div>
          )}
          <div className="impqx-ahri-shop-top">
            <div className="impqx-ahri-shop-header-main">
              <h2 className="impqx-ahri-shop-title">{title}</h2>
            </div>
            <div className="impqx-ahri-shop-actions d-flex flex-wrap">
              <div className="impqx-ahri-shop-actions-search">
                <div className="impqx-ahri-shop-search-camera-group">
                  <div className="impqx-ahri-shop-search-control">
                    <span className="impqx-ahri-shop-search-prefix-icon" aria-hidden="true">
                      <FontAwesomeIcon icon={faMagnifyingGlass} />
                    </span>
                    <input
                      id="impqx-ahri-shop-search"
                      className="impqx-ahri-shop-input impqx-ahri-shop-search-input"
                      aria-label="Search systems"
                      value={searchInput}
                      placeholder={searchConfig?.placeholder || "Search systems"}
                      onChange={(event) => setSearchInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") onSearch();
                        if (event.key === "Escape" && searchInput) clearSearch();
                      }}
                    />
                    {searchInput && (
                      <button
                        type="button"
                        className="impqx-ahri-shop-search-clear-btn"
                        onClick={clearSearch}
                        aria-label="Clear search text"
                        title="Clear search"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                  <input
                    ref={imageFileInputRef}
                    type="file"
                    accept="image/*"
                    aria-hidden="true"
                    className="impqx-ahri-shop-image-file-input"
                    onChange={(e) => { setImageCameraMenuOpen(false); handleImageSearch(e.target.files?.[0]); }}
                  />
                  <button
                    type="button"
                    className="impqx-ahri-shop-button impqx-ahri-shop-search-icon-btn"
                    onClick={onSearch}
                    disabled={loading}
                    aria-label={loading ? "Searching" : "Search systems"}
                    title={loading ? "Searching..." : "Search"}
                  >
                    <span className="impqx-ahri-shop-search-button-text">Search</span>
                  </button>
                  {imageSearchEnabled && (
                  <div className="impqx-ahri-shop-camera-wrap">
                    <button
                      ref={cameraBtnRef}
                      type="button"
                      className={`impqx-ahri-shop-button impqx-ahri-shop-camera-btn${imageSearchLoading ? " loading" : ""}${imageCameraMenuOpen ? " active" : ""}`}
                      onClick={() => setImageCameraMenuOpen((o) => !o)}
                      disabled={imageSearchLoading || loading}
                      aria-label={imageSearchLoading ? "Extracting model from image" : "Search by equipment nameplate photo"}
                      aria-expanded={imageCameraMenuOpen}
                      title={imageSearchLoading ? "Reading image…" : "Search by equipment nameplate — take or upload a photo of the unit's nameplate label"}
                    >
                      <FontAwesomeIcon icon={faCamera} />
                    </button>
                    {imageCameraMenuOpen && (
                      <div className="impqx-ahri-shop-camera-menu" role="menu">
                        <button
                          type="button"
                          className="impqx-ahri-shop-camera-menu-item"
                          role="menuitem"
                          onClick={() => {
                            setImageCameraMenuOpen(false);
                            handleCameraPick();
                          }}
                        >
                          <FontAwesomeIcon icon={faCamera} />
                          Take Photo
                        </button>
                        <button
                          type="button"
                          className="impqx-ahri-shop-camera-menu-item"
                          role="menuitem"
                          onClick={() => { setImageCameraMenuOpen(false); imageFileInputRef.current?.click(); }}
                        >
                          <FontAwesomeIcon icon={faImage} />
                          Upload Image
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                </div>
                {imageSearchError && (
                  <div className="impqx-ahri-shop-image-search-error" role="alert">
                    <span className="impqx-ahri-shop-image-search-error-text">{imageSearchError}</span>
                    {imageConfigInfo && (
                      <span className="impqx-ahri-shop-image-search-error-cfg">
                        {" — "}Account {imageConfigInfo.account_id}, key: {imageConfigInfo.api_key_prefix}
                        {imageConfigInfo.api_key_is_demo && " (placeholder — update OpenAI key in AHRI settings for this account)"}
                      </span>
                    )}
                    {!imageConfigInfo && (
                      <button
                        type="button"
                        className="impqx-ahri-shop-image-search-error-check"
                        onClick={checkImageConfig}
                      >
                        Check config
                      </button>
                    )}
                    <button
                      type="button"
                      className="impqx-ahri-shop-image-search-error-dismiss"
                      onClick={() => { setImageSearchError(""); setImageConfigInfo(null); }}
                      aria-label="Dismiss"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </div>
                )}
                <div className="impqx-ahri-shop-warehouse-wrap" ref={warehouseEditorRef}>
                  <button
                    type="button"
                    className={`impqx-ahri-shop-button impqx-ahri-shop-warehouse-toggle ${warehouseEditorOpen ? "active" : ""}`}
                    title="Set warehouse code"
                    aria-label="Set warehouse code"
                    onClick={() => {
                      if (warehouseEditorOpen) {
                        cancelWarehouseEditor();
                      } else {
                        openWarehouseEditor();
                      }
                    }}
                  >
                    <span className="impqx-ahri-shop-warehouse-icon" aria-hidden="true">
                      <FontAwesomeIcon icon={faWarehouse} />
                    </span>
                    <span className="impqx-ahri-shop-warehouse-label">
                      {selectedWarehouseLabel || "Warehouse"}
                    </span>
                  </button>
                  {warehouseEditorOpen && (
                    <div className="impqx-ahri-shop-warehouse-popup" role="dialog" aria-label="Warehouse selector">
                      {warehouseOptions.length > 0 ? (
                        <>
                          <input
                            className="impqx-ahri-shop-input impqx-ahri-shop-warehouse-input impqx-ahri-shop-warehouse-search"
                            aria-label="Search branches"
                            value={warehouseSearchInput}
                            placeholder="Search branch, city, street, or code"
                            onChange={(event) => setWarehouseSearchInput(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Escape") cancelWarehouseEditor();
                            }}
                          />
                          <div
                            id="impqx-ahri-shop-warehouse-select"
                            className="impqx-ahri-shop-warehouse-option-list"
                            role="listbox"
                            aria-label="Select branch"
                          >
                            {filteredWarehouseOptions.map((warehouse) => (
                              <button
                                key={`warehouse-${warehouse.branchCode}`}
                                type="button"
                                className={`impqx-ahri-shop-warehouse-option ${
                                  normalizeWarehouseCode(warehouse.branchCode) === normalizeWarehouseCode(selectedWarehouseCode)
                                    ? "is-selected"
                                    : ""
                                }`}
                                role="option"
                                aria-selected={normalizeWarehouseCode(warehouse.branchCode) === normalizeWarehouseCode(selectedWarehouseCode)}
                                onClick={() => saveWarehouseEditor(warehouse.branchCode)}
                              >
                                <strong>{warehouse.branchName}</strong>
                                <span>{[warehouse.locality, warehouse.street].filter(Boolean).join(" | ")}</span>
                              </button>
                            ))}
                          </div>
                          {filteredWarehouseOptions.length === 0 && (
                            <div className="impqx-ahri-shop-warehouse-empty">No matching branches</div>
                          )}
                        </>
                      ) : (
                        <>
                          <label className="impqx-ahri-shop-popup-input-label" htmlFor="impqx-ahri-shop-warehouse-code-input">
                            Warehouse Code
                          </label>
                          <input
                            id="impqx-ahri-shop-warehouse-code-input"
                            className="impqx-ahri-shop-input impqx-ahri-shop-warehouse-input"
                            aria-label="Warehouse code"
                            value={warehouseDraftCode}
                            placeholder="Warehouse code (e.g. 014)"
                            onChange={(event) => setWarehouseDraftCode(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") saveWarehouseEditor();
                              if (event.key === "Escape") cancelWarehouseEditor();
                            }}
                          />
                          <div className="impqx-ahri-shop-warehouse-popup-actions">
                            <button type="button" className="impqx-ahri-shop-button primary" onClick={() => saveWarehouseEditor()}>
                              Save
                            </button>
                            <button type="button" className="impqx-ahri-shop-button" onClick={cancelWarehouseEditor}>
                              Cancel
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="impqx-ahri-shop-warehouse-wrap">
                  <button
                    type="button"
                    className={`impqx-ahri-shop-button impqx-ahri-shop-warehouse-toggle ${shipToZipEditorOpen ? "active" : ""}`}
                    title="Set Ship To ZIP"
                    aria-label="Set Ship To ZIP"
                    onClick={() => {
                      if (shipToZipEditorOpen) {
                        cancelShipToZipEditor();
                      } else {
                        openShipToZipEditor();
                      }
                    }}
                  >
                    <span className="impqx-ahri-shop-warehouse-icon" aria-hidden="true">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </span>
                    <span className="impqx-ahri-shop-warehouse-label">
                      {normalizeShipToZip(shipToZipInput) || "Ship To ZIP"}
                    </span>
                  </button>
                  {shipToZipEditorOpen && (
                    <div className="impqx-ahri-shop-warehouse-popup" role="dialog" aria-label="Ship To ZIP selector">
                      <input
                        className="impqx-ahri-shop-input impqx-ahri-shop-warehouse-input"
                        aria-label="Ship To ZIP"
                        value={shipToZipDraft}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={5}
                        placeholder="Ship To ZIP (e.g. 92227)"
                        onChange={(event) => setShipToZipDraft(normalizeShipToZip(event.target.value))}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") saveShipToZipEditor();
                          if (event.key === "Escape") cancelShipToZipEditor();
                        }}
                      />
                      <div className="impqx-ahri-shop-warehouse-popup-actions">
                        <button type="button" className="impqx-ahri-shop-button primary" onClick={saveShipToZipEditor}>
                          Save
                        </button>
                        <button type="button" className="impqx-ahri-shop-button" onClick={cancelShipToZipEditor}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="impqx-ahri-shop-button impqx-ahri-shop-clear-filters-icon-btn"
                  onClick={clearAllFilters}
                  disabled={!hasAnyFilter}
                  aria-label="Clear Filters"
                  title="Clear Filters"
                >
                  <span className="impqx-ahri-shop-clear-filters-icon-wrap" aria-hidden="true">
                    <FontAwesomeIcon icon={faFilter} />
                    <span className="impqx-ahri-shop-clear-filters-slash" />
                  </span>
                  <span>Clear</span>
                </button>
              </div>
              {imageExtractionCard && (
                <div className="impqx-ahri-shop-extraction-wrap">
                  <div
                    className="impqx-ahri-shop-extraction-card"
                    onClick={() => setExtractionCardExpanded((v) => !v)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExtractionCardExpanded((v) => !v); }}
                    aria-expanded={extractionCardExpanded}
                    aria-label="Nameplate reader result — click to expand"
                  >
                    <button
                      type="button"
                      className="impqx-ahri-shop-extraction-thumb-btn"
                      onClick={(e) => { e.stopPropagation(); setNameplateLightboxOpen(true); }}
                      aria-label="View full-size nameplate image"
                      title="View full-size image"
                    >
                      <img
                        src={imageExtractionCard.imageDataUrl}
                        className="impqx-ahri-shop-extraction-thumb"
                        alt="Captured nameplate"
                      />
                    </button>
                    <div className="impqx-ahri-shop-extraction-info">
                      <span className="impqx-ahri-shop-extraction-label">Nameplate Reader</span>
                      <span className="impqx-ahri-shop-extraction-model">{imageExtractionCard.modelNumber}</span>
                    </div>
                    <div className="impqx-ahri-shop-extraction-card-actions">
                      <span className="impqx-ahri-shop-extraction-expand-btn" aria-hidden="true">
                        <FontAwesomeIcon icon={extractionCardExpanded ? faChevronUp : faChevronDown} />
                      </span>
                      <button
                        type="button"
                        className="impqx-ahri-shop-extraction-dismiss-btn"
                        onClick={(e) => { e.stopPropagation(); setImageExtractionCard(null); setExtractionCardExpanded(false); setNameplateLightboxOpen(false); }}
                        aria-label="Dismiss nameplate card"
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  </div>
                  {extractionCardExpanded && (
                    <div className="impqx-ahri-shop-extraction-panel" onClick={(e) => e.stopPropagation()}>
                      <div className="impqx-ahri-shop-extraction-panel-header">
                        <button
                          type="button"
                          className="impqx-ahri-shop-extraction-thumb-btn"
                          onClick={(e) => { e.stopPropagation(); setNameplateLightboxOpen(true); }}
                          aria-label="View full-size nameplate image"
                          title="View full-size image"
                        >
                          <img
                            src={imageExtractionCard.imageDataUrl}
                            className="impqx-ahri-shop-extraction-panel-thumb"
                            alt="Captured nameplate"
                          />
                        </button>
                        <div>
                          <div className="impqx-ahri-shop-extraction-panel-title">Nameplate Reader</div>
                          <div className="impqx-ahri-shop-extraction-panel-model">{imageExtractionCard.modelNumber}</div>
                        </div>
                      </div>
                      <div className="impqx-ahri-shop-extraction-panel-fields">
                        {imageExtractionCard.extractedInfo?.brand && (
                          <>
                            <span className="impqx-ahri-shop-extraction-panel-key">Brand</span>
                            <span className="impqx-ahri-shop-extraction-panel-val">{imageExtractionCard.extractedInfo.brand}</span>
                          </>
                        )}
                        {imageExtractionCard.extractedInfo?.serial_number && (
                          <>
                            <span className="impqx-ahri-shop-extraction-panel-key">Serial</span>
                            <span className="impqx-ahri-shop-extraction-panel-val">{imageExtractionCard.extractedInfo.serial_number}</span>
                          </>
                        )}
                        {imageExtractionCard.extractedInfo?.voltage && (
                          <>
                            <span className="impqx-ahri-shop-extraction-panel-key">Voltage</span>
                            <span className="impqx-ahri-shop-extraction-panel-val">{imageExtractionCard.extractedInfo.voltage}V</span>
                          </>
                        )}
                        {imageExtractionCard.extractedInfo?.refrigerant && (
                          <>
                            <span className="impqx-ahri-shop-extraction-panel-key">Refrigerant</span>
                            <span className="impqx-ahri-shop-extraction-panel-val">{imageExtractionCard.extractedInfo.refrigerant}</span>
                          </>
                        )}
                        {imageExtractionCard.extractedInfo?.capacity && (
                          <>
                            <span className="impqx-ahri-shop-extraction-panel-key">Capacity</span>
                            <span className="impqx-ahri-shop-extraction-panel-val">{imageExtractionCard.extractedInfo.capacity}</span>
                          </>
                        )}
                        {imageExtractionCard.extractedInfo?.other_fields &&
                          Object.entries(imageExtractionCard.extractedInfo.other_fields)
                            .filter(([, v]) => v != null && v !== "")
                            .map(([k, v]) => (
                              <Fragment key={k}>
                                <span className="impqx-ahri-shop-extraction-panel-key">{k.replace(/_/g, " ")}</span>
                                <span className="impqx-ahri-shop-extraction-panel-val">{String(v)}</span>
                              </Fragment>
                            ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {nameplateLightboxOpen && imageExtractionCard && createPortal(
          <div
            className="impqx-ahri-shop-nameplate-lightbox-overlay"
            onClick={() => setNameplateLightboxOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Nameplate image"
          >
            <div className="impqx-ahri-shop-nameplate-lightbox" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="impqx-ahri-shop-nameplate-lightbox-close"
                onClick={() => setNameplateLightboxOpen(false)}
                aria-label="Close image"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
              <img
                src={imageExtractionCard.imageDataUrl}
                className="impqx-ahri-shop-nameplate-lightbox-img"
                alt="Nameplate — full size"
              />
              <div className="impqx-ahri-shop-nameplate-lightbox-caption">
                {imageExtractionCard.modelNumber}
              </div>
            </div>
          </div>,
          document.body
        )}

        {showMissingAuthBanner && (
          <div className="impqx-ahri-shop-banner error">
            API key/token missing. Configure `apiKey` to query AHRI APIs.
          </div>
        )}

        {customerVisibleError && (
          <div className="impqx-ahri-shop-banner error">{customerVisibleError}</div>
        )}

        <section className="impqx-ahri-shop-filter-card">
          <div className="impqx-ahri-shop-card-head">
            {allowCustomerModeSwitch && enabledInteractionModes.length > 1 && (
              <div className="impqx-ahri-shop-mode-switch" role="tablist" aria-label="Interaction mode">
                {enabledInteractionModes.map((mode) => (
                  <button
                    key={`interaction-mode-${mode}`}
                    type="button"
                    className={`impqx-ahri-shop-button ${effectiveInteractionMode === mode ? "primary" : ""}`}
                    onClick={() => setInteractionMode(mode)}
                  >
                    {mode === "qa" ? "Q&A Mode" : "Filters Mode"}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="row g-3 align-items-start">
            <div className="col-12 col-xl-8">
              {effectiveInteractionMode === "qa" ? (
                <div className="impqx-ahri-shop-qa-compact">
                  <div className="impqx-ahri-shop-qa-prompt">
                    {conversationalSelectionText}
                  </div>
                  {isMobileFilterLayout ? (
                    <div className="impqx-ahri-shop-mobile-filter-picker">
                      <label className="impqx-ahri-shop-mobile-filter-label" htmlFor="impqx-ahri-qa-filter-field">
                        Filter field
                      </label>
                      <select
                        id="impqx-ahri-qa-filter-field"
                        className="impqx-ahri-shop-input impqx-ahri-shop-mobile-filter-select"
                        value={qaFocusPanelKey}
                        onChange={(event) => {
                          const selectedPanelKey = String(event.target.value || "");
                          const selectedEntry = qaAvailableFilters.find((entry) => entry.panelKey === selectedPanelKey);
                          if (!selectedEntry) return;
                          setQaFocusFilterKey(String(selectedEntry.key || ""));
                          setMobileActiveFilterKey(selectedPanelKey);
                          setQaValueDropdownOpen(true);
                        }}
                      >
                        {qaAvailableFilters.map((filter) => {
                          const source = filter.type === "advanced" ? selectedAdvancedFilters : selectedFilters;
                          const selectedCount = Array.isArray(source?.[filter.key]) ? source[filter.key].length : 0;
                          return (
                            <option key={`qa-mobile-focus-${filter.panelKey}`} value={filter.panelKey}>
                              {selectedCount > 0 ? `${filter.label} (${selectedCount})` : filter.label}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  ) : (
                    <div className="impqx-ahri-shop-qa-scroll-shell">
                      {(qaAttributeScrollState.canLeft || qaAttributeScrollState.canRight) && (
                        <button
                          type="button"
                          className="impqx-ahri-shop-qa-scroll-btn left"
                          onClick={() => scrollQaRowBy(qaAttributeRowRef, -220)}
                          disabled={!qaAttributeScrollState.canLeft}
                          aria-label="Scroll attributes left"
                        >
                          ‹
                        </button>
                      )}
                      <div
                        ref={qaAttributeRowRef}
                        className="impqx-ahri-shop-qa-attribute-row"
                        role="tablist"
                        aria-label="Start building by attribute"
                        onScroll={() => updateQaScrollState(qaAttributeRowRef, setQaAttributeScrollState)}
                      >
                        {qaAvailableFilters.map((filter) => {
                          const isActive = String(qaFocusFilter?.key || "") === String(filter.key || "");
                          const source = filter.type === "advanced" ? selectedAdvancedFilters : selectedFilters;
                          const selectedCount = Array.isArray(source?.[filter.key]) ? source[filter.key].length : 0;
                          return (
                            <button
                              key={`qa-focus-${filter.key}`}
                              type="button"
                              className={`impqx-ahri-shop-qa-chip ${isActive ? "is-active" : ""}`}
                              onClick={() => {
                                setQaFocusFilterKey(String(filter.key || ""));
                                setQaValueDropdownOpen((prev) => (prev ? prev : true));
                              }}
                            >
                              <span>{filter.label || filter.key}</span>
                              {selectedCount > 0 && <small>{selectedCount}</small>}
                            </button>
                          );
                        })}
                      </div>
                      {(qaAttributeScrollState.canLeft || qaAttributeScrollState.canRight) && (
                        <button
                          type="button"
                          className="impqx-ahri-shop-qa-scroll-btn right"
                          onClick={() => scrollQaRowBy(qaAttributeRowRef, 220)}
                          disabled={!qaAttributeScrollState.canRight}
                          aria-label="Scroll attributes right"
                        >
                          ›
                        </button>
                      )}
                    </div>
                  )}
                  {qaFocusFilter ? (
                    <>
                      <div className="impqx-ahri-shop-qa-prompt">
                        What would you like to select next for <strong>{qaFocusFilter.label || qaFocusFilter.key}</strong>?
                      </div>
                      <div
                        ref={qaValueDropdownRef}
                        className={`impqx-ahri-shop-facet-panel impqx-ahri-shop-facet-dropdown ${isMobileFilterLayout || qaValueDropdownOpen ? "is-open" : ""} ${isMobileFilterLayout ? "impqx-ahri-shop-facet-dropdown-inline" : ""}`}
                      >
                        {isMobileFilterLayout ? (
                          <div className="impqx-ahri-shop-mobile-filter-values-head">
                            <p className="impqx-ahri-shop-select-label">{qaFocusFilter.label || qaFocusFilter.key}</p>
                            <span className="impqx-ahri-shop-facet-count">
                              {qaSelectedValues.length ? `${qaSelectedValues.length} selected` : "Any"}
                            </span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="impqx-ahri-shop-facet-head"
                            onClick={() => setQaValueDropdownOpen((prev) => !prev)}
                          >
                            <p className="impqx-ahri-shop-select-label">{qaFocusFilter.label || qaFocusFilter.key}</p>
                            <div className="impqx-ahri-shop-facet-head-right">
                              <span className="impqx-ahri-shop-facet-count">
                                {qaSelectedValues.length ? `${qaSelectedValues.length} selected` : "Any"}
                              </span>
                              <span className="impqx-ahri-shop-facet-chevron" aria-hidden="true">▾</span>
                            </div>
                          </button>
                        )}
                        {(isMobileFilterLayout || qaValueDropdownOpen) && (
                          <div className={`impqx-ahri-shop-facet-options ${isMobileFilterLayout ? "impqx-ahri-shop-facet-options-inline" : ""}`} role="group" aria-label={qaFocusFilter.label || qaFocusFilter.key}>
                            {qaFocusOptions.length === 0 ? (
                              <div className="impqx-ahri-shop-facet-empty">No values available</div>
                            ) : (
                              qaFocusOptions.map((option) => {
                                const value = String(option.value);
                                const checked = qaSelectedValues.some((entry) => String(entry) === value);
                                const isQaMultiSelect = qaFocusFilter.type === "advanced" || qaFocusFilter.entry?.multi_select === true;
                                return (
                                  <label key={`qa-value-${qaFocusFilter.key}-${value}`} className="impqx-ahri-shop-facet-option">
                                    <input
                                      type={isQaMultiSelect ? "checkbox" : "radio"}
                                      name={isQaMultiSelect ? undefined : `qa-value-${qaFocusFilter.key}`}
                                      checked={checked}
                                      onChange={() => {
                                        if (qaFocusFilter.type === "advanced") {
                                          const nextValues = checked
                                            ? qaSelectedValues.filter((entry) => String(entry) !== value)
                                            : [...qaSelectedValues, value];
                                          onAdvancedFilterChange(qaFocusFilter.key, nextValues);
                                        } else {
                                          toggleFilterValue(qaFocusFilter.key, value);
                                        }
                                        if (!isMobileFilterLayout) {
                                          setQaValueDropdownOpen(false);
                                        }
                                      }}
                                    />
                                    <span className="impqx-ahri-shop-facet-option-label">{decodeHtmlEntities(value)}</span>
                                    {typeof option.count === "number" && (
                                      <span className="impqx-ahri-shop-facet-option-count">{option.count.toLocaleString()}</span>
                                    )}
                                  </label>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="impqx-ahri-shop-facet-empty">No selectable attributes available right now.</div>
                  )}
                </div>
              ) : (
                isMobileFilterLayout ? (
                  <div className="impqx-ahri-shop-mobile-filter-layout">
                    <div className="impqx-ahri-shop-mobile-filter-picker impqx-ahri-shop-mobile-filter-picker--filters">
                      <label className="impqx-ahri-shop-mobile-filter-label" htmlFor="impqx-ahri-filters-field">
                        Filter field
                      </label>
                      <select
                        id="impqx-ahri-filters-field"
                        className="impqx-ahri-shop-input impqx-ahri-shop-mobile-filter-select"
                        value={mobileActiveFilterKeyResolved}
                        onChange={(event) => {
                          setMobileActiveFilterKey(String(event.target.value || ""));
                        }}
                      >
                        {filterPanelEntries.map((entry) => {
                          const source = entry.type === "advanced" ? selectedAdvancedFilters : selectedFilters;
                          const selectedCount = Array.isArray(source?.[entry.key]) ? source[entry.key].length : 0;
                          return (
                            <option key={`mobile-filter-field-${entry.panelKey}`} value={entry.panelKey}>
                              {selectedCount > 0 ? `${entry.label} (${selectedCount})` : entry.label}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {mobileActiveFilterEntry ? (
                      <div className="impqx-ahri-shop-facet-panel impqx-ahri-shop-facet-dropdown impqx-ahri-shop-facet-dropdown-inline is-open">
                        <div className="impqx-ahri-shop-mobile-filter-values-head">
                          <p className="impqx-ahri-shop-select-label">{mobileActiveFilterEntry.label}</p>
                          <span className="impqx-ahri-shop-facet-count">
                            {mobileActiveSelectedValues.length ? `${mobileActiveSelectedValues.length} selected` : "Any"}
                          </span>
                        </div>
                        <div className="impqx-ahri-shop-facet-options impqx-ahri-shop-facet-options-inline" role="group" aria-label={mobileActiveFilterEntry.label}>
                          {mobileActiveFilterOptions.length === 0 ? (
                            <div className="impqx-ahri-shop-facet-empty">No values available</div>
                          ) : (
                            mobileActiveFilterOptions.map((option) => {
                              const value = String(option.value);
                              const checked = mobileActiveSelectedValues.some((entry) => String(entry) === value);
                              const isMobileMultiSelect = mobileActiveFilterEntry.type === "advanced" || mobileActiveFilterEntry.entry?.multi_select === true;
                              return (
                                <label key={`${mobileActiveFilterEntry.panelKey}-${value}`} className="impqx-ahri-shop-facet-option">
                                  <input
                                    type={isMobileMultiSelect ? "checkbox" : "radio"}
                                    name={isMobileMultiSelect ? undefined : mobileActiveFilterEntry.panelKey}
                                    checked={checked}
                                    onChange={() => {
                                      if (mobileActiveFilterEntry.type === "advanced") {
                                        const nextValues = checked
                                          ? mobileActiveSelectedValues.filter((entry) => String(entry) !== value)
                                          : [...mobileActiveSelectedValues, value];
                                        onAdvancedFilterChange(mobileActiveFilterEntry.key, nextValues);
                                      } else {
                                        toggleFilterValue(mobileActiveFilterEntry.key, value);
                                      }
                                    }}
                                  />
                                  <span className="impqx-ahri-shop-facet-option-label">{decodeHtmlEntities(value)}</span>
                                  {typeof option.count === "number" && (
                                    <span className="impqx-ahri-shop-facet-option-count">{option.count.toLocaleString()}</span>
                                  )}
                                </label>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="impqx-ahri-shop-facet-empty">No selectable attributes available right now.</div>
                    )}
                  </div>
                ) : (
                  <div className="impqx-ahri-shop-filter-wrap row g-3" ref={filterWrapRef}>
                    {orderedFilterConfig.map((filterItem) => {
                      const filter = filterItem.entry || {};
                      const options = filterItem.type === "advanced"
                        ? (filter.values || []).map((entry) => ({
                            value: entry.value || entry.label,
                            count: parseOptionalCount(entry?.count),
                          }))
                        : facetData?.[filter.key] || [];
                      const selected = filterItem.type === "advanced"
                        ? selectedAdvancedFilters?.[filter.key] || []
                        : selectedFilters?.[filter.key] || [];
                      const panelKey = buildFilterPanelKey(filterItem.type, filterItem.key);
                      const isOpen = openFilterByKey[panelKey] === true;
                      return (
                        <div key={panelKey} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                          <div className={`impqx-ahri-shop-facet-panel impqx-ahri-shop-facet-dropdown ${isOpen ? "is-open" : ""}`}>
                            <button
                              type="button"
                              className="impqx-ahri-shop-facet-head"
                              onClick={() => {
                                setOpenFilterByKey((prev) => (prev[panelKey] ? {} : { [panelKey]: true }));
                              }}
                            >
                              <p className="impqx-ahri-shop-select-label">{filter.label || filter.key}</p>
                              <div className="impqx-ahri-shop-facet-head-right">
                                <span className="impqx-ahri-shop-facet-count">{selected.length ? `${selected.length} selected` : "Any"}</span>
                                <span className="impqx-ahri-shop-facet-chevron" aria-hidden="true">▾</span>
                              </div>
                            </button>
                            {isOpen && (
                              <div className="impqx-ahri-shop-facet-options" role="group" aria-label={filter.label || filter.key}>
                                {options.length === 0 ? (
                                  <div className="impqx-ahri-shop-facet-empty">No values available</div>
                                ) : (
                                  options.map((option) => {
                                    const value = String(option.value);
                                    const checked = selected.some((entry) => String(entry) === value);
                                    const isMultiSelect = filterItem.type === "advanced" || filter.multi_select === true;
                                    return (
                                      <label key={`${panelKey}-${value}`} className="impqx-ahri-shop-facet-option">
                                        <input
                                          type={isMultiSelect ? "checkbox" : "radio"}
                                          name={isMultiSelect ? undefined : panelKey}
                                          checked={checked}
                                          onChange={() => {
                                            if (filterItem.type === "advanced") {
                                              const nextValues = checked
                                                ? selected.filter((entry) => String(entry) !== value)
                                                : [...selected, value];
                                              onAdvancedFilterChange(filter.key, nextValues);
                                            } else {
                                              toggleFilterValue(filter.key, value);
                                            }
                                            if (!isMultiSelect) {
                                              setOpenFilterByKey({});
                                            }
                                          }}
                                        />
                                        <span className="impqx-ahri-shop-facet-option-label">{decodeHtmlEntities(value)}</span>
                                        {typeof option.count === "number" && (
                                          <span className="impqx-ahri-shop-facet-option-count">{option.count.toLocaleString()}</span>
                                        )}
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </div>
            <div className="col-12 col-xl-4">
              <div className="impqx-ahri-shop-build-summary impqx-ahri-shop-build-summary-inline">
                <div className="impqx-ahri-shop-build-summary-head">
                  <strong>System Build Summary</strong>
                  {buildSummary.productValues.length > 0 ? (
                    <span className="impqx-ahri-shop-build-summary-badge">Product filtered</span>
                  ) : (
                    <span className="impqx-ahri-shop-build-summary-badge muted">No product filter</span>
                  )}
                </div>
                {!buildSummary.hasSelections ? (
                  <div className="impqx-ahri-shop-build-summary-empty">
                    No choices selected yet. Start choosing attributes to build your system.
                  </div>
                ) : (
                  <div className="impqx-ahri-shop-build-summary-grid">
                    {buildSummary.sections.map((section) => (
                      <div key={`build-summary-${section.key}`} className="impqx-ahri-shop-build-summary-row">
                        <span className="impqx-ahri-shop-build-summary-label">{section.label}</span>
                        <span className="impqx-ahri-shop-build-summary-value">{section.values.join(", ")}</span>
                        <button
                          type="button"
                          className="impqx-ahri-shop-build-summary-remove"
                          onClick={() => clearSummaryFilter(section.key, section.type)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    {buildSummary.productValues.length > 0 && (
                      <div className="impqx-ahri-shop-build-summary-row">
                        <span className="impqx-ahri-shop-build-summary-label">Product filter</span>
                        <span className="impqx-ahri-shop-build-summary-value">
                          {buildSummary.productValues.join(", ")}
                        </span>
                        {hasRemovableProductCodeFilters && (
                          <button
                            type="button"
                            className="impqx-ahri-shop-build-summary-remove"
                            onClick={clearSummaryProductFilter}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {(advancedSearchEnabled || activeResultsTab === "ahri") && (
          <div className="impqx-ahri-shop-results-toolbar">
            {advancedSearchEnabled && (
              <div className="impqx-ahri-shop-results-tabs" role="tablist" aria-label="Search result tabs">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeResultsTab === "ahri"}
                  className={`impqx-ahri-shop-results-tab${activeResultsTab === "ahri" ? " is-active" : ""}`}
                  onClick={() => setActiveResultsTab("ahri")}
                >
                  AHRI Results
                  <span className="impqx-ahri-shop-results-tab-count">
                    {loading ? "…" : Number(totalFound || 0).toLocaleString()}
                  </span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeResultsTab === "products"}
                  className={`impqx-ahri-shop-results-tab${activeResultsTab === "products" ? " is-active" : ""}`}
                  onClick={() => setActiveResultsTab("products")}
                >
                  Products
                  <span className="impqx-ahri-shop-results-tab-count">
                    {productSearchLoading ? "…" : Number(productSearchTotal || 0).toLocaleString()}
                  </span>
                </button>
              </div>
            )}

            {activeResultsTab === "ahri" && (
              <div className="impqx-ahri-shop-results-toolbar-right">
                <div className="impqx-ahri-shop-meta">
                  <span>
                    {loading ? "Loading systems..." : `Showing ${systems.length} of ${Number(totalFound || 0).toLocaleString()} systems`}
                  </span>
                  {facetsLoading && <span>Refreshing filters...</span>}
                </div>
                {totalPages > 1 && (
                  <div className="impqx-ahri-shop-pagination impqx-ahri-shop-pagination--inline">
                    <button
                      type="button"
                      className="impqx-ahri-shop-button"
                      disabled={loading || page <= 1}
                      onClick={() => void fetchResults(page - 1, query, selectedFilters, productCodeFilters, selectedAdvancedFilters)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      className="impqx-ahri-shop-button"
                      disabled={loading || page >= totalPages}
                      onClick={() => void fetchResults(page + 1, query, selectedFilters, productCodeFilters, selectedAdvancedFilters)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {activeResultsTab === "ahri" && otherWarehouseSearchFallback && selectedWarehouseCode && (
          <div className="impqx-ahri-shop-banner info" role="status">
            No systems available at warehouse {selectedWarehouseCode} for this lookup.
            Showing matches available at other warehouses.
          </div>
        )}
        {activeResultsTab === "ahri" && selectedSystemKeys.length > 0 && (
          <div className="impqx-ahri-shop-comparebar">
            <div className="impqx-ahri-shop-comparebar-left">
              <strong>{selectedSystemKeys.length} selected</strong>
              <span className="impqx-ahri-shop-subtitle">
                Use compare to view selected systems side-by-side (up to {MAX_COMPARE_SELECTIONS}).
              </span>
            </div>
            <div className="impqx-ahri-shop-comparebar-actions">
              <button
                type="button"
                className="impqx-ahri-shop-button primary"
                onClick={compareSelectedSystems}
                disabled={selectedSystems.length < 2}
              >
                Compare Selected
              </button>
              <button
                type="button"
                className="impqx-ahri-shop-button"
                onClick={clearSelectedSystems}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {activeResultsTab === "ahri" && <div className="impqx-ahri-shop-results impqx-ahri-shop-system-grid">
          {(loading || !bootstrapped) && systems.length === 0 && (
            <div className="impqx-ahri-shop-system-grid-full">
              <div className="impqx-ahri-shop-card impqx-ahri-shop-loading-card">
                <div className="impqx-ahri-shop-loading-animation" aria-hidden="true">
                  <div className="impqx-ahri-shop-penguin-track">
                    <div className="impqx-ahri-shop-penguin-walk">
                      <div className="impqx-ahri-shop-penguin" />
                    </div>
                  </div>
                </div>
                <strong>Loading systems...</strong>
                <div className="impqx-ahri-shop-subtitle">Please wait while we fetch your AHRI results.</div>
              </div>
            </div>
          )}
          {bootstrapped && hasApiAuth && !loading && systems.length === 0 && (
            <div className="impqx-ahri-shop-system-grid-full">
              <div className="impqx-ahri-shop-card impqx-ahri-shop-empty-card">
                {hasRemovableFilters && !clearFiltersPromptDismissed ? (
                  <>
                    <strong>No systems match your search and filters.</strong>
                    <div className="impqx-ahri-shop-subtitle">
                      Your active filters may be hiding matching systems. Want to clear the
                      filters and run this search again?
                    </div>
                    <div className="impqx-ahri-shop-empty-actions">
                      <button
                        type="button"
                        className="impqx-ahri-shop-button primary"
                        onClick={clearAllFilters}
                      >
                        Yes, clear filters
                      </button>
                      <button
                        type="button"
                        className="impqx-ahri-shop-button"
                        onClick={() => setClearFiltersPromptDismissed(true)}
                      >
                        No, keep filters
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <strong>No systems found.</strong>
                    <div className="impqx-ahri-shop-subtitle">
                      {hasRemovableFilters
                        ? "Try removing some filters or entering another search query."
                        : "Try entering another search query."}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {systems.map((system, index) => {
            const systemSelectionKey = String(system?.id || "").trim() || `system-${index}`;
            const systemCartId = String(system?.id || "").trim() || systemSelectionKey;
            const isSystemSelected = selectedSystemKeys.includes(systemSelectionKey);
            const isCompareLimitReached = selectedSystemKeys.length >= MAX_COMPARE_SELECTIONS && !isSystemSelected;
            // Resolve the displayed product per slot: an explicit card-level
            // selection wins; otherwise default inventory-first (first in-stock
            // alternative at the selected warehouse), falling back to the
            // mapped primary. Keeps the card consistent with the detail drawer.
            const systemId = String(system?.id || "").trim();
            const pickSlotCode = (slotType, fallback) =>
              resolveDisplayedSlotCode(
                componentSelections,
                systemId,
                slotType,
                fallback,
                system?.componentOptions,
                productDetails,
                selectedWarehouseCode
              );
            // When a product filter is active and one of a slot's alternatives
            // matches it, the slot collapses to only the filtered product (see
            // renderSystemSlot). Surface that same product as the slot's primary
            // so the displayed unit, pricing, and system total stay consistent.
            const slotFilteredCodes = (slotType) => {
              if (!productCodeFilters.length) return [];
              const allCodes = system?.componentOptions?.[slotType]?.codes || [];
              return allCodes.filter((candidate) =>
                productCodeFilters.some(
                  (entry) => String(entry).trim() === String(candidate).trim()
                )
              );
            };
            const applySlotProductFilter = (slotType, resolvedCode) => {
              const c = String(resolvedCode || "").trim();
              const filtered = slotFilteredCodes(slotType);
              if (!filtered.length) return c;
              if (filtered.some((fc) => String(fc).trim() === c)) return c;
              return String(filtered[0]).trim();
            };
            const outdoorCode = applySlotProductFilter("outdoor", pickSlotCode("outdoor", system.outdoorProductCode));
            const indoorCode = applySlotProductFilter("indoor", pickSlotCode("indoor", system.indoorProductCode));
            const furnaceCode = applySlotProductFilter("furnace", pickSlotCode("furnace", system.furnaceProductCode));
            // "Completely available" badge (AD-4): derived entirely from the
            // search response's own combination data (complete_at_{wh}/
            // qty_{slot}_at_{wh}, precomputed at variant-compute time), so it
            // renders immediately with the card -- unlike the per-slot dots
            // below, it never waits on the separate fetchProductDetails call.
            // Prefer the combination matching the resolved slot codes so it
            // tracks the shopper's picker selection; fall back to the
            // primary combination for the common single-combo case.
            const matchedCombination = (system.combinations || []).find((combo) =>
              combo.indoorProductCode === indoorCode
              && combo.outdoorProductCode === outdoorCode
              && combo.furnaceProductCode === furnaceCode
            ) || system.combinations?.[0] || null;
            const combinationAvailability = matchedCombination
              ? getVariantCombinationAvailability(matchedCombination, selectedWarehouseCode)
              : null;
            // Brand summary line lists every distinct brand across that
            // slot's eligible options (deduped), joined by "/" so a shopper
            // sees up front that e.g. the indoor unit comes in two brands
            // before opening the picker -- for variant-mode systems
            // codeToBrand maps each slot's candidate codes to its brand.
            const brandsForSlot = (slotType, fallbackBrand, resolvedCode) => {
              const codes = system?.componentOptions?.[slotType]?.codes || [];
              const seen = new Set();
              const out = [];
              codes.forEach((code) => {
                const decoded = decodeHtmlEntities(system?.codeToBrand?.[String(code || "").trim()]) || "";
                if (decoded && !seen.has(decoded)) {
                  seen.add(decoded);
                  out.push(decoded);
                }
              });
              if (out.length) return out.join(" / ");
              return (
                decodeHtmlEntities(system?.codeToBrand?.[String(resolvedCode || "").trim()]) ||
                decodeHtmlEntities(fallbackBrand) ||
                "-"
              );
            };
            const outdoorBrandsSummary = brandsForSlot("outdoor", system.outdoorBrand, outdoorCode);
            const indoorBrandsSummary = brandsForSlot("indoor", system.indoorBrand, indoorCode);

            const outdoorDetail = outdoorCode ? lookupProductDetail(outdoorCode, productDetails) : null;
            const indoorDetail = indoorCode ? lookupProductDetail(indoorCode, productDetails) : null;
            const furnaceDetail = furnaceCode ? lookupProductDetail(furnaceCode, productDetails) : null;

            const outdoorPricing = resolvePricingForCodeLayers(outdoorCode, outdoorDetail, priceLookupByCode);
            const indoorPricing = resolvePricingForCodeLayers(indoorCode, indoorDetail, priceLookupByCode);
            const furnacePricing = resolvePricingForCodeLayers(furnaceCode, furnaceDetail, priceLookupByCode);

            const priceRows = [outdoorPricing, indoorPricing, furnacePricing].filter((entry) => pricingRowIsDisplayable(entry));
            const systemTotalList = priceRows.reduce((sum, entry) => sum + (Number.isFinite(entry.listPrice) ? entry.listPrice : 0), 0);
            const systemTotalYour = priceRows.reduce((sum, entry) => sum + (Number.isFinite(entry.yourPrice) ? entry.yourPrice : 0), 0);
            const hasSystemTotal = priceRows.length > 0;
            const systemGate = getSystemPriceGateForAddToCart(
              system,
              productDetails,
              priceLookupByCode,
              isGetPricePendingForCode
            );
            const systemAddDisabled = systemGate.pending || !systemGate.hasDisplayablePrice;
            const systemAddTitle = systemAddToCartBlockedTitle(systemGate);

            const linePriceMarkup = (code, pricing) => {
              const c = String(code || "").trim();
              if (!c) return null;
              if (isGetPricePendingForCode(c)) {
                return (
                  <span className="impqx-ahri-shop-subtitle" title="Loading prices">
                  Loading…
                  </span>
                );
              }
              if (mustLoginBeforeGetprice()) {
                return <span className="impqx-ahri-shop-subtitle">Login for Price</span>;
              }
              if (!pricingRowIsDisplayable(pricing)) {
                return <span className="impqx-ahri-shop-subtitle">Call for price</span>;
              }
              return (
                <span className="impqx-ahri-shop-price-pair">
                  {shouldShowListPrice(pricing.listPrice, pricing.yourPrice) && (
                    <span className="impqx-ahri-shop-price-list">{formatUsd(pricing.listPrice)}</span>
                  )}
                  <span className="impqx-ahri-shop-price-your">{formatUsd(pricing.yourPrice)}</span>
                </span>
              );
            };
            const componentCodes = [
              String(outdoorCode || "").trim(),
              String(indoorCode || "").trim(),
              String(furnaceCode || "").trim(),
            ].filter(Boolean);

            // Render one system component row plus, when the slot has multiple
            // approved alternatives in stock, an inline picker strip so the
            // shopper sees and can switch options directly from the list.
            const renderSystemSlot = (slotType, label, code, pricing) => {
              const c = String(code || "").trim();
              if (!c) return null;
              const liveDetail = lookupProductDetail(c, productDetails);
              // Fall back to the combo's own embedded name/image/description
              // (already on the search response, see parseVariantCombination)
              // while fetchProductDetails is still loading -- renders
              // immediately instead of a blank/placeholder card.
              const embeddedDetail = matchedCombination
                ? getEmbeddedSlotProductInfo(matchedCombination, slotType)
                : null;
              const detail = (liveDetail?.name || liveDetail?.image_url)
                ? liveDetail
                : embeddedDetail || liveDetail;
              const dotClass = getAvailabilityDotClass(liveDetail, selectedWarehouseCode, {
                pending: isProductDetailPending(c),
              });
              const isLocked = lockedProductCodeSet.has(c);
              const allCodes = system?.componentOptions?.[slotType]?.codes || [];
              // When a product filter is applied and one or more of this slot's
              // options match it, collapse the slot to just the filtered product(s).
              const filteredSlotCodes = slotFilteredCodes(slotType);
              // With a branch selected, show only the options in stock at that
              // branch (hide unavailable alternatives) to honor the "fully
              // available at this branch" settings. When nothing in this slot is
              // in stock — e.g. the system was surfaced from other locations
              // because nothing matched at the branch — the full option list is
              // shown so the shopper can still see and shop the system.
              const visibleCodes = resolveVisibleSlotCodes(allCodes, {
                selectedWarehouse: selectedWarehouseCode,
                isInStock: isCodeInStock,
                filteredCodes: filteredSlotCodes,
              });
              const hasChoices = visibleCodes.length > 1;
              return (
                <Fragment key={`slot-${slotType}`}>
                  <div
                    className="impqx-ahri-shop-product-row impqx-ahri-shop-system-product-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => openProductDetail(c, slotType, system)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openProductDetail(c, slotType, system);
                      }
                    }}
                  >
                    <img
                      src={resolveAssetUrl(detail?.image_url, endpoint) || PRODUCT_IMAGE_PLACEHOLDER}
                      alt={decodeHtmlEntities(detail?.name) || c}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = PRODUCT_IMAGE_PLACEHOLDER;
                      }}
                    />
                    <span className="impqx-ahri-shop-system-product-copy">
                      <strong>
                        <span className={`impqx-ahri-shop-availability-dot ${dotClass}`} aria-hidden="true" />
                        {label}
                        {hasChoices && (
                          <span
                            className="impqx-ahri-shop-badge impqx-ahri-shop-slot-options-count-badge"
                            title={`${visibleCodes.length} ${label.toLowerCase()} options available`}
                          >
                            {visibleCodes.length} options
                          </span>
                        )}
                      </strong>
                      {/* When this slot has multiple eligible options, the
                          name/code/category/status below are shown once per
                          option in the picker list right underneath -- not
                          duplicated here for one arbitrarily-resolved code. */}
                      {!hasChoices && (
                        <>
                          <span className="impqx-ahri-shop-system-product-name">
                            {decodeHtmlEntities(detail?.name) || c}
                          </span>
                          <small>
                            {detail?.manufacturer_part_number || c}
                            {detail?.category ? ` • ${detail.category}` : ""}
                          </small>
                          {(() => {
                            const slotStatus = getSlotAvailabilityStatus(
                              dotClass,
                              selectedWarehouseCode,
                              selectedWarehouseLabel
                            );
                            if (!slotStatus) return null;
                            const slotQty = combinationAvailability?.qty?.[slotType];
                            const statusText = slotStatus.tone === "available" && slotQty != null
                              ? `${slotQty} ${slotStatus.text}`
                              : slotStatus.text;
                            return (
                              <span
                                className={`impqx-ahri-shop-availability-status impqx-ahri-shop-availability-status--${slotStatus.tone} impqx-ahri-shop-system-product-status`}
                              >
                                {statusText}
                              </span>
                            );
                          })()}
                        </>
                      )}
                    </span>
                    <span className="impqx-ahri-shop-system-product-price-col">
                      <button
                        type="button"
                        className={`impqx-ahri-shop-product-filter-btn ${productCodeFilters.some((entry) => String(entry) === c) ? "is-active" : ""}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          applyProductCodeFilter(c);
                        }}
                        disabled={isLocked}
                        title={isLocked ? "Product filter locked from widget property" : "Filter systems by this product"}
                        aria-label={isLocked ? "Product filter locked from widget property" : "Filter systems by this product"}
                      >
                        <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                          <path d="M3 5h18l-7 8v6l-4-2v-4z" />
                        </svg>
                      </button>
                      {linePriceMarkup(c, pricing)}
                    </span>
                  </div>
                  {hasChoices && (() => {
                    // Surface what actually distinguishes look-alike options by
                    // diffing their attributes (names are frequently identical).
                    const optionDiffs = computeOptionDifferentiators(
                      visibleCodes.map((vc) => ({
                        code: vc,
                        attributes: productDetails[vc]?.attributes,
                        description: decodeHtmlEntities(productDetails[vc]?.description),
                        category: productDetails[vc]?.category,
                      })),
                      { maxKeys: 3 }
                    );
                    const diffKeyLabels = (optionDiffs.labelsByCode[visibleCodes[0]] || []).map(
                      (entry) => entry.keyLabel
                    );
                    return (
                    <div className="impqx-ahri-shop-slot-options" role="group" aria-label={`${label} options`}>
                      <span className="impqx-ahri-shop-slot-options-label">
                        {visibleCodes.length} {label.toLowerCase()} options
                        {diffKeyLabels.length
                          ? ` — differ by ${diffKeyLabels.join(", ")}`
                          : " — tap to view details"}
                      </span>
                      <div className="impqx-ahri-shop-slot-options-list">
                        {visibleCodes.map((optCode) => {
                          const optDetail = lookupProductDetail(optCode, productDetails);
                          const optDot = getAvailabilityDotClass(optDetail, selectedWarehouseCode, {
                            pending: isProductDetailPending(optCode),
                          });
                          const optName = decodeHtmlEntities(optDetail?.name) || optCode;
                          const isSel = optCode === c;
                          const isOptFilterActive = productCodeFilters.some((entry) => String(entry) === optCode);
                          const isOptFilterLocked = lockedProductCodeSet.has(optCode);
                          const diffLabels = optionDiffs.labelsByCode[optCode] || [];
                          const fallbackDesc =
                            !diffLabels.length && optionDiffs.useDescriptionFallback
                              ? optionDiffs.descriptionByCode[optCode] || ""
                              : "";
                          return (
                            <div
                              key={`chip-${slotType}-${optCode}`}
                              className={`impqx-ahri-shop-slot-option-row ${isSel ? "is-selected" : ""}`}
                            >
                              <div
                                className="impqx-ahri-shop-slot-option-chip"
                                role="button"
                                tabIndex={0}
                                title="View product details"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openProductDetail(optCode, slotType, system);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    openProductDetail(optCode, slotType, system);
                                  }
                                }}
                              >
                                <span className={`impqx-ahri-shop-availability-dot ${optDot}`} aria-hidden="true" />
                                <span className="impqx-ahri-shop-slot-option-text">
                                  <span className="impqx-ahri-shop-slot-option-name">{optName}</span>
                                  {optName !== optCode && (
                                    <span className="impqx-ahri-shop-slot-option-code">{optCode}</span>
                                  )}
                                  {(() => {
                                    const optStatus = getSlotAvailabilityStatus(
                                      optDot,
                                      selectedWarehouseCode,
                                      selectedWarehouseLabel
                                    );
                                    if (!optStatus) return null;
                                    return (
                                      <span
                                        className={`impqx-ahri-shop-availability-status impqx-ahri-shop-availability-status--${optStatus.tone} impqx-ahri-shop-slot-option-status`}
                                      >
                                        {optStatus.text}
                                      </span>
                                    );
                                  })()}
                                  {diffLabels.length > 0 && (
                                    <span className="impqx-ahri-shop-slot-option-diffs">
                                      {diffLabels.map((d) => (
                                        <span key={d.key} className="impqx-ahri-shop-slot-option-diff">
                                          <span className="impqx-ahri-shop-slot-option-diff-key">{d.keyLabel}</span>
                                          <span className="impqx-ahri-shop-slot-option-diff-val">{d.value}</span>
                                        </span>
                                      ))}
                                    </span>
                                  )}
                                  {fallbackDesc && (
                                    <span
                                      className="impqx-ahri-shop-slot-option-desc"
                                      dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(fallbackDesc) }}
                                    />
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={`impqx-ahri-shop-product-filter-btn impqx-ahri-shop-slot-option-filter-btn ${isOptFilterActive ? "is-active" : ""}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  applyProductCodeFilter(optCode);
                                }}
                                disabled={isOptFilterLocked}
                                title={
                                  isOptFilterLocked
                                    ? "Product filter locked from widget property"
                                    : isOptFilterActive
                                      ? "Remove filter for this option"
                                      : "Filter systems by this option"
                                }
                                aria-label={
                                  isOptFilterLocked
                                    ? "Product filter locked from widget property"
                                    : isOptFilterActive
                                      ? "Remove filter for this option"
                                      : "Filter systems by this option"
                                }
                              >
                                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                  <path d="M3 5h18l-7 8v6l-4-2v-4z" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}
                </Fragment>
              );
            };
            const componentOtherWarehouseSets = componentCodes
              .map((code) =>
                new Set(getProductAvailability(lookupProductDetail(code, productDetails), selectedWarehouseCode).otherWarehouseCodes)
              )
              .filter((set) => set.size > 0);
            let commonOtherWarehouseCodes = [];
            if (componentOtherWarehouseSets.length && componentOtherWarehouseSets.length === componentCodes.length) {
              const intersection = new Set(componentOtherWarehouseSets[0]);
              componentOtherWarehouseSets.slice(1).forEach((set) => {
                Array.from(intersection).forEach((value) => {
                  if (!set.has(value)) intersection.delete(value);
                });
              });
              commonOtherWarehouseCodes = Array.from(intersection);
            }
            const productOtherWarehouseCodes = [
              ...new Set(
                componentCodes.flatMap((code) =>
                  getProductAvailability(lookupProductDetail(code, productDetails), selectedWarehouseCode).otherWarehouseCodes
                )
              ),
            ].sort();
            const systemDocOtherWarehouseCodes = getOtherWarehouseCodesFromDoc(system.rawDoc, selectedWarehouseCode);
            const displayOtherWarehouseCodes = commonOtherWarehouseCodes.length
              ? commonOtherWarehouseCodes
              : productOtherWarehouseCodes.length
                ? productOtherWarehouseCodes
                : systemDocOtherWarehouseCodes;
            const showOtherWarehouseNotice =
              !!selectedWarehouseCode
              && displayOtherWarehouseCodes.length > 0
              && (
                otherWarehouseSearchFallback
                || !system.allComponentsAvailable
                || system.warehouseAvailabilityScoreValue <= 0
                || !system.hasAnyComponentAvailable
              );
            const systemRebateContainerId = getEcoRebatesContainerId(system.id, "card");

            return (
            <div key={`${system.id || "system"}-${index}`} className="impqx-ahri-shop-system-grid-item">
              <article className={`impqx-ahri-shop-card impqx-ahri-shop-system-card ${isSystemSelected ? "is-selected" : ""} ${system.showFeaturedTag ? "impqx-ahri-shop-ribbon-host" : ""}`}>
                {system.showFeaturedTag && (() => {
                  const names = (system.featuredGroupNamesForWarehouse?.length
                    ? system.featuredGroupNamesForWarehouse
                    : system.featuredGroupName ? [system.featuredGroupName] : []);
                  const namesText = names.join(", ");
                  return (
                    <span
                      className="impqx-ahri-shop-ribbon-flag"
                      title={namesText ? `Featured group${names.length > 1 ? "s" : ""}: ${namesText}` : "Featured system"}
                    >
                      <svg className="impqx-ahri-shop-ribbon-flag-icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M8 0.5c.3 2.1 1 3.6 2 4.6 1 1 2.5 1.7 4.6 2-2.1.3-3.6 1-4.6 2-1 1-1.7 2.5-2 4.6-.3-2.1-1-3.6-2-4.6-1-1-2.5-1.7-4.6-2 2.1-.3 3.6-1 4.6-2 1-1 1.7-2.5 2-4.6z" />
                      </svg>
                      <span className="impqx-ahri-shop-ribbon-flag-text">{namesText || "Featured"}</span>
                    </span>
                  );
                })()}
                <div className="impqx-ahri-shop-system-head">
                  <div className="impqx-ahri-shop-system-head-left">
                    <strong className="impqx-ahri-shop-system-id">AHRI #{renderAhriIdText(system.id, system)}</strong>
                  </div>
                  <div className="impqx-ahri-shop-system-head-right">
                    <label className="impqx-ahri-shop-compare-toggle">
                      <input
                        type="checkbox"
                        checked={isSystemSelected}
                        disabled={isCompareLimitReached}
                        onChange={() => toggleSystemSelection(systemSelectionKey, system)}
                      />
                      <span>Select</span>
                    </label>
                    <span
                      className={`impqx-ahri-shop-match-indicator ${system.completeMatch ? "complete" : system.partialMatch ? "partial" : "unmatched"}`}
                      aria-label={system.completeMatch ? "Complete match" : system.partialMatch ? "Partial match" : "No product match"}
                      title={system.completeMatch ? "Complete match" : system.partialMatch ? "Partial match" : "No product match"}
                    >
                      {system.completeMatch ? "✓" : system.partialMatch ? "•" : "×"}
                    </span>
                    {showScore && (
                      <span
                        className={`impqx-ahri-shop-score-chip${showScoreDebug ? " impqx-ahri-shop-score-chip--clickable" : ""}`}
                        title={showScoreDebug ? "Click to inspect score breakdown" : `Ranking score: ${formatScore(system.sortScore)}`}
                        role={showScoreDebug ? "button" : undefined}
                        tabIndex={showScoreDebug ? 0 : undefined}
                        onClick={showScoreDebug ? () => setScoreDebugSystemId((prev) => prev === systemSelectionKey ? null : systemSelectionKey) : undefined}
                        onKeyDown={showScoreDebug ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setScoreDebugSystemId((prev) => prev === systemSelectionKey ? null : systemSelectionKey); } } : undefined}
                      >
                        Score: {formatScore(system.sortScore)}
                      </span>
                    )}
                  </div>
                </div>

                {showScore && showScoreDebug && scoreDebugSystemId === systemSelectionKey && (
                  <div className="impqx-ahri-shop-score-debug">
                    <div className="impqx-ahri-shop-score-debug-title">Score Breakdown</div>
                    <table className="impqx-ahri-shop-score-debug-table">
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Value</th>
                          <th>Bonus</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Featured</td>
                          <td>{system.featuredForWarehouse ? "Yes" : "No"}</td>
                          <td>+{formatScore(system.featuredScoreBonus)}</td>
                        </tr>
                        <tr>
                          <td>Complete Match</td>
                          <td>{system.completeMatch ? "Yes" : "No"}</td>
                          <td>+{formatScore(system.completeMatchScoreBonus)}</td>
                        </tr>
                        <tr>
                          <td>Warehouse Tag Match</td>
                          <td>{system.warehouseTagMatched ? "Yes" : "No"}</td>
                          <td>+{formatScore(system.warehouseTagScoreBonus)}</td>
                        </tr>
                        <tr>
                          <td>Warehouse Availability</td>
                          <td>{formatScore(system.warehouseAvailabilityScoreValue)}</td>
                          <td>+{formatScore(system.warehouseAvailabilityScoreValue)}</td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="2"><strong>Total Score</strong></td>
                          <td><strong>{formatScore(system.sortScore)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                    <div className="impqx-ahri-shop-score-debug-formula">
                      <code>score = featured({formatScore(system.featuredScoreBonus)}) + completeMatch({formatScore(system.completeMatchScoreBonus)}) + warehouseTag({formatScore(system.warehouseTagScoreBonus)}) + availability({formatScore(system.warehouseAvailabilityScoreValue)}) = {formatScore(system.sortScore)}</code>
                    </div>
                  </div>
                )}

                {/* Brand summary reflects whichever code is currently
                    selected per slot -- the actual per-slot picker (when a
                    slot has more than one eligible option) renders inside
                    that slot's own block below, via renderSystemSlot's
                    existing componentOptions-driven chip picker. */}
                {showBrandRow && (outdoorCode || indoorCode) && (
                  <div className="impqx-ahri-shop-system-brands">
                    <div className="impqx-ahri-shop-system-brands-row">
                      {outdoorCode && (
                        <div className="impqx-ahri-shop-system-brand-item">
                          <span className="impqx-ahri-shop-system-label">Outdoor:</span> {outdoorBrandsSummary}
                        </div>
                      )}
                      {indoorCode && (
                        <div className="impqx-ahri-shop-system-brand-item">
                          <span className="impqx-ahri-shop-system-label">Indoor:</span> {indoorBrandsSummary}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {system.id && bootstrapped && ecoRebatesConfig.enabled && (
                  <div
                    className={`impqx-ahri-shop-rebate-widget-wrap ${ecoRebateVisibleByContainerId[systemRebateContainerId] ? "is-visible" : "is-hidden"}`}
                    role="button"
                    tabIndex={0}
                    title="Click to view more rebate details"
                    onClickCapture={(event) => {
                      activateRebateDetails(systemRebateContainerId, event);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        activateRebateDetails(systemRebateContainerId, event);
                      }
                    }}
                  >
                    <span className="impqx-ahri-shop-rebate-icon" aria-hidden="true">
                      <i className="fa-solid fa-bolt" />
                    </span>
                    <div id={systemRebateContainerId} className="impqx-ahri-shop-rebate-widget" />
                    <span
                      className="impqx-ahri-shop-rebate-more-btn"
                      title="Click to view more rebate details"
                      aria-label="Click to view more rebate details"
                    >
                      More info
                    </span>
                  </div>
                )}
                {showOtherWarehouseNotice && (() => {
                  const altCount = displayOtherWarehouseCodes.length;
                  const altPanelId = `impqx-ahri-alt-wh-${systemSelectionKey}`;
                  const isAltExpanded = !!expandedAltWarehouseKeys[systemSelectionKey];
                  const locationWord = altCount === 1 ? "location" : "locations";
                  const summaryText =
                    otherWarehouseSearchFallback || !system.hasAnyComponentAvailable
                      ? `This system is stocked at ${altCount} other ${locationWord}`
                      : `Entire system is available at ${altCount} other ${locationWord}`;
                  return (
                  <div className={`impqx-ahri-shop-system-alt-warehouse ${isAltExpanded ? "is-expanded" : ""}`}>
                    <button
                      type="button"
                      className="impqx-ahri-shop-system-alt-warehouse-toggle"
                      aria-expanded={isAltExpanded}
                      aria-controls={altPanelId}
                      onClick={(event) => {
                        event.stopPropagation();
                        setExpandedAltWarehouseKeys((prev) => ({
                          ...prev,
                          [systemSelectionKey]: !prev[systemSelectionKey],
                        }));
                      }}
                    >
                      <span className="impqx-ahri-shop-system-alt-warehouse-pin" aria-hidden="true">
                        <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                          <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
                        </svg>
                      </span>
                      <span className="impqx-ahri-shop-system-alt-warehouse-summary">
                        {summaryText}
                        <span className="impqx-ahri-shop-system-alt-warehouse-cta">
                          {isAltExpanded ? "Hide locations" : "Show locations"}
                        </span>
                      </span>
                      <span
                        className={`impqx-ahri-shop-system-alt-warehouse-chevron ${isAltExpanded ? "is-open" : ""}`}
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {isAltExpanded && (
                      <div id={altPanelId} className="impqx-ahri-shop-system-alt-warehouse-chips">
                        {displayOtherWarehouseCodes.map((altCode) => {
                          const altDetail = findWarehouseDetailByCode(warehouseOptions, altCode);
                          const altName = altDetail ? formatWarehouseOptionLabel(altDetail) || altDetail.branchName : "";
                          const altLabel = altName || altCode;
                          return (
                            <button
                              key={`alt-wh-${system.id || index}-${altCode}`}
                              type="button"
                              className="impqx-ahri-shop-system-alt-warehouse-chip"
                              title={`Shop this system from ${altLabel} (${altCode})`}
                              aria-label={`Switch to and shop from warehouse ${altLabel}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                viewSystemAtWarehouse(system, altCode);
                              }}
                            >
                              <span className="impqx-ahri-shop-system-alt-warehouse-chip-text">{altLabel}</span>
                              <span className="impqx-ahri-shop-system-alt-warehouse-go" aria-hidden="true">
                                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                  <path d="M5 12h13M13 6l6 6-6 6" />
                                </svg>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  );
                })()}
                <div className="impqx-ahri-shop-system-metrics">
                  <div className="impqx-ahri-shop-system-metric">
                    <div className="impqx-ahri-shop-system-metric-label">SEER2</div>
                    <div className="impqx-ahri-shop-system-metric-value seer">{system.seer2 || "-"}</div>
                  </div>
                  <div className="impqx-ahri-shop-system-metric">
                    <div className="impqx-ahri-shop-system-metric-label">HSPF2</div>
                    <div className="impqx-ahri-shop-system-metric-value hspf">{system.hspf2 || "-"}</div>
                  </div>
                  <div className="impqx-ahri-shop-system-metric">
                    <div className="impqx-ahri-shop-system-metric-label">EER2</div>
                    <div className="impqx-ahri-shop-system-metric-value eer">{system.eer2 || "-"}</div>
                  </div>
                </div>

                <div className="impqx-ahri-shop-product-list impqx-ahri-shop-system-product-list">
                  {renderSystemSlot("outdoor", "Outdoor Unit", outdoorCode, outdoorPricing)}
                  {renderSystemSlot("indoor", "Indoor Unit", indoorCode, indoorPricing)}
                  {renderSystemSlot("furnace", "Furnace", furnaceCode, furnacePricing)}
                </div>
                <div className="impqx-ahri-shop-system-actions-wrap">
                  {hasSystemTotal && (
                    <div className="impqx-ahri-shop-system-total">
                      <span className="impqx-ahri-shop-system-total-label">System Cost</span>
                      <span className="impqx-ahri-shop-system-total-values">
                        {shouldShowListPrice(systemTotalList, systemTotalYour) && (
                          <span className="impqx-ahri-shop-price-list">{formatUsd(systemTotalList)}</span>
                        )}
                        <span className="impqx-ahri-shop-price-your">
                          {formatUsd(systemTotalYour)} <span className="impqx-ahri-shop-price-uom">/ EA</span>
                        </span>
                      </span>
                    </div>
                  )}
                  <div className="impqx-ahri-shop-card-actions impqx-ahri-shop-system-actions">
                    <button type="button" className="impqx-ahri-shop-button impqx-ahri-shop-system-details-btn" onClick={() => openSystemDetail(system)}>
                      <span className="impqx-ahri-shop-system-details-btn-label">
                        {system.hasComponentChoices ? "Details & Options" : "Details"}
                      </span>
                      {system.hasComponentChoices && (
                        <span className="impqx-ahri-shop-badge impqx-ahri-shop-choices-badge" title="Multiple component options — choose your configuration">
                          Choose unit
                        </span>
                      )}
                    </button>
                    <span className="impqx-ahri-shop-system-actions-divider" aria-hidden="true" />
                    <div className="impqx-ahri-shop-cart-entry-inline">
                      <input
                        type="number"
                        min={1}
                        step={1}
                        className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
                        aria-label="System quantity"
                        value={getCartInputValues("system", systemCartId, { qty: 1, uom: "EA" }).qty}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => setCartInputField("system", systemCartId, "qty", event.target.value)}
                      />
                      <button
                        type="button"
                        className="impqx-ahri-shop-button primary impqx-ahri-shop-system-add-btn"
                        disabled={systemAddDisabled}
                        title={systemAddTitle}
                        onClick={() => {
                          const cartInput = getCartInputValues("system", systemCartId, { qty: 1, uom: "EA" });
                          addSystemProductsToCart(buildConfiguredSystem(system), {
                            quantity: cartInput.qty,
                            uom: cartInput.uom,
                          });
                        }}
                      >
                        <span className="impqx-ahri-shop-system-add-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                            <circle cx="9" cy="20" r="1.6" />
                            <circle cx="18" cy="20" r="1.6" />
                            <path d="M3 4h2l2.2 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 .98-.79L20 8H7" />
                          </svg>
                        </span>
                        Add System
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>
            );
          })}
        </div>}

        {activeResultsTab === "ahri" && selectedSystemKeys.length > 0 && (
          <section className="impqx-ahri-shop-selection-tray">
            <div className="impqx-ahri-shop-selection-tray-left">
              <strong>{selectedSystemKeys.length}</strong>
              <span>{selectedSystemKeys.length === 1 ? "system selected" : "systems selected"}</span>
            </div>
            <div className="impqx-ahri-shop-selection-tray-actions">
              <button
                type="button"
                className="impqx-ahri-shop-button"
                onClick={compareSelectedSystems}
                disabled={selectedSystems.length < 2}
              >
                Compare Selected
              </button>
              <button
                type="button"
                className="impqx-ahri-shop-button primary"
                onClick={addSelectedSystemsToCart}
                disabled={selectedSystemsAddToCartGate.disabled}
                title={selectedSystemsAddToCartGate.title}
              >
                Add Selected to Cart
              </button>
              <button
                type="button"
                className="impqx-ahri-shop-button"
                onClick={clearSelectedSystems}
              >
                Clear Selection
              </button>
            </div>
          </section>
        )}

        {activeResultsTab === "ahri" && selectedSystemAnchor && (
          <section className="impqx-ahri-shop-quick-narrow">
            <div className="impqx-ahri-shop-quick-narrow-title">Quick narrow by selected system</div>
            <div className="impqx-ahri-shop-quick-narrow-actions">
              <button type="button" className="impqx-ahri-shop-button" onClick={() => applyQuickNarrow("brand")}>
                Match Brand
              </button>
              <button type="button" className="impqx-ahri-shop-button" onClick={() => applyQuickNarrow("seer2")}>
                Match SEER2
              </button>
              <button type="button" className="impqx-ahri-shop-button" onClick={() => applyQuickNarrow("bundle")}>
                Match Product Bundle
              </button>
            </div>
          </section>
        )}

        {activeResultsTab === "ahri" && (
        <div className="impqx-ahri-shop-pagination d-flex justify-content-end align-items-center gap-2">
          <button
            type="button"
            className="impqx-ahri-shop-button"
            disabled={loading || page <= 1}
            onClick={() => void fetchResults(page - 1, query, selectedFilters, productCodeFilters, selectedAdvancedFilters)}
          >
            Previous
          </button>
          <span>
            Page {page} / {totalPages}
          </span>
          <button
            type="button"
            className="impqx-ahri-shop-button"
            disabled={loading || page >= totalPages}
            onClick={() => void fetchResults(page + 1, query, selectedFilters, productCodeFilters, selectedAdvancedFilters)}
          >
            Next
          </button>
        </div>
        )}

        {activeResultsTab === "products" && (
          <div className="impqx-ahri-shop-product-tab-panel">
            {productSearchLoading && (
              <div className="impqx-ahri-shop-card impqx-ahri-shop-loading-card">
                <div className="impqx-ahri-shop-loading-animation" aria-hidden="true">
                  <div className="impqx-ahri-shop-penguin-track">
                    <div className="impqx-ahri-shop-penguin-walk">
                      <div className="impqx-ahri-shop-penguin" />
                    </div>
                  </div>
                </div>
                <strong>Searching products...</strong>
              </div>
            )}
            {!productSearchLoading && productResults.length === 0 && (
              <div className="impqx-ahri-shop-card impqx-ahri-shop-empty-card">
                <strong>No products found.</strong>
                <div className="impqx-ahri-shop-subtitle">Try a different search term.</div>
              </div>
            )}
            {!productSearchLoading && productResults.length > 0 && (
              <>
                <div className="impqx-ahri-shop-meta">
                  <span>
                    {productSearchTotal > PRODUCT_PAGE_SIZE
                      ? `Page ${productPage} · ${((productPage - 1) * PRODUCT_PAGE_SIZE + 1).toLocaleString()}–${Math.min(productPage * PRODUCT_PAGE_SIZE, productSearchTotal).toLocaleString()} of ${productSearchTotal.toLocaleString()} products`
                      : `${productResults.length.toLocaleString()} of ${productSearchTotal.toLocaleString()} products`}
                  </span>
                </div>
                <div className="impqx-ahri-shop-product-tab-grid">
                  {productResults.map((product, idx) => {
                    const imgUrl = product.image_url ? resolveAssetUrl(product.image_url, endpoint) : null;
                    const pricing = resolvePricingForProductCode(product.product_code);
                    const pgCartId = `pgrid-${product.product_code}`;
                    const addDisabled = isProductAddToCartBlockedByPrice(product.product_code, pricing, isGetPricePendingForCode);
                    const addTitle = productAddToCartBlockedTitle(product.product_code, pricing, isGetPricePendingForCode);
                    return (
                      <div
                        key={product.id || product.product_code || idx}
                        className="impqx-ahri-shop-product-tab-card impqx-ahri-shop-product-ec-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => openProductDetail(product.product_code, "product")}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openProductDetail(product.product_code, "product"); }}
                        aria-label={`View ${product.name || product.product_code} details`}
                      >
                        {/* Row 1 col 1 — image */}
                        <div className="impqx-ahri-shop-product-ec-img-wrap">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={product.name || product.product_code}
                              className="impqx-ahri-shop-product-ec-img"
                              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                            />
                          ) : null}
                          <div className="impqx-ahri-shop-product-ec-no-img" style={imgUrl ? { display: "none" } : {}}>
                            <FontAwesomeIcon icon={faImage} />
                          </div>
                        </div>
                        {/* Row 1 col 2 — meta */}
                        <div className="impqx-ahri-shop-product-ec-meta">
                          {product.category && (
                            <span className="impqx-ahri-shop-product-ec-cat">{product.category}</span>
                          )}
                          {product.manufacturer_name && (
                            <div className="impqx-ahri-shop-product-ec-mfr">{decodeHtmlEntities(product.manufacturer_name)}</div>
                          )}
                          <div className="impqx-ahri-shop-product-ec-code">{product.product_code}</div>
                          <div className="impqx-ahri-shop-price-pair">
                            {isGetPricePendingForCode(product.product_code) ? (
                              <span className="impqx-ahri-shop-price-loading">Loading…</span>
                            ) : pricing.yourPrice ? (
                              <>
                                {shouldShowListPrice(pricing.listPrice, pricing.yourPrice) && (
                                  <span className="impqx-ahri-shop-price-list">{formatUsd(pricing.listPrice)}</span>
                                )}
                                <span className="impqx-ahri-shop-price-your">{formatUsd(pricing.yourPrice)}</span>
                              </>
                            ) : null}
                          </div>
                        </div>
                        {/* Row 2 — full-width name + description */}
                        <div className="impqx-ahri-shop-product-ec-body">
                          <div
                            className="impqx-ahri-shop-product-ec-name"
                            dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(product.name) || "—" }}
                          />
                          {(product.description || product.short_description) && (
                            <div
                              className="impqx-ahri-shop-product-ec-desc"
                              dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(product.description || product.short_description) }}
                            />
                          )}
                        </div>
                        {/* Row 3 — full-width actions */}
                        <div className="impqx-ahri-shop-product-ec-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="impqx-ahri-shop-cart-entry-inline">
                            <input
                              type="number"
                              min={1}
                              step={1}
                              className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
                              aria-label={`Quantity for ${product.name || product.product_code}`}
                              value={getCartInputValues("product", pgCartId, { qty: 1, uom: "EA" }).qty}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setCartInputField("product", pgCartId, "qty", e.target.value)}
                            />
                            <button
                              type="button"
                              className="impqx-ahri-shop-button primary impqx-ahri-shop-system-add-btn"
                              disabled={addDisabled}
                              title={addTitle || "Add to cart"}
                              onClick={(e) => {
                                e.stopPropagation();
                                const cartInput = getCartInputValues("product", pgCartId, { qty: 1, uom: "EA" });
                                addToCart({ id: product.product_code }, "product", {
                                  itemId: product.product_code,
                                  title: product.name || product.product_code,
                                  subtitle: `Product • ${product.product_code}`,
                                  imageUrl: imgUrl || "",
                                  unitListPrice: pricing.listPrice,
                                  unitYourPrice: pricing.yourPrice,
                                  quantity: normalizeCartQty(cartInput.qty, 1),
                                  uom: normalizeCartUom(cartInput.uom, "EA"),
                                  componentRole: "product",
                                });
                              }}
                            >
                              <span className="impqx-ahri-shop-system-add-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                  <circle cx="9" cy="20" r="1.6" />
                                  <circle cx="18" cy="20" r="1.6" />
                                  <path d="M3 4h2l2.2 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 .98-.79L20 8H7" />
                                </svg>
                              </span>
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {productSearchTotal > PRODUCT_PAGE_SIZE && (
              <div className="impqx-ahri-shop-pagination d-flex justify-content-end align-items-center gap-2" style={{marginTop: "12px"}}>
                <button
                  type="button"
                  className="impqx-ahri-shop-button"
                  disabled={productSearchLoading || productPage <= 1}
                  onClick={() => void fetchProductResults(query, productPage - 1)}
                >Previous</button>
                <span>Page {productPage} / {Math.ceil(productSearchTotal / PRODUCT_PAGE_SIZE) || 1}</span>
                <button
                  type="button"
                  className="impqx-ahri-shop-button"
                  disabled={productSearchLoading || !productHasNext}
                  onClick={() => void fetchProductResults(query, productPage + 1)}
                >Next</button>
              </div>
            )}
          </div>
        )}

        {detailOpen && selectedSystem && (
          <div
            className="impqx-ahri-shop-overlay impqx-ahri-shop-overlay-drawer"
            style={embeddedHeaderOffset ? { inset: `${embeddedHeaderOffset}px 0 0 0` } : undefined}
            onClick={() => setDetailOpen(false)}
          >
            <div
              className="impqx-ahri-shop-drawer impqx-ahri-shop-system-detail-drawer impqx-ahri-shop-system-detail-modal impqx-ahri-shop-drawer-slide"
              onClick={(event) => event.stopPropagation()}
            >
              <div className={`impqx-ahri-shop-modal-head ${isComparisonView ? "impqx-ahri-shop-modal-head-compare" : ""}`}>
                {!isComparisonView && (
                  <strong>System Details - AHRI #{renderAhriIdText(selectedSystem.id, selectedSystem)}</strong>
                )}
                <div className="d-flex gap-2 flex-wrap">
                  {!isComparisonView && (
                    <>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
                        aria-label="System quantity"
                        value={getCartInputValues("system", String(selectedSystem.id || ""), { qty: 1, uom: "EA" }).qty}
                        onChange={(event) => setCartInputField("system", String(selectedSystem.id || ""), "qty", event.target.value)}
                      />
                      <button
                        type="button"
                        className="impqx-ahri-shop-button primary impqx-ahri-shop-system-add-btn"
                        disabled={detailDrawerSystemAddGate.pending || !detailDrawerSystemAddGate.hasDisplayablePrice}
                        title={systemAddToCartBlockedTitle(detailDrawerSystemAddGate)}
                        onClick={() => {
                          const cartInput = getCartInputValues("system", String(selectedSystem.id || ""), { qty: 1, uom: "EA" });
                          addSystemProductsToCart(buildConfiguredSystem(selectedSystem), {
                            quantity: cartInput.qty,
                            uom: cartInput.uom,
                          });
                        }}
                      >
                        <span className="impqx-ahri-shop-system-add-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                            <circle cx="9" cy="20" r="1.6" />
                            <circle cx="18" cy="20" r="1.6" />
                            <path d="M3 4h2l2.2 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 .98-.79L20 8H7" />
                          </svg>
                        </span>
                        Add System to Cart
                      </button>
                    </>
                  )}
                  <button type="button" className="impqx-ahri-shop-button" onClick={() => setDetailOpen(false)}>
                    Close
                  </button>
                </div>
              </div>
              {isComparisonView ? (
                <div className="compare-shell">
                  <div className="compare-hero compare-hero-in-modal">
                    <div className="compare-hero__content">
                      <div>
                        <h4 className="mb-1">Compare AHRI Systems</h4>
                        <div className="impqx-ahri-shop-subtitle">Review AHRI data and matched products side-by-side.</div>
                      </div>
                      <div className="compare-hero__actions">
                        <span className="compare-selected-tag">{orderedComparisonSystems.length} Selected</span>
                        <div className="compare-hero__toggles">
                          <button
                            type="button"
                            className={`impqx-ahri-shop-button ${compareHighlightMode === "diff" ? "primary" : ""}`}
                            onClick={() => setCompareHighlightMode("diff")}
                          >
                            Highlight Differences
                          </button>
                          <button
                            type="button"
                            className={`impqx-ahri-shop-button ${compareHighlightMode === "similar" ? "primary" : ""}`}
                            onClick={() => setCompareHighlightMode("similar")}
                          >
                            Highlight Similar
                          </button>
                          <button
                            type="button"
                            className={`impqx-ahri-shop-button ${compareHighlightMode === "both" ? "primary" : ""}`}
                            onClick={() => setCompareHighlightMode("both")}
                          >
                            Highlight Both
                          </button>
                          <button type="button" className="impqx-ahri-shop-button" onClick={() => setCompareHighlightMode("none")}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="compare-table-wrap compare-desktop">
                    <div className="compare-table">
                      <div
                        className="compare-row-grid compare-row-header"
                        style={{ gridTemplateColumns: `200px repeat(${orderedComparisonSystems.length}, minmax(260px, 1fr))` }}
                      >
                        <div className="compare-cell compare-header compare-label">Attribute</div>
                        {orderedComparisonSystems.map((system, index) => (
                          <div
                            key={`header-${system?.id || system?.__selectionKey || index}`}
                            className={`compare-cell compare-header ${compareDragIndex === index ? "compare-col-dragging" : ""}`}
                            draggable
                            onDragStart={() => setCompareDragIndex(index)}
                            onDragEnd={() => setCompareDragIndex(null)}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => {
                              if (compareDragIndex === null || compareDragIndex === index) return;
                              setCompareColumnOrder((prev) => {
                                const next = [...prev];
                                const [moved] = next.splice(compareDragIndex, 1);
                                next.splice(index, 0, moved);
                                return next;
                              });
                              setCompareDragIndex(null);
                            }}
                          >
                            <div className="fw-semibold">AHRI #{renderAhriIdText(system?.id, system)}</div>
                            <div className="text-muted small">
                              {decodeHtmlEntities(system?.outdoorBrand) || "-"} / {decodeHtmlEntities(system?.indoorBrand) || "-"}
                            </div>
                            <div className="compare-col-controls">
                              <button
                                type="button"
                                className="compare-col-btn"
                                disabled={index === 0}
                                onClick={() =>
                                  setCompareColumnOrder((prev) => {
                                    const next = [...prev];
                                    [next[index], next[index - 1]] = [next[index - 1], next[index]];
                                    return next;
                                  })
                                }
                              >
                                ←
                              </button>
                              <button
                                type="button"
                                className="compare-col-btn"
                                disabled={index === orderedComparisonSystems.length - 1}
                                onClick={() =>
                                  setCompareColumnOrder((prev) => {
                                    const next = [...prev];
                                    [next[index], next[index + 1]] = [next[index + 1], next[index]];
                                    return next;
                                  })
                                }
                              >
                                →
                              </button>
                            </div>
                            {renderCompareSystemAddAction(system)}
                          </div>
                        ))}
                      </div>
                      {compareRows.map((row) => (
                        <div
                          key={`row-${row.key}`}
                          className="compare-row-grid"
                          style={{ gridTemplateColumns: `200px repeat(${orderedComparisonSystems.length}, minmax(260px, 1fr))` }}
                        >
                          <div className="compare-cell compare-label">
                            <div className="compare-label-row">
                              <span>{row.label}</span>
                            </div>
                          </div>
                          {orderedComparisonSystems.map((system, index) => (
                            <div
                              key={`${system?.id || system?.__selectionKey || index}-${row.key}`}
                              className={`compare-cell ${getRowCellHighlightClass(row, system)}`}
                            >
                              {row.type === "product" ? (
                                <div className={`compare-product-highlight ${getProductHighlightClass(row.productType, system)}`}>
                                  {row.productType === "outdoor"
                                    ? renderCompareProductCard(system?.outdoorProductCode, "Outdoor Unit", system, "outdoor")
                                    : row.productType === "indoor"
                                      ? renderCompareProductCard(system?.indoorProductCode, "Indoor Unit", system, "indoor")
                                      : renderCompareProductCard(system?.furnaceProductCode, "Furnace", system, "furnace")}
                                </div>
                              ) : (
                                typeof row.value === "function" ? row.value(system) : "-"
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="compare-mobile-stack compare-mobile">
                    {orderedComparisonSystems.map((system, index) => (
                      <article key={`mobile-${system?.id || system?.__selectionKey || index}`} className="compare-system-card mb-3">
                        <div className="mb-2">
                          <div className="fw-semibold">AHRI #{renderAhriIdText(system?.id, system)}</div>
                          <div className="text-muted small">
                            {decodeHtmlEntities(system?.outdoorBrand) || "-"} / {decodeHtmlEntities(system?.indoorBrand) || "-"}
                          </div>
                          {renderCompareSystemAddAction(system)}
                        </div>
                        <div className="compare-product-stack">
                          <div className={`compare-product-highlight ${getProductHighlightClass("outdoor", system)}`}>
                            {renderCompareProductCard(system?.outdoorProductCode, "Outdoor Unit", system, "outdoor")}
                          </div>
                          <div className={`compare-product-highlight ${getProductHighlightClass("indoor", system)}`}>
                            {renderCompareProductCard(system?.indoorProductCode, "Indoor Unit", system, "indoor")}
                          </div>
                          <div className={`compare-product-highlight ${getProductHighlightClass("furnace", system)}`}>
                            {renderCompareProductCard(system?.furnaceProductCode, "Furnace", system, "furnace")}
                          </div>
                        </div>
                        {compareRows.filter((row) => row.type !== "product").map((row) => (
                          <div key={`mobile-${system?.id || index}-${row.key}`} className={`compare-row ${getRowCellHighlightClass(row, system)}`}>
                            <div className="compare-label">{row.label}</div>
                            <div className="compare-value">{typeof row.value === "function" ? row.value(system) : "-"}</div>
                          </div>
                        ))}
                      </article>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="impqx-ahri-shop-system-modal-card">
                    <div className="impqx-ahri-shop-system-modal-card-title">Efficiency Ratings</div>
                    <div className="impqx-ahri-shop-system-detail-metrics">
                      <div><span>SEER2</span><strong>{selectedSystem.seer2 || "-"}</strong></div>
                      <div><span>HSPF2</span><strong>{selectedSystem.hspf2 || "-"}</strong></div>
                      <div><span>EER2</span><strong>{selectedSystem.eer2 || "-"}</strong></div>
                      <div><span>Cooling</span><strong>{getSystemRawValue(selectedSystem, ["rows_coolingcapacity", "rows_cooling_capacity"]) || "-"}</strong></div>
                    </div>
                  </div>
                  <div className="impqx-ahri-shop-system-modal-card">
                    <div className="impqx-ahri-shop-system-modal-card-title">Status Badges</div>
                    <div className="impqx-ahri-shop-status-tags">
                      {selectedSystem.isEnergyStar && (
                        <span className="impqx-ahri-shop-status-pill success">Energy Star Certified</span>
                      )}
                      {selectedSystem.completeMatch && (
                        <span className="impqx-ahri-shop-status-pill info">Complete Product Match</span>
                      )}
                      {selectedSystem.partialMatch && !selectedSystem.completeMatch && (
                        <span className="impqx-ahri-shop-status-pill warning">Partial Product Match</span>
                      )}
                      {selectedSystem.featuredForWarehouse && (() => {
                        const names = (selectedSystem.featuredGroupNamesForWarehouse?.length
                          ? selectedSystem.featuredGroupNamesForWarehouse
                          : selectedSystem.featuredGroupName ? [selectedSystem.featuredGroupName] : []);
                        const namesText = names.join(", ");
                        return (
                          <span className="impqx-ahri-shop-status-pill featured">
                            <svg className="impqx-ahri-shop-ribbon-flag-icon" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                              <path d="M8 0.5c.3 2.1 1 3.6 2 4.6 1 1 2.5 1.7 4.6 2-2.1.3-3.6 1-4.6 2-1 1-1.7 2.5-2 4.6-.3-2.1-1-3.6-2-4.6-1-1-2.5-1.7-4.6-2 2.1-.3 3.6-1 4.6-2 1-1 1.7-2.5 2-4.6z" />
                            </svg>
                            <span>{namesText || "Featured"}</span>
                          </span>
                        );
                      })()}
                      {!!getSystemRawValue(selectedSystem, ["rows_programname"]) && (
                        <span className="impqx-ahri-shop-status-pill neutral">
                          {getSystemRawValue(selectedSystem, ["rows_programname"])}
                        </span>
                      )}
                      {!!getSystemRawValue(selectedSystem, ["rows_region"]) && (
                        <span className="impqx-ahri-shop-status-pill neutral">
                          {getSystemRawValue(selectedSystem, ["rows_region"])}
                        </span>
                      )}
                    </div>
                  </div>
                  {!!selectedSystem?.id && bootstrapped && ecoRebatesConfig.enabled && (
                    <div
                      className={`impqx-ahri-shop-rebate-widget-wrap ${ecoRebateVisibleByContainerId[selectedSystemRebateContainerId] ? "is-visible" : "is-hidden"}`}
                      role="button"
                      tabIndex={0}
                      title="Click to view more rebate details"
                      onClickCapture={(event) => {
                        activateRebateDetails(selectedSystemRebateContainerId, event);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          activateRebateDetails(selectedSystemRebateContainerId, event);
                        }
                      }}
                    >
                      <span className="impqx-ahri-shop-rebate-icon" aria-hidden="true">
                        <i className="fa-solid fa-bolt" />
                      </span>
                      <div id={selectedSystemRebateContainerId} className="impqx-ahri-shop-rebate-widget" />
                      <span
                        className="impqx-ahri-shop-rebate-more-btn"
                        title="Click to view more rebate details"
                        aria-label="Click to view more rebate details"
                      >
                        More info
                      </span>
                    </div>
                  )}
                  <div className="impqx-ahri-shop-system-modal-card">
                    <div className="impqx-ahri-shop-system-modal-card-title">System Components</div>
                    {selectedSystemChoiceSlotCount > 0 && (
                      <div className="impqx-ahri-shop-detail-options-banner" role="note">
                        <span className="impqx-ahri-shop-detail-options-banner-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                            <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14h-2v-2h2zm0-4h-2V7h2z" />
                          </svg>
                        </span>
                        <div className="impqx-ahri-shop-detail-options-banner-copy">
                          <strong>
                            {selectedSystemChoiceSlotCount === 1
                              ? "1 component has multiple approved options"
                              : `${selectedSystemChoiceSlotCount} components have multiple approved options`}
                          </strong>
                          <span>Select the configuration you want for each highlighted unit below.</span>
                        </div>
                      </div>
                    )}
                    {!isComparisonView && (
                      <div className="impqx-ahri-shop-subtitle impqx-ahri-shop-system-modal-total-hint">
                        {selectedSystemTotals.priceLoading ? (
                          <span>Loading system prices…</span>
                        ) : selectedSystemTotals.hasPrice ? (
                          <span>
                            System total:{" "}
                            <span className="impqx-ahri-shop-price-your">{formatUsd(selectedSystemTotals.totalYour)}</span>
                            {shouldShowListPrice(selectedSystemTotals.totalList, selectedSystemTotals.totalYour) ? (
                              <>
                                {" "}
                                <span className="impqx-ahri-shop-price-list">
                                  ({formatUsd(selectedSystemTotals.totalList)} list)
                                </span>
                              </>
                            ) : null}
                          </span>
                        ) : (
                          <span>
                            {mustLoginBeforeGetprice()
                              ? "Log in to see system pricing"
                              : "System pricing unavailable"}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="impqx-ahri-shop-system-detail-products-grid">
                      {selectedSystemProducts.map((product) => (
                        <article
                          key={`detail-product-${product.code}`}
                          className={`impqx-ahri-shop-system-detail-product-card ${product.hasChoices ? "has-options" : ""}`}
                        >
                          <div className="impqx-ahri-shop-system-detail-product-head">
                            <strong>{product.slot}</strong>
                            {product.hasChoices ? (
                              <span className="impqx-ahri-shop-detail-option-count-badge">
                                {product.slotOptions.length} options
                              </span>
                            ) : (
                              <span>{product.code}</span>
                            )}
                          </div>
                          {product.hasChoices && (
                            <div
                              className="impqx-ahri-shop-detail-option-panel"
                              role="radiogroup"
                              aria-label={`Choose ${product.slot}`}
                            >
                              <div className="impqx-ahri-shop-detail-option-panel-head">
                                <div className="impqx-ahri-shop-detail-option-panel-title">
                                  <span className="impqx-ahri-shop-detail-option-panel-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                      <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h10v2H4z" />
                                    </svg>
                                  </span>
                                  <span>Choose your {product.slot.toLowerCase()}</span>
                                </div>
                                <span className="impqx-ahri-shop-detail-option-panel-hint">
                                  Tap an option below to update this component
                                </span>
                                {product.optionDiffKeyLabels?.length > 0 && (
                                  <span className="impqx-ahri-shop-detail-option-panel-differ">
                                    Options differ by {product.optionDiffKeyLabels.join(", ")}
                                  </span>
                                )}
                              </div>
                              <div className="impqx-ahri-shop-detail-option-cards">
                                {product.slotOptions.map((option) => {
                                  const isSelected = option.code === product.code;
                                  return (
                                    <button
                                      key={`detail-opt-${product.type}-${option.code}`}
                                      type="button"
                                      role="radio"
                                      aria-checked={isSelected}
                                      className={`impqx-ahri-shop-detail-option-card ${isSelected ? "is-selected" : ""}`}
                                      onClick={() => selectComponentCode(product.type, option.code)}
                                    >
                                      <span className="impqx-ahri-shop-detail-option-card-check" aria-hidden="true">
                                        {isSelected ? (
                                          <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                            <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                                          </svg>
                                        ) : (
                                          <span className="impqx-ahri-shop-detail-option-card-check-empty" />
                                        )}
                                      </span>
                                      <img
                                        className="impqx-ahri-shop-detail-option-card-thumb"
                                        src={option.imageUrl || PRODUCT_IMAGE_PLACEHOLDER}
                                        alt=""
                                        loading="lazy"
                                        onError={(event) => {
                                          event.currentTarget.onerror = null;
                                          event.currentTarget.src = PRODUCT_IMAGE_PLACEHOLDER;
                                        }}
                                      />
                                      <span className="impqx-ahri-shop-detail-option-card-body">
                                        <span className="impqx-ahri-shop-detail-option-card-top">
                                          <span className={`impqx-ahri-shop-availability-dot ${option.dotClass}`} aria-hidden="true" />
                                          <span className="impqx-ahri-shop-detail-option-card-name">{option.name}</span>
                                        </span>
                                        <span className="impqx-ahri-shop-detail-option-card-code">{option.code}</span>
                                        {option.stockStatus && (
                                          <span
                                            className={`impqx-ahri-shop-availability-status impqx-ahri-shop-availability-status--${option.stockStatus.tone} impqx-ahri-shop-detail-option-card-stock`}
                                          >
                                            {option.stockStatus.text}
                                          </span>
                                        )}
                                        {option.diffLabels.length > 0 && (
                                          <span className="impqx-ahri-shop-detail-option-card-diffs">
                                            {option.diffLabels.map((diff) => (
                                              <span key={`${option.code}-${diff.key}`} className="impqx-ahri-shop-slot-option-diff">
                                                <span className="impqx-ahri-shop-slot-option-diff-key">{diff.keyLabel}</span>
                                                <span className="impqx-ahri-shop-slot-option-diff-val">{diff.value}</span>
                                              </span>
                                            ))}
                                          </span>
                                        )}
                                      </span>
                                      <span className="impqx-ahri-shop-detail-option-card-price">
                                        {isGetPricePendingForCode(option.code) ? (
                                          <span className="impqx-ahri-shop-subtitle">Loading…</span>
                                        ) : mustLoginBeforeGetprice() ? (
                                          <span className="impqx-ahri-shop-subtitle">Login</span>
                                        ) : pricingRowIsDisplayable(option.pricing) ? (
                                          <span className="impqx-ahri-shop-price-your">{formatUsd(option.pricing.yourPrice)}</span>
                                        ) : (
                                          <span className="impqx-ahri-shop-subtitle">Call</span>
                                        )}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div className="impqx-ahri-shop-detail-selected-label">
                            {product.hasChoices ? "Your selected configuration" : "Configuration"}
                          </div>
                          <img
                            className="impqx-ahri-shop-system-detail-product-image"
                            src={product.imageUrl || PRODUCT_IMAGE_PLACEHOLDER}
                            alt={product.name}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = PRODUCT_IMAGE_PLACEHOLDER;
                            }}
                          />
                          <div className="impqx-ahri-shop-system-detail-product-title">{product.name}</div>
                          <div className="impqx-ahri-shop-system-detail-product-sub">
                            {product.detail?.manufacturer_part_number || product.code}
                            {product.detail?.category ? ` • ${product.detail.category}` : ""}
                          </div>
                          <div className="impqx-ahri-shop-system-detail-product-buyrow">
                            <div className="impqx-ahri-shop-system-detail-product-pricing">
                              {isGetPricePendingForCode(product.code) ? (
                                <span className="impqx-ahri-shop-subtitle" title="Loading prices">
                                  Loading…
                                </span>
                              ) : mustLoginBeforeGetprice() ? (
                                <span className="impqx-ahri-shop-subtitle">Login for Price</span>
                              ) : !pricingRowIsDisplayable(product.pricing) ? (
                                <span className="impqx-ahri-shop-subtitle">Call for price</span>
                              ) : (
                                <>
                                  {shouldShowListPrice(product?.pricing?.listPrice, product?.pricing?.yourPrice) && (
                                    <span className="impqx-ahri-shop-price-list">{formatUsd(product.pricing.listPrice)}</span>
                                  )}
                                  <span className="impqx-ahri-shop-price-your">
                                    {formatUsd(product?.pricing?.yourPrice)}{" "}
                                    <span className="impqx-ahri-shop-price-uom">/ {resolveProductUom(product.detail)}</span>
                                  </span>
                                </>
                              )}
                            </div>
                            <div className="impqx-ahri-shop-cart-entry-inline">
                              <input
                                type="number"
                                min={1}
                                step={1}
                                className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
                                aria-label={`${product.slot} quantity`}
                                value={getCartInputValues("product", product.code, { qty: 1, uom: resolveProductUom(product.detail) }).qty}
                                onChange={(event) => setCartInputField("product", product.code, "qty", event.target.value)}
                              />
                              <button
                                type="button"
                                className="impqx-ahri-shop-button primary impqx-ahri-shop-system-add-btn"
                                disabled={isProductAddToCartBlockedByPrice(
                                  product.code,
                                  product.pricing,
                                  isGetPricePendingForCode
                                )}
                                title={productAddToCartBlockedTitle(
                                  product.code,
                                  product.pricing,
                                  isGetPricePendingForCode
                                )}
                                onClick={() => {
                                  const cartInput = getCartInputValues("product", product.code, {
                                    qty: 1,
                                    uom: resolveProductUom(product.detail),
                                  });
                                  addProductToCart(product, selectedSystem, {
                                    quantity: cartInput.qty,
                                    uom: cartInput.uom,
                                  });
                                }}
                              >
                                <span className="impqx-ahri-shop-system-add-icon" aria-hidden="true">
                                  <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                                    <circle cx="9" cy="20" r="1.6" />
                                    <circle cx="18" cy="20" r="1.6" />
                                    <path d="M3 4h2l2.2 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 .98-.79L20 8H7" />
                                  </svg>
                                </span>
                                Add to Cart
                              </button>
                            </div>
                          </div>
                          {product.description && (
                            <div
                              className="impqx-ahri-shop-system-detail-product-description"
                              dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(product.description) }}
                            />
                          )}
                          <div className="impqx-ahri-shop-system-detail-product-meta">
                            <div><span>Manufacturer</span><strong>{decodeHtmlEntities(product.detail?.manufacturer_name) || "-"}</strong></div>
                            <div><span>Category</span><strong>{product.detail?.category || "-"}</strong></div>
                          </div>
                          {product.attrs.length > 0 && (
                            <div className="impqx-ahri-shop-system-detail-attrs">
                              {product.attrs.map(([key, value]) => (
                                <div key={`${product.code}-${key}`} className="impqx-ahri-shop-system-detail-attr-row">
                                  <span>{humanizeFieldName(key)}</span>
                                  <strong>{formatDetailValue(value) || "-"}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                        </article>
                      ))}
                    </div>
                  </div>

                  <div className="impqx-ahri-shop-system-modal-card">
                    <div className="impqx-ahri-shop-system-modal-card-title impqx-ahri-shop-ahri-record-head">
                      <span>AHRI Certificate Details</span>
                      {selectedSystemAhriRows.length > 0 && (
                        <span className="impqx-ahri-shop-ahri-record-count">
                          {selectedSystemAhriRows.length} {selectedSystemAhriRows.length === 1 ? "spec" : "specs"}
                        </span>
                      )}
                    </div>
                    {selectedSystemAhriRows.length > 0 ? (
                      <div className="impqx-ahri-shop-ahri-record-grid">
                        {selectedSystemAhriRows.map((row) => (
                          <div key={`ahri-row-${row.key}`} className="impqx-ahri-shop-ahri-record-item">
                            <span className="impqx-ahri-shop-ahri-record-item-label">{row.label}</span>
                            <strong className="impqx-ahri-shop-ahri-record-item-value">{row.value || "-"}</strong>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="impqx-ahri-shop-ahri-record-empty">
                        No AHRI certificate details available for this system.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}


        {productOpen && selectedProduct && (
          <div
            className="impqx-ahri-shop-overlay impqx-ahri-shop-overlay-drawer"
            style={embeddedHeaderOffset ? { inset: `${embeddedHeaderOffset}px 0 0 0` } : undefined}
            onClick={() => setProductOpen(false)}
          >
            <div
              className="impqx-ahri-shop-drawer impqx-ahri-shop-product-drawer"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="impqx-ahri-shop-modal-head">
                <strong>{toTitleCase(selectedProduct.type || "Product")} Details</strong>
                <button type="button" className="impqx-ahri-shop-button" onClick={() => setProductOpen(false)}>
                  Close
                </button>
              </div>
              <div className="impqx-ahri-shop-product-detail impqx-ahri-shop-product-pdp">
                <div className="impqx-ahri-shop-product-pdp-hero">
                  <div className="impqx-ahri-shop-product-pdp-media">
                    {!productImageLoadFailed ? (
                      <img
                        src={selectedProductImageUrl}
                        alt={selectedProductName}
                        onError={() => setProductImageLoadFailed(true)}
                      />
                    ) : (
                      <div className="impqx-ahri-shop-product-detail-noimg">No Image</div>
                    )}
                  </div>
                  <div className="impqx-ahri-shop-product-pdp-info">
                    <h5>{selectedProductName}</h5>
                    <div className="impqx-ahri-shop-product-pdp-meta">
                      <div className="impqx-ahri-shop-product-pdp-meta-row">
                        <span className="impqx-ahri-shop-product-pdp-meta-label">Code</span>
                        <strong className="impqx-ahri-shop-product-pdp-meta-value">{selectedProduct.code}</strong>
                      </div>
                      <div className="impqx-ahri-shop-product-pdp-meta-row">
                        <span className="impqx-ahri-shop-product-pdp-meta-label">MPN</span>
                        <strong className="impqx-ahri-shop-product-pdp-meta-value">
                          {selectedProduct?.data?.manufacturer_part_number || "-"}
                        </strong>
                      </div>
                      <div className="impqx-ahri-shop-product-pdp-meta-row">
                        <span className="impqx-ahri-shop-product-pdp-meta-label">Category</span>
                        <strong className="impqx-ahri-shop-product-pdp-meta-value">
                          {selectedProduct?.data?.category || "-"}
                        </strong>
                      </div>
                      <div className="impqx-ahri-shop-product-pdp-meta-row">
                        <span className="impqx-ahri-shop-product-pdp-meta-label">Manufacturer</span>
                        <strong className="impqx-ahri-shop-product-pdp-meta-value">
                          {decodeHtmlEntities(selectedProduct?.data?.manufacturer_name) || "-"}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>
                {!!selectedProductRebateCode && bootstrapped && ecoRebatesConfig.enabled && (
                  <div
                    className={`impqx-ahri-shop-rebate-widget-wrap ${ecoRebateVisibleByContainerId[selectedProductRebateContainerId] ? "is-visible" : "is-hidden"}`}
                    role="button"
                    tabIndex={0}
                    title="Click to view more rebate details"
                    onClickCapture={(event) => {
                      activateRebateDetails(selectedProductRebateContainerId, event);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        activateRebateDetails(selectedProductRebateContainerId, event);
                      }
                    }}
                  >
                    <span className="impqx-ahri-shop-rebate-icon" aria-hidden="true">
                      <i className="fa-solid fa-bolt" />
                    </span>
                    <div id={selectedProductRebateContainerId} className="impqx-ahri-shop-rebate-widget" />
                    <span
                      className="impqx-ahri-shop-rebate-more-btn"
                      title="Click to view more rebate details"
                      aria-label="Click to view more rebate details"
                    >
                      More info
                    </span>
                  </div>
                )}
                <div
                  className="impqx-ahri-shop-product-description"
                  dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(selectedProduct?.data?.description) || "No description available." }}
                />
                {selectedProduct?.data?.attributes && (
                  <div className="impqx-ahri-shop-attr-list">
                    {Object.entries(selectedProduct.data.attributes).map(([key, value]) => (
                      <div key={key} className="impqx-ahri-shop-attr-row">
                        <span>{key}</span>
                        <strong>{String(value || "-")}</strong>
                      </div>
                    ))}
                  </div>
                )}
                <div className="impqx-ahri-shop-product-pdp-actions">
                  <div className="impqx-ahri-shop-product-pdp-actions-left">
                    <span className="impqx-ahri-shop-system-total-label">Your Price</span>
                    <span className="impqx-ahri-shop-system-total-values">
                      {selectedProductPdpPriceUi.mode === "pending" && (
                        <span className="impqx-ahri-shop-subtitle" title="Loading prices">
                          Loading…
                        </span>
                      )}
                      {selectedProductPdpPriceUi.mode === "login" && (
                        <span className="impqx-ahri-shop-subtitle">Login for Price</span>
                      )}
                      {selectedProductPdpPriceUi.mode === "call" && (
                        <span className="impqx-ahri-shop-subtitle">Call for price</span>
                      )}
                      {selectedProductPdpPriceUi.mode === "price" && (
                        <span className="impqx-ahri-shop-price-your">
                          {formatUsd(selectedProductPdpPriceUi.pricing?.yourPrice)}{" "}
                          <span className="impqx-ahri-shop-price-uom">/ {selectedProductCartInput.uom}</span>
                        </span>
                      )}
                      {selectedProductPdpPriceUi.mode === "empty" && (
                        <span className="impqx-ahri-shop-price-your">—</span>
                      )}
                    </span>
                  </div>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className="impqx-ahri-shop-input impqx-ahri-shop-cart-qty-input"
                    aria-label="Product quantity"
                    value={selectedProductCartInput.qty}
                    onChange={(event) => setCartInputField("product", selectedProduct.code, "qty", event.target.value)}
                  />
                  <button
                    type="button"
                    className="impqx-ahri-shop-button primary impqx-ahri-shop-product-pdp-add-btn"
                    disabled={selectedProductPdpPriceUi.addDisabled}
                    title={selectedProductPdpPriceUi.addTitle}
                    onClick={() => {
                      addToCart(selectedProduct.system || { id: selectedProduct.code }, "product", {
                        itemId: selectedProduct.code,
                        title: selectedProductName,
                        subtitle: `${selectedProduct.type || "Product"} • ${selectedProduct.code}`,
                        imageUrl: selectedProductImageUrl,
                        unitListPrice: selectedProductPricing.listPrice,
                        unitYourPrice: selectedProductPricing.yourPrice,
                        lineComment: selectedProduct?.system?.id ? `AHRI# ${selectedProduct.system.id}` : "",
                        quantity: selectedProductCartInput.qty,
                        uom: selectedProductCartInput.uom,
                        componentRole: selectedProduct.type || "product",
                      });
                    }}
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {cartOpen && (
          <div
            className="impqx-ahri-shop-overlay"
            style={embeddedHeaderOffset ? { inset: `${embeddedHeaderOffset}px 0 0 0` } : undefined}
            onClick={() => setCartOpen(false)}
          >
            <div className="impqx-ahri-shop-drawer" onClick={(event) => event.stopPropagation()}>
              <div className="impqx-ahri-shop-modal-head">
                <strong>Cart ({cartItemCount})</strong>
                <button type="button" className="impqx-ahri-shop-button" onClick={() => setCartOpen(false)}>
                  Close
                </button>
              </div>
              <div className="impqx-ahri-shop-cart-list">
                {cart.length === 0 ? (
                  <div className="impqx-ahri-shop-subtitle">Cart is empty.</div>
                ) : (
                  <>
                    {cart.map((item) => {
                      const qty = Math.max(0, Number(item?.quantity || 0));
                      const lineYour = Number.isFinite(item?.unitYourPrice) ? Number(item.unitYourPrice) * qty : null;
                      const lineList = Number.isFinite(item?.unitListPrice) ? Number(item.unitListPrice) * qty : null;
                      return (
                        <div key={`${item.id}-${item.type}`} className="impqx-ahri-shop-cart-row">
                          <div className="impqx-ahri-shop-cart-main">
                            <img
                              className="impqx-ahri-shop-cart-image"
                              src={item.imageUrl || PRODUCT_IMAGE_PLACEHOLDER}
                              alt={item.title || item.id}
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = PRODUCT_IMAGE_PLACEHOLDER;
                              }}
                            />
                            <div>
                              <strong>{item.title || `AHRI #${item.system?.id || item.id}`}</strong>
                              <div className="impqx-ahri-shop-subtitle">{item.subtitle || item.type}</div>
                              {item.lineComment && (
                                <div className="impqx-ahri-shop-cart-comment">{item.lineComment}</div>
                              )}
                              {(Number.isFinite(item?.unitListPrice) || Number.isFinite(item?.unitYourPrice)) && (
                                <div className="impqx-ahri-shop-cart-prices">
                                  {shouldShowListPrice(item?.unitListPrice, item?.unitYourPrice) && (
                                    <span className="impqx-ahri-shop-price-list">{formatUsd(item.unitListPrice)}</span>
                                  )}
                                  <span className="impqx-ahri-shop-price-your">
                                    {formatUsd(item.unitYourPrice)}{" "}
                                    <span className="impqx-ahri-shop-price-uom">/ {normalizeCartUom(item?.uom, "EA")}</span>
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="impqx-ahri-shop-cart-side">
                            <div className="impqx-ahri-shop-cart-qty">
                              <button type="button" className="impqx-ahri-shop-button" onClick={() => updateCartQty(item.id, item.type, qty - 1)}>
                                -
                              </button>
                              <span>{qty}</span>
                              <button type="button" className="impqx-ahri-shop-button" onClick={() => updateCartQty(item.id, item.type, qty + 1)}>
                                +
                              </button>
                            </div>
                            <div className="impqx-ahri-shop-cart-line-total">
                              {shouldShowListPrice(lineList, lineYour) && <span className="impqx-ahri-shop-price-list">{formatUsd(lineList)}</span>}
                              {Number.isFinite(lineYour) && <span className="impqx-ahri-shop-price-your">{formatUsd(lineYour)}</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="impqx-ahri-shop-cart-summary">
                      <span>Cart Total</span>
                      <span className="impqx-ahri-shop-cart-summary-values">
                        {shouldShowListPrice(cartTotals.listTotal, cartTotals.yourTotal) && (
                          <span className="impqx-ahri-shop-price-list">{formatUsd(cartTotals.listTotal)}</span>
                        )}
                        <span className="impqx-ahri-shop-price-your">{formatUsd(cartTotals.yourTotal)}</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {rebateDetailsState.open && (
          <div
            className="impqx-ahri-shop-overlay impqx-ahri-shop-overlay-drawer"
            style={embeddedHeaderOffset ? { inset: `${embeddedHeaderOffset}px 0 0 0` } : undefined}
            onClick={closeRebateDetails}
          >
            <div
              className="impqx-ahri-shop-drawer impqx-ahri-shop-rebate-details-drawer impqx-ahri-shop-drawer-slide"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-label="Energy rebate details"
              aria-busy={rebateDetailsState.loading ? "true" : "false"}
            >
              <div className="impqx-ahri-shop-modal-head">
                <strong>
                  <span className="impqx-ahri-shop-rebate-details-head-icon" aria-hidden="true">
                    <i className="fa-solid fa-bolt" />
                  </span>
                  Rebate Details
                  {rebateDetailsState.context.label ? ` · ${rebateDetailsState.context.label}` : ""}
                </strong>
                <button type="button" className="impqx-ahri-shop-button" onClick={closeRebateDetails}>
                  Close
                </button>
              </div>
              <div className="impqx-ahri-shop-rebate-details-body">
                {(() => {
                  const area = rebateDetailsState.data?.area;
                  const cityLabel = String(area?.cityState || "").trim()
                    || [area?.city, area?.state].filter(Boolean).join(", ");
                  const zipLabel = area?.zipCodePadded || area?.zipCode;
                  if (!cityLabel && !zipLabel) return null;
                  return (
                    <div className="impqx-ahri-shop-rebate-details-area">
                      <span className="impqx-ahri-shop-rebate-details-area-label">Offers shown for</span>
                      <strong>
                        {cityLabel || "Your area"}
                        {zipLabel ? ` (${zipLabel})` : ""}
                      </strong>
                    </div>
                  );
                })()}
                {rebateDetailsState.loading && (
                  <div className="impqx-ahri-shop-rebate-details-status">
                    Loading rebate details…
                  </div>
                )}
                {!rebateDetailsState.loading && rebateDetailsState.error && (
                  <div className="impqx-ahri-shop-rebate-details-status impqx-ahri-shop-rebate-details-error">
                    {rebateDetailsState.error}
                  </div>
                )}
                {!rebateDetailsState.loading && !rebateDetailsState.error && (
                  <>
                    {(() => {
                      const details = Array.isArray(rebateDetailsState.data?.product_rebate_details)
                        ? rebateDetailsState.data.product_rebate_details
                        : [];
                      const programsCount = details.reduce(
                        (acc, entry) => acc + (Array.isArray(entry?.rebatePrograms) ? entry.rebatePrograms.length : 0),
                        0
                      );
                      if (!details.length || programsCount === 0) {
                        return (
                          <div className="impqx-ahri-shop-rebate-details-status">
                            No rebate programs are currently available for this item in your area.
                          </div>
                        );
                      }
                      return details.map((entry, entryIdx) => {
                        const product = entry?.product || {};
                        const programs = Array.isArray(entry?.rebatePrograms) ? entry.rebatePrograms : [];
                        if (!programs.length) return null;
                        const productLabel = decodeHtmlEntities(
                          product.modelNumber
                            || product.siteSKU
                            || product.skus
                            || rebateDetailsState.context.id
                            || ""
                        );
                        const productMeta = [product.brand, product.category]
                          .map((part) => String(part || "").trim())
                          .filter(Boolean)
                          .join(" • ");
                        return (
                          <section
                            key={`rebate-product-${entryIdx}-${product?.siteSKU || product?.modelNumber || entryIdx}`}
                            className="impqx-ahri-shop-rebate-details-product"
                          >
                            <header className="impqx-ahri-shop-rebate-details-product-head">
                              <strong>{productLabel || rebateDetailsState.context.label}</strong>
                              {productMeta && (
                                <span className="impqx-ahri-shop-rebate-details-product-meta">{productMeta}</span>
                              )}
                              <span className="impqx-ahri-shop-rebate-details-program-count">
                                {programs.length} program{programs.length === 1 ? "" : "s"}
                              </span>
                            </header>
                            <ul className="impqx-ahri-shop-rebate-details-program-list">
                              {programs.map((program, programIdx) => {
                                const amount = pickRebateProgramDisplayAmount(program);
                                const typeLabel = summarizeRebateOfferType(program);
                                const startLabel = formatEcoRebateDate(program.startDate);
                                const endLabel = formatEcoRebateDate(program.expirationDate || program.endDate);
                                const action = pickRebateProgramAction(program);
                                const consumerType = String(program.consumerType || "").replace(/_/g, " ").trim();
                                const sponsor = String(program.sponsorName || program.sponsor || "").trim();
                                const name = String(program.programName || program.name || "").trim() || "Energy Rebate";
                                const terms = String(program.terms || program.termsAndConditions || "").trim();
                                const notes = String(program.notes || program.description || "").trim();
                                return (
                                  <li
                                    key={`rebate-program-${entryIdx}-${programIdx}`}
                                    className="impqx-ahri-shop-rebate-details-program"
                                  >
                                    <div className="impqx-ahri-shop-rebate-details-program-head">
                                      <div className="impqx-ahri-shop-rebate-details-program-title">
                                        <strong>{decodeHtmlEntities(name)}</strong>
                                        {sponsor && (
                                          <span className="impqx-ahri-shop-rebate-details-program-sponsor">
                                            by {decodeHtmlEntities(sponsor)}
                                          </span>
                                        )}
                                      </div>
                                      {amount && (
                                        <span className="impqx-ahri-shop-rebate-amount impqx-ahri-shop-rebate-details-amount">
                                          {amount}
                                        </span>
                                      )}
                                    </div>
                                    <div className="impqx-ahri-shop-rebate-details-program-meta">
                                      {typeLabel && (
                                        <span className="impqx-ahri-shop-rebate-details-chip">{typeLabel}</span>
                                      )}
                                      {consumerType && (
                                        <span className="impqx-ahri-shop-rebate-details-chip subtle">{consumerType}</span>
                                      )}
                                      {(startLabel || endLabel) && (
                                        <span className="impqx-ahri-shop-rebate-details-chip subtle">
                                          {startLabel && endLabel
                                            ? `${startLabel} – ${endLabel}`
                                            : endLabel
                                              ? `Expires ${endLabel}`
                                              : `Starts ${startLabel}`}
                                        </span>
                                      )}
                                    </div>
                                    {notes && (
                                      <p className="impqx-ahri-shop-rebate-details-program-notes">{notes}</p>
                                    )}
                                    {terms && terms !== notes && (
                                      <details className="impqx-ahri-shop-rebate-details-program-terms">
                                        <summary>Terms &amp; conditions</summary>
                                        <p>{terms}</p>
                                      </details>
                                    )}
                                    {action && (
                                      <div className="impqx-ahri-shop-rebate-details-program-actions">
                                        <a
                                          className="impqx-ahri-shop-button primary impqx-ahri-shop-rebate-details-action-btn"
                                          href={action.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {action.label}
                                        </a>
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </section>
                        );
                      });
                    })()}
                    <div className="impqx-ahri-shop-rebate-details-footer">
                      Rebate data provided by EcoRebates. Offer availability is subject to change; please confirm details
                      with the program sponsor before purchase.
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {cameraModalOpen && (

      <div className="impqx-ahri-shop-camera-modal-overlay" role="dialog" aria-modal="true" aria-label="Capture equipment photo">
        <div className="impqx-ahri-shop-camera-modal">
          <div className="impqx-ahri-shop-camera-modal-header">
            <span className="impqx-ahri-shop-camera-modal-title">Take Photo</span>
            <button type="button" className="impqx-ahri-shop-camera-modal-close" onClick={handleCloseCameraModal} aria-label="Close">×</button>
          </div>
          <div className="impqx-ahri-shop-camera-modal-body">
            {cameraError ? (
              <div className="impqx-ahri-shop-camera-modal-error">{cameraError}</div>
            ) : (
              <>
                <video ref={cameraVideoRef} autoPlay playsInline muted className="impqx-ahri-shop-camera-video" />
                <canvas ref={cameraCanvasRef} style={{ display: "none" }} />
              </>
            )}
            <div className="impqx-ahri-shop-camera-modal-actions">
              <button
                type="button"
                className="impqx-ahri-shop-button impqx-ahri-shop-camera-modal-capture"
                onClick={handleCapturePhoto}
                disabled={!cameraReady || !!cameraError}
              >
                <FontAwesomeIcon icon={faCamera} /> Capture
              </button>
              <button type="button" className="impqx-ahri-shop-camera-modal-cancel" onClick={handleCloseCameraModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
